import React, { useEffect, useState, useCallback, useRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import mn from "date-fns/locale/mn";

import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";

import { getFunctions, httpsCallable } from "firebase/functions";

import {
  doc,
  updateDoc,
  getFirestore,
  collection,
  collectionGroup,
  query,
  where,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  serverTimestamp,
  deleteField,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import {
  FiLogOut,
  FiSearch,
  FiHome,
  FiLock,
  FiFileText,
  FiLoader,
} from "react-icons/fi";

registerLocale("mn", mn);

const MySwal = withReactContent(Swal);

const MINU_EBARIMT_PROVIDER = "MINU_REDPOINT_ECR";
const DEFAULT_MINU_EBARIMT_TERMINAL_ID = "70034413";
const DEFAULT_MINU_EBARIMT_AES_KEY = ""; // Secrets MUST remain server-only.
const MINU_EBARIMT_SENDDATA_URL =
  "https://api.minu.mn/redpointapi/ecr/senddata";
const MINU_EBARIMT_PUT_URL =
  "https://api.minu.mn/redpointapi/ecr/ebarimtput";
const MINU_EBARIMT_AES_IV = ""; // Secrets MUST remain server-only.
const DIRECT_EBARIMT_QR_IMAGE_URL =
  "https://api.qrserver.com/v1/create-qr-code/";
const EBARIMT_MERCHANT_INFO_REGION = "asia-northeast1";
const EBARIMT_MERCHANT_INFO_FUNCTION_NAME = "getEbarimtMerchantInfoAsia";
const DEFAULT_FIREBASE_PROJECT_ID = "iparking-55511";
const XPRINTER_RECEIPT_TYPE = "XPRINTER_RECEIPT";
const EBARIMT_RECEIPT_TITLE = "Баримт";

/**
 * Firestore Timestamp, Date, string болон number
 * хэлбэрийн хугацааг форматлана.
 */
const formatDateTime = (value) => {
  if (!value) return "-";

  let dateObject;

  if (typeof value?.toDate === "function") {
    dateObject = value.toDate();
  } else if (typeof value === "string" || typeof value === "number") {
    dateObject = new Date(value);
  } else if (value instanceof Date) {
    dateObject = value;
  } else {
    return "-";
  }

  if (Number.isNaN(dateObject.getTime())) {
    return "-";
  }

  const year = dateObject.getFullYear();

  const month = String(dateObject.getMonth() + 1).padStart(2, "0");

  const day = String(dateObject.getDate()).padStart(2, "0");

  const hour = String(dateObject.getHours()).padStart(2, "0");

  const minute = String(dateObject.getMinutes()).padStart(2, "0");

  return `${year}.${month}.${day} ${hour}:${minute}`;
};

/**
 * Firestore Timestamp болон callable function-оор сериалчлагдсан
 * timestamp-ийг харьцуулахад ашиглах millisecond утга болгоно.
 */
const getTimestampMillis = (value) => {
  if (!value) return 0;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().getTime();
  }

  const seconds = Number(value?.seconds ?? value?._seconds);

  if (Number.isFinite(seconds)) {
    return seconds * 1000;
  }

  if (typeof value === "string") {
    const rawValue = value.trim();
    if (!rawValue) return 0;

    /*
     * Cloud Function searchParkingLogs нь Firestore Timestamp-ийг
     * toISOString() ашиглан `2026-09-23T05:10:00.000Z` хэлбэрээр буцаадаг.
     *
     * Өмнөх parser ISO string-ийн Z / timezone offset-ийг үл тоогоод
     * Монголын local time мэт parse хийснээс хугацаа олон цагаар зөрж байсан.
     *
     * Timezone тэмдэгтэй string-ийг эхлээд native Date parser-аар parse хийнэ.
     */
    const hasExplicitTimezone =
      /(?:Z|[+-]\d{2}:?\d{2})$/i.test(rawValue);

    if (hasExplicitTimezone) {
      const zonedTime = Date.parse(rawValue);

      if (!Number.isNaN(zonedTime)) {
        return zonedTime;
      }
    }

    /*
     * Timezone-гүй `YYYY-MM-DD HH:mm:ss` утгыг тухайн төхөөрөмжийн
     * local time гэж үзнэ.
     */
    const localDateMatch = rawValue.match(
      /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})(?:[T\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
    );

    if (localDateMatch) {
      const [, year, month, day, hour = "0", minute = "0", second = "0"] =
        localDateMatch;
      const localDate = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      );

      if (!Number.isNaN(localDate.getTime())) {
        return localDate.getTime();
      }
    }

    const parsedTime = Date.parse(rawValue);
    return Number.isNaN(parsedTime) ? 0 : parsedTime;
  }

  const parsedDate = new Date(value);
  const parsedTime = parsedDate.getTime();

  return Number.isNaN(parsedTime) ? 0 : parsedTime;
};

/**
 * Үнэ string эсвэл number хэлбэртэй байсан ч
 * найдвартай тоо болгон хувиргана.
 */
const getCleanPrice = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? value : Number.NaN;
  }
  const raw = String(value).replace(/[₮\s]/g, "").trim();
  // Explicit grouping: 1,500 / 1.500 (thousands), 1,500.50 / 1.500,50,
  // plus ungrouped 1500.50 or 1500,50. Invalid/negative => NaN.
  let normalized;
  if (/^\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?$/.test(raw)) {
    normalized = raw.replace(/,/g, "");
  } else if (/^\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?$/.test(raw)) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (/^\d+(?:[.,]\d{1,2})?$/.test(raw)) {
    normalized = raw.replace(",", ".");
  } else return Number.NaN;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : Number.NaN;
};

const DOCTOR_NOTE_FREE_MINUTES = 120;
const PARKING_BILLING_UNIT_MINUTES = 60;

const parseDurationMinutes = (value) => {
  if (value === undefined || value === null || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  }

  const normalizedValue = String(value).trim().toLowerCase();
  if (!normalizedValue) return 0;

  const colonParts = normalizedValue
    .split(":")
    .map((part) => Number(part.replace(/[^0-9]/g, "")));

  if (
    colonParts.length >= 2 &&
    colonParts.every((part) => Number.isFinite(part))
  ) {
    const [hours = 0, minutes = 0, seconds = 0] =
      colonParts.length === 2
        ? [colonParts[0], colonParts[1], 0]
        : colonParts.slice(-3);

    return Math.max(0, Math.ceil(hours * 60 + minutes + seconds / 60));
  }

  const daysMatch = normalizedValue.match(/(\d+)\s*(?:өд|day|days)/);
  const hoursMatch = normalizedValue.match(/(\d+)\s*(?:цаг|hour|hours|h)/);
  const minutesMatch = normalizedValue.match(
    /(\d+)\s*(?:мин|minute|minutes|min|m)/,
  );

  if (daysMatch || hoursMatch || minutesMatch) {
    return (
      (Number(daysMatch?.[1] || 0) || 0) * 24 * 60 +
      (Number(hoursMatch?.[1] || 0) || 0) * 60 +
      (Number(minutesMatch?.[1] || 0) || 0)
    );
  }

  const numericValue = Number(normalizedValue.replace(/[^0-9]/g, ""));

  return Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0;
};

const getParkingDurationMinutes = (record) => {
  /*
   * Эмнэлгийн бичгийн тооцоонд хамгийн найдвартай эх үүсвэр:
   * entry_time -> exit_time (гарсан бол)
   * entry_time -> Date.now() (одоо зогсоолд байгаа бол)
   *
   * durationMinutes / duration нь stale эсвэл өөр нэгжтэй байж болох тул
   * зөвхөн timestamp байхгүй үед fallback болгон ашиглана.
   */
  const entryTime = getTimestampMillis(record?.entry_time);
  const exitTime = getTimestampMillis(record?.exit_time);

  const statusToken = String(record?.status || "")
    .trim()
    .toLowerCase();

  const isStillInside =
    !exitTime ||
    statusToken === "parked" ||
    statusToken.includes("тооцоолж") ||
    record?.listenerActive === true ||
    record?.exitTry === true;

  if (entryTime > 0) {
    const endTime =
      isStillInside || exitTime <= entryTime
        ? Date.now()
        : exitTime;

    if (endTime > entryTime) {
      return Math.max(
        0,
        Math.ceil((endTime - entryTime) / 60000),
      );
    }
  }

  const explicitMinutes = Number(
    record?.durationMinutes ??
      record?.duration_minutes ??
      record?.parkingMinutes ??
      record?.parking_minutes,
  );

  if (Number.isFinite(explicitMinutes) && explicitMinutes > 0) {
    return Math.ceil(explicitMinutes);
  }

  return parseDurationMinutes(record?.duration);
};

const getConfiguredParkingPaymentMode = (pricingConfig = {}) => {
  const rawMode = String(
    pricingConfig?.PAYMENT_TYPE ??
      pricingConfig?.paymentType ??
      pricingConfig?.payment_type ??
      pricingConfig?.billingType ??
      pricingConfig?.billing_type ??
      "",
  )
    .trim()
    .toUpperCase();

  if (["FIXED", "FIX", "FLAT", "FLAT_RATE"].includes(rawMode)) {
    return "FIXED";
  }

  if (["HOURLY", "HOUR", "PER_HOUR"].includes(rawMode)) {
    return "HOURLY";
  }

  return "";
};

const isLargeParkingRecord = (record) =>
  record?.isLarge === true ||
  record?.is_large === true ||
  normalizePaymentToken(record?.vehicleType) === "LARGE" ||
  normalizePaymentToken(record?.vehicle_type) === "LARGE";

const getConfiguredParkingUnitAmount = (record, pricingConfig = {}) => {
  const isLarge = isLargeParkingRecord(record);

  return getCleanPrice(
    // Цагийн үнэ. FIXED_PRICE-ийг энд огт ашиглахгүй.
    (isLarge
      ? record?.largeVehiclePrice ??
        record?.large_vehicle_price ??
        pricingConfig?.CONFIG_PRICE_LARGE ??
        pricingConfig?.configPriceLarge ??
        pricingConfig?.largeVehiclePrice ??
        pricingConfig?.large_vehicle_price
      : undefined) ??
      record?.pricePerHour ??
      record?.price_per_hour ??
      record?.PRICE_PER_HOUR ??
      pricingConfig?.PRICE_PER_HOUR ??
      pricingConfig?.pricePerHour ??
      pricingConfig?.price_per_hour ??
      record?.hourlyRate ??
      record?.hourly_rate ??
      pricingConfig?.HOURLY_RATE ??
      pricingConfig?.hourlyRate ??
      pricingConfig?.hourly_rate ??
      record?.rate ??
      record?.unitPrice ??
      record?.unit_price,
  );
};

const getConfiguredFixedAmount = (record, pricingConfig = {}) => {
  const isLarge = isLargeParkingRecord(record);

  return getCleanPrice(
    (isLarge
      ? record?.fixedPriceLarge ??
        record?.fixed_price_large ??
        pricingConfig?.FIXED_PRICE_LARGE ??
        pricingConfig?.fixedPriceLarge ??
        pricingConfig?.fixed_price_large ??
        pricingConfig?.CONFIG_PRICE_LARGE ??
        pricingConfig?.configPriceLarge
      : undefined) ??
      record?.fixedPrice ??
      record?.fixed_price ??
      pricingConfig?.FIXED_PRICE ??
      pricingConfig?.fixedPrice ??
      pricingConfig?.fixed_price,
  );
};

const getConfiguredFreeMinutes = (record, pricingConfig = {}) =>
  getCleanPrice(
    record?.freeMinutes ??
      record?.free_minutes ??
      pricingConfig?.FREE_MINUTES ??
      pricingConfig?.freeMinutes ??
      pricingConfig?.free_minutes,
  );

const getFirstPositiveAmount = (...values) => {
  for (const value of values) {
    const amount = getCleanPrice(value);
    if (amount > 0) return amount;
  }

  return 0;
};

const getDoctorNoteParkingAmount = (record, pricingConfig = {}) => {
  const totalMinutes = getParkingDurationMinutes(record);
  const paymentMode = getConfiguredParkingPaymentMode(pricingConfig);
  const hourlyAmount = getConfiguredParkingUnitAmount(record, pricingConfig);
  const fixedAmount = getConfiguredFixedAmount(record, pricingConfig);

  /*
   * Эмнэлгийн бичиг дээр ердийн FREE_MINUTES-ийг нэмэхгүй.
   * Зөвхөн DOCTOR_NOTE_FREE_MINUTES = 120 минутыг хасна.
   */
  const payableMinutes = Math.max(
    0,
    totalMinutes - DOCTOR_NOTE_FREE_MINUTES,
  );

  const recordAmount = getFirstPositiveAmount(
    record?.amountDue,
    record?.amount_due,
    record?.price,
    record?.originalPrice,
    record?.original_price,
    record?.totalPrice,
    record?.total_price,
  );

  if (totalMinutes <= DOCTOR_NOTE_FREE_MINUTES) {
    const originalAmount =
      paymentMode === "FIXED"
        ? fixedAmount || recordAmount
        : hourlyAmount > 0
          ? Math.ceil(
              totalMinutes / PARKING_BILLING_UNIT_MINUTES,
            ) * hourlyAmount
          : recordAmount;

    return {
      originalAmount,
      amountDue: 0,
      discountAmount: originalAmount,
      totalMinutes,
      payableMinutes: 0,
      totalBillingUnits:
        hourlyAmount > 0
          ? Math.ceil(totalMinutes / PARKING_BILLING_UNIT_MINUTES)
          : 0,
      payableBillingUnits: 0,
      unitAmount:
        paymentMode === "FIXED" ? fixedAmount : hourlyAmount,
      paymentMode: paymentMode || "UNKNOWN",
    };
  }

  if (paymentMode === "FIXED") {
    const amountDue = fixedAmount > 0 ? fixedAmount : recordAmount;

    return {
      originalAmount: recordAmount || amountDue,
      amountDue,
      discountAmount: 0,
      totalMinutes,
      payableMinutes,
      totalBillingUnits: 1,
      payableBillingUnits: 1,
      unitAmount: fixedAmount,
      paymentMode: "FIXED",
    };
  }

  if (hourlyAmount > 0) {
    /*
     * Жишээ:
     * 603 минут - 120 минут = 483 минут
     * ceil(483 / 60) = 9 цаг
     * 9 × 2,000₮ = 18,000₮
     */
    const totalBillingUnits = Math.ceil(
      totalMinutes / PARKING_BILLING_UNIT_MINUTES,
    );
    const payableBillingUnits = Math.ceil(
      payableMinutes / PARKING_BILLING_UNIT_MINUTES,
    );

    const calculatedOriginalAmount = totalBillingUnits * hourlyAmount;
    const amountDue = payableBillingUnits * hourlyAmount;
    const discountAmount = Math.max(
      0,
      calculatedOriginalAmount - amountDue,
    );

    return {
      originalAmount: calculatedOriginalAmount,
      amountDue,
      discountAmount,
      totalMinutes,
      payableMinutes,
      totalBillingUnits,
      payableBillingUnits,
      unitAmount: hourlyAmount,
      paymentMode: "HOURLY",
    };
  }

  // Legacy config олдохгүй үед 0₮ гэж буруу болгохгүй.
  return {
    originalAmount: recordAmount,
    amountDue: recordAmount,
    discountAmount: 0,
    totalMinutes,
    payableMinutes,
    totalBillingUnits: 0,
    payableBillingUnits: 0,
    unitAmount: 0,
    paymentMode: "FALLBACK",
  };
};

const getParkingRecordIdentity = (record) =>
  String(
    record?.docId ||
      record?.documentId ||
      record?.firestoreId ||
      record?.firestoreDocId ||
      record?._docId ||
      record?._id ||
      record?.id ||
      "",
  ).trim();

/**
 * Callable function Firestore-ийн бүтэн resource name буцаасан ч Web SDK-д
 * ашиглах document path болгон цэвэрлэнэ.
 */
const normalizeFirestoreDocumentPath = (value) => {
  const rawPath = String(value || "").trim();

  if (!rawPath) return "";

  const documentsMarker = "/documents/";
  const markerIndex = rawPath.indexOf(documentsMarker);
  const relativePath =
    markerIndex >= 0
      ? rawPath.slice(markerIndex + documentsMarker.length)
      : rawPath;

  return relativePath.replace(/^\/+|\/+$/g, "");
};

const getParkingRecordDocumentPaths = (record) =>
  [
    record?.path,
    record?.refPath,
    record?.ref_path,
    record?.docPath,
    record?.doc_path,
    record?.recordPath,
    record?.record_path,
    record?.documentPath,
    record?.document_path,
    record?.firestorePath,
    record?.firestore_path,
    record?.sourcePath,
    record?.source_path,
    record?.__name__,
    record?.ref?.path,
    record?.reference?.path,
    record?.documentReference?.path,
    record?._ref?.path,
    typeof record?.id === "string" && record.id.includes("/")
      ? record.id
      : "",
  ]
    .map(normalizeFirestoreDocumentPath)
    .filter((value, index, values) => {
      const segmentCount = value.split("/").filter(Boolean).length;

      return (
        Boolean(value) &&
        segmentCount > 0 &&
        segmentCount % 2 === 0 &&
        values.indexOf(value) === index
      );
    });

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    const normalizedValue = String(value || "").trim();

    if (normalizedValue) return normalizedValue;
  }

  return "";
};

const formatEbarimtApiAmount = (value) => {
  const amount = getCleanPrice(value);
  if (!Number.isFinite(amount)) throw new Error("И-Баримтын дүн буруу байна.");
  return String(Math.round(amount * 100)); // integer cents, not string concatenation
};

const getMinuEbarimtPaymentType = (paymentType, paymentMethod) => {
  const normalizedType = String(paymentType || "").trim().toLowerCase();

  if (["card", "cash", "qpay"].includes(normalizedType)) {
    return normalizedType;
  }

  const normalizedMethod = String(paymentMethod || "").trim().toUpperCase();

  if (normalizedMethod === "CARD") return "card";
  if (normalizedMethod === "CASH") return "cash";

  return "qpay";
};

const getMinuXprinterEbarimtConfig = (
  parkingConfig = {},
  parkingName = "",
  parkId = "",
) => {
  const terminalId = firstNonEmptyString(
    parkingConfig?.ebarimtTerminalId,
    parkingConfig?.ebarimt_terminal_id,
    parkingConfig?.terminalNo,
    parkingConfig?.terminal_no,
    parkingConfig?.minuEbarimtTerminalId,
    parkingConfig?.minu_ebarimt_terminal_id,
    parkingConfig?.ebarimt?.terminalNo,
    parkingConfig?.ebarimt?.terminal_no,
    parkingConfig?.ebarimt?.terminalId,
    parkingConfig?.ebarimt?.terminal_id,
    parkingConfig?.minuEbarimt?.terminalNo,
    parkingConfig?.minuEbarimt?.terminal_no,
    parkingConfig?.minuEbarimt?.terminalId,
    parkingConfig?.minuEbarimt?.terminal_id,
    parkingConfig?.printer?.terminalId,
    parkingConfig?.printer?.terminal_id,
    DEFAULT_MINU_EBARIMT_TERMINAL_ID,
  );

  const branchName = firstNonEmptyString(
    parkingConfig?.branchName,
    parkingConfig?.branch_name,
    parkingConfig?.name,
    parkingConfig?.parkingName,
    parkingConfig?.parking_name,
    parkingConfig?.config?.branchName,
    parkingConfig?.settings?.branchName,
    parkingName,
    parkId,
  );

  const printerName = firstNonEmptyString(
    parkingConfig?.xprinterName,
    parkingConfig?.xprinter_name,
    parkingConfig?.receiptPrinterName,
    parkingConfig?.receipt_printer_name,
    parkingConfig?.printer?.name,
    parkingConfig?.printer?.printerName,
    parkingConfig?.xprinter?.name,
    "Xprinter receipt",
  );

  // EBarimt AES keys are no longer read from a Firestore document into the browser.
  const aesKey = "";
  const aesIV = "";

  const paperWidthMm = Number(
    firstNonEmptyString(
      parkingConfig?.paperWidthMm,
      parkingConfig?.paper_width_mm,
      parkingConfig?.receiptPaperWidthMm,
      parkingConfig?.receipt_paper_width_mm,
      parkingConfig?.printer?.paperWidthMm,
      parkingConfig?.printer?.paper_width_mm,
      parkingConfig?.xprinter?.paperWidthMm,
      parkingConfig?.xprinter?.paper_width_mm,
      "58",
    ),
  );

  return {
    terminalId,
    branchName,
    printerName,
    aesKey,
    aesIV,
    paperWidthMm:
      Number.isFinite(paperWidthMm) && paperWidthMm > 0 ? paperWidthMm : 58,
  };
};

const getRecordsCollectionPathFromLogs = (records) => {
  for (const record of records || []) {
    const [documentPath] = getParkingRecordDocumentPaths(record);

    if (documentPath) {
      return documentPath.split("/").slice(0, -1).join("/");
    }

    const collectionUrl = firstNonEmptyString(
      record?.collectionUrl,
      record?.collection_url,
      record?.source?.collectionUrl,
      record?.source?.collection_url,
    );
    const branchId = firstNonEmptyString(
      record?.branchId,
      record?.branch_id,
      record?.source?.branchId,
      record?.source?.branch_id,
    );

    if (collectionUrl && branchId) {
      return `${collectionUrl}/${branchId}/records`;
    }
  }

  return "";
};

/**
 * parkings/{parkId} дээр local server-ийн Firestore source хадгалагдсан бол
 * Python listener-тэй ижил `{collection_url}/{branch_id}/records` path үүсгэнэ.
 */
const getConfiguredRecordsCollectionPath = (parkingData, parkId) => {
  const explicitPath = firstNonEmptyString(
    parkingData?.recordsCollectionPath,
    parkingData?.records_collection_path,
    parkingData?.firestore?.recordsCollectionPath,
    parkingData?.firestore?.records_collection_path,
    parkingData?.config?.recordsCollectionPath,
    parkingData?.config?.records_collection_path,
    parkingData?.settings?.recordsCollectionPath,
    parkingData?.settings?.records_collection_path,
    parkingData?.server?.recordsCollectionPath,
    parkingData?.server?.records_collection_path,
    parkingData?.localServer?.recordsCollectionPath,
    parkingData?.localServer?.records_collection_path,
    parkingData?.listener?.recordsCollectionPath,
    parkingData?.listener?.records_collection_path,
  );

  if (explicitPath) {
    const normalizedPath = normalizeFirestoreDocumentPath(explicitPath);
    const segmentCount = normalizedPath.split("/").filter(Boolean).length;

    if (segmentCount > 0 && segmentCount % 2 === 1) {
      return normalizedPath;
    }
  }

  const collectionUrl = firstNonEmptyString(
    parkingData?.collectionUrl,
    parkingData?.collection_url,
    parkingData?.firestore?.collectionUrl,
    parkingData?.firestore?.collection_url,
    parkingData?.config?.collectionUrl,
    parkingData?.config?.collection_url,
    parkingData?.settings?.collectionUrl,
    parkingData?.settings?.collection_url,
    parkingData?.server?.collectionUrl,
    parkingData?.server?.collection_url,
    parkingData?.localServer?.collectionUrl,
    parkingData?.localServer?.collection_url,
    parkingData?.listener?.collectionUrl,
    parkingData?.listener?.collection_url,
    parkId,
  );
  const branchId = firstNonEmptyString(
    parkingData?.branchId,
    parkingData?.branch_id,
    parkingData?.firestore?.branchId,
    parkingData?.firestore?.branch_id,
    parkingData?.config?.branchId,
    parkingData?.config?.branch_id,
    parkingData?.settings?.branchId,
    parkingData?.settings?.branch_id,
    parkingData?.server?.branchId,
    parkingData?.server?.branch_id,
    parkingData?.localServer?.branchId,
    parkingData?.localServer?.branch_id,
    parkingData?.listener?.branchId,
    parkingData?.listener?.branch_id,
    "cars",
  );

  return `${collectionUrl}/${branchId}/records`;
};

const LATIN_PLATE_TO_CYRILLIC = Object.freeze({
  A: "А",
  B: "В",
  C: "С",
  E: "Е",
  H: "Н",
  K: "К",
  M: "М",
  O: "О",
  P: "Р",
  T: "Т",
  U: "У",
  X: "Х",
  Y: "Ү",
  Z: "З",
});

/**
 * Firestore болон Cloud Function-ээс ирсэн улсын дугаарт Latin/Cyrillic
 * ижил харагддаг үсэг, зай, зураас зөрсөн ч нэг утгаар харьцуулна.
 */
const normalizePlateForMatching = (value) =>
  String(value || "")
    .normalize("NFKC")
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-ZА-ЯЁӨҮ]/g, "")
    .split("")
    .map((character) => LATIN_PLATE_TO_CYRILLIC[character] || character)
    .join("");

const getRecordPlateValues = (record) =>
  [
    record?.car_plate,
    record?.plate,
    record?.license_plate,
    record?.licensePlate,
    record?.plate_number,
    record?.plateNumber,
  ].filter((value) => value !== undefined && value !== null && value !== "");

const recordMatchesNormalizedPlate = (record, normalizedPlate) =>
  Boolean(normalizedPlate) &&
  getRecordPlateValues(record).some(
    (value) => normalizePlateForMatching(value) === normalizedPlate,
  );

const PAYMENT_METHOD_OPTIONS = {
  CASH: "Бэлнээр төлсөн",
  CARD: "Картаар төлсөн",
  DOCTOR_NOTE: "Эмнэлгийн бичгээр",
  OTHER_SERVICE: "Бусад үйлчилгээ",
};

const PAYMENT_TYPE_LABELS = {
  cash: "Бэлнээр төлсөн",
  qpay: "QPay төлсөн",
  card: "Картаар төлсөн",
  doc: "Эмнэлгийн бичгээр",
  otherReason: "Бусад үйлчилгээ",
};

const getPaymentTypeForSettlement = (settlementMethod) => {
  if (settlementMethod === "CARD") return "card";
  if (settlementMethod === "CASH") return "cash";
  if (settlementMethod === "DOCTOR_NOTE") return "doc";
  if (settlementMethod === "OTHER_SERVICE") return "otherReason";

  return "qpay";
};

/**
 * Төлбөрийн сувгийг canonical хэлбэрт оруулна:
 * qpay | card | cash | doc | otherReason
 *
 * Шинэ record дээр paymentType хамгийн өндөр priority-тэй.
 * Хуучин record дээр paymentMethod, manualSettlement*, paymentSource,
 * POS/QPay-ийн legacy field-үүдээр fallback танина.
 */
const normalizePaymentTypeToken = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .trim()
    .replace(/[\s/_-]+/g, " ")
    .toUpperCase();

const classifyPaymentTypeToken = (value) => {
  const token = normalizePaymentTypeToken(value);
  if (!token) return null;

  if (
    token === "DOC" ||
    token === "DOCTOR NOTE" ||
    token === "MEDICAL NOTE" ||
    token.includes("ЭМНЭЛГИЙН БИЧИГ")
  ) return "doc";

  if (
    token === "OTHERREASON" ||
    token === "OTHER REASON" ||
    token === "OTHER SERVICE" ||
    token.includes("БУСАД ҮЙЛЧИЛГЭЭ")
  ) return "otherReason";

  if (
    token === "CASH" ||
    token === "BELEN" ||
    token === "БЭЛЭН" ||
    token.includes("CASH")
  ) return "cash";

  if (
    token === "CARD" ||
    token === "POS" ||
    token === "BANK CARD" ||
    token.includes("CARD") ||
    token.includes("POS") ||
    token.includes("WEB SERIAL")
  ) return "card";

  if (
    token === "QPAY" ||
    token === "QR" ||
    token === "QPAY QR" ||
    token.includes("QPAY") ||
    token.includes("QR")
  ) return "qpay";

  return null;
};

const getCanonicalPaymentType = (record) => {
  const explicitTypeCandidates = [
    record?.paymentType,
    record?.payment_type,
    record?.ebarimtPaymentType,
    record?.ebarimt_payment_type,
  ];

  for (const value of explicitTypeCandidates) {
    const classifiedType = classifyPaymentTypeToken(value);
    if (classifiedType) return classifiedType;
  }

  const methodCandidates = [
    record?.paymentMethod,
    record?.payment_method,
    record?.manualSettlementPaymentMethod,
    record?.manualSettlementMethod,
    record?.settlementMethod,
    record?.doctorNotePaymentMethod,
  ];

  for (const value of methodCandidates) {
    const classifiedType = classifyPaymentTypeToken(value);
    if (classifiedType) return classifiedType;
  }

  const sourceCandidates = [
    record?.paymentSource,
    record?.payment_source,
    record?.provider,
    record?.providerName,
    record?.providerPaymentStatus,
    record?.manualSettlementChannel,
  ];

  for (const value of sourceCandidates) {
    const classifiedType = classifyPaymentTypeToken(value);
    if (classifiedType) return classifiedType;
  }

  const posSucceeded = [record?.posStatus, record?.manualPosStatus]
    .map((value) => normalizePaymentTypeToken(value))
    .some((value) => value === "SUCCESS" || value === "PAID");

  if (
    posSucceeded ||
    record?.posRrn ||
    record?.posTerminal ||
    record?.manualPosTransactionId
  ) return "card";

  if (
    record?.qpayInvoiceId ||
    record?.qpay_invoice_id ||
    record?.invoiceId ||
    record?.invoice_id
  ) return "qpay";

  // Төрөл хадгалагдаагүй хуучин record-ийг зөвхөн бодитоор мөнгө төлсөн
  // нотолгоо + эерэг дүнтэй үед QPay legacy гэж үзнэ.
  const paymentValue = normalizePaymentTypeToken(record?.payment);
  const paymentStatus = normalizePaymentTypeToken(
    record?.paymentStatus ?? record?.payment_status,
  );
  const legacyPaidAmount = getCleanPrice(
    record?.paidAmount ??
      record?.paid_amount ??
      record?.amountPaid ??
      record?.amount_paid ??
      record?.price,
  );
  const hasLegacyPaidSignal =
    record?.payment === true ||
    record?.payment === 1 ||
    ["TRUE", "1", "PAID", "ТӨЛСӨН", "ТӨЛӨГДСӨН"].includes(paymentValue) ||
    ["PAID", "ТӨЛСӨН", "ТӨЛӨГДСӨН"].includes(paymentStatus);

  if (hasLegacyPaidSignal && legacyPaidAmount > 0) {
    return "qpay";
  }

  // 0₮ / үнэгүй / төрөл нь тодорхойгүй record-ийг QPay гэж хүчээр ангилахгүй.
  return null;
};

const EXEMPTION_METHODS = new Set(["DOCTOR_NOTE", "OTHER_SERVICE"]);

const POS_BAUD_RATE = 115200;
const POS_PAYMENT_TIMEOUT_MILLISECONDS = 75000;
const POS_SUCCESS_CODE = 0;
const POS_CANCELLED_CODE = -22;
const POS_MAX_RECEIVE_BUFFER = 128 * 1024;
const PAYMENT_BREAKDOWN_FALLBACK_PAGE_SIZE = 2000;
const PAYMENT_BREAKDOWN_FALLBACK_MAX_PAGES = 10;
const PAYMENT_BREAKDOWN_FALLBACK_POLL_MILLISECONDS = 15000;

const createManualPosRequestId = () => {
  if (
    typeof window !== "undefined" &&
    typeof window.crypto?.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `pos-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createPosTransactionId = () => {
  if (
    typeof window !== "undefined" &&
    typeof window.crypto?.getRandomValues === "function"
  ) {
    const digits = [];
    const randomBytes = new Uint8Array(16);

    /*
     * 0-249 утгыг л ашигласнаар byte % 10 хийх үеийн
     * магадлалын хазайлтыг арилгаж яг 10 random цифр үүсгэнэ.
     */
    while (digits.length < 10) {
      window.crypto.getRandomValues(randomBytes);

      for (
        let index = 0;
        index < randomBytes.length && digits.length < 10;
        index += 1
      ) {
        const value = randomBytes[index];

        if (value < 250) {
          digits.push(String(value % 10));
        }
      }
    }

    return digits.join("");
  }

  return String(Math.floor(Math.random() * 10000000000)).padStart(10, "0");
};

const POS_ATTEMPT_STORAGE_PREFIX = "iparking:manual-pos:";
const POS_ATTEMPT_MAX_AGE_MILLISECONDS = 48 * 60 * 60 * 1000;

const getPosAttemptStorageKey = (parkId, parkingRecord) => {
  const recordIdentity = getParkingRecordIdentity(parkingRecord);
  const plate = normalizePlateForMatching(
    parkingRecord?.car_plate || parkingRecord?.plate,
  );
  const entryTime = getTimestampMillis(parkingRecord?.entry_time);

  return `${POS_ATTEMPT_STORAGE_PREFIX}${parkId}:${recordIdentity || plate}:${entryTime || "open"}`;
};

const readStoredPosAttempt = (storageKey) => {
  if (typeof window === "undefined" || !storageKey) return null;

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return null;

    const storedAttempt = JSON.parse(rawValue);
    const updatedAt = Number(storedAttempt?.updatedAt || 0);

    if (
      !updatedAt ||
      Date.now() - updatedAt > POS_ATTEMPT_MAX_AGE_MILLISECONDS
    ) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    return storedAttempt;
  } catch {
    return null;
  }
};

const writeStoredPosAttempt = (storageKey, attempt) => {
  if (typeof window === "undefined" || !storageKey) return;

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ ...attempt, updatedAt: Date.now() }),
    );
  } catch {
    // localStorage хаалттай байсан ч POS ажиллагааг зогсоохгүй.
  }
};

const clearStoredPosAttempt = (storageKey) => {
  if (typeof window === "undefined" || !storageKey) return;

  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // localStorage хаалттай байж болно.
  }
};

const buildPosCommand = (amount, transactionId) =>
  `<{amount:${Math.round(amount)},data:${transactionId}}>`;

const toPosCommandAmount = (amount) => Math.round(Number(amount) * 100);

/**
 * Serial stream нь JSON-ийг хэд хэдэн chunk болгон буцааж болдог.
 * Complete {...} объектуудыг string доторх хаалтыг тооцолгүй салгана.
 */
const extractPosJsonObjects = (rawBuffer) => {
  const objects = [];
  let buffer = String(rawBuffer || "");

  while (buffer) {
    const start = buffer.indexOf("{");

    if (start < 0) {
      return {
        objects,
        remaining:
          buffer.length > POS_MAX_RECEIVE_BUFFER ? buffer.slice(-1024) : buffer,
      };
    }

    let depth = 0;
    let inString = false;
    let escaped = false;
    let end = -1;

    for (let index = start; index < buffer.length; index += 1) {
      const character = buffer[index];

      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (character === "\\") {
          escaped = true;
        } else if (character === '"') {
          inString = false;
        }
        continue;
      }

      if (character === '"') {
        inString = true;
      } else if (character === "{") {
        depth += 1;
      } else if (character === "}") {
        depth -= 1;
        if (depth === 0) {
          end = index + 1;
          break;
        }
      }
    }

    if (end < 0) {
      const remaining = buffer.slice(start);
      return {
        objects,
        remaining:
          remaining.length > POS_MAX_RECEIVE_BUFFER
            ? remaining.slice(-POS_MAX_RECEIVE_BUFFER)
            : remaining,
      };
    }

    objects.push(buffer.slice(start, end));
    buffer = buffer.slice(end);
  }

  return { objects, remaining: "" };
};

const parsePosResponse = (
  payload,
  transactionId,
  expectedPosAmount,
  displayAmount = expectedPosAmount,
) => {
  const posPayload =
    payload?.pos && typeof payload.pos === "object"
      ? payload.pos
      : payload?.result &&
          typeof payload.result === "object" &&
          "code" in payload.result
        ? payload.result
        : payload;

  if (!posPayload || typeof posPayload !== "object") {
    return {
      status: "FAILED",
      reason: "INVALID_POS_RESPONSE",
      description: "POS-ийн хариу буруу бүтэцтэй байна.",
    };
  }

  const code = Number(posPayload.code);
  const responseTransactionId = String(posPayload.data || "").trim();
  const responseAmount = Number(posPayload.amount);
  const description = String(posPayload.desc || "").trim();
  const rawPan = String(posPayload.pan || "").trim();

  const commonResult = {
    code: Number.isFinite(code) ? code : null,
    description,
    transactionId,
    amount: Math.round(displayAmount),
    posCommandAmount: Math.round(expectedPosAmount),
    terminal: String(posPayload.terminal || "").trim(),
    rrn: String(posPayload.rrn || "").trim(),
    paymentType: String(posPayload.type || "").trim(),
    maskedPan: rawPan.includes("*") ? rawPan.slice(0, 64) : "",
    appCode: String(posPayload.appCode || "").trim(),
    entryMode: String(posPayload.entryMode || "").trim(),
    cardType: String(posPayload.cardType || "").trim(),
  };

  if (code === POS_CANCELLED_CODE) {
    return {
      ...commonResult,
      status: "CANCELLED",
      reason: "TRANSACTION_CANCELLED",
    };
  }

  if (code !== POS_SUCCESS_CODE) {
    return {
      ...commonResult,
      status: "FAILED",
      reason: `POS_CODE_${Number.isFinite(code) ? code : "INVALID"}`,
    };
  }

  if (responseTransactionId !== String(transactionId)) {
    return {
      ...commonResult,
      status: "MISMATCH",
      reason: "TRANSACTION_ID_MISMATCH",
    };
  }

  if (responseAmount !== Math.round(expectedPosAmount)) {
    return {
      ...commonResult,
      status: "MISMATCH",
      reason: "AMOUNT_MISMATCH",
    };
  }

  return {
    ...commonResult,
    status: "SUCCESS",
    reason: "",
  };
};

const getPaymentMethodLabel = (paymentType, isSettled = false) =>
  isSettled && paymentType
    ? PAYMENT_TYPE_LABELS[paymentType] || "Төлсөн"
    : "-";

/**
 * Payment төлөвийг нэг газар canonical хэлбэрт оруулна.
 * Firestore-ийн хуучин/шинэ schema, boolean/string payment болон
 * Монгол/Англи статусуудыг ижил утгаар танина.
 */
const normalizePaymentToken = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();

const PAYMENT_TRUE_VALUES = new Set([
  "TRUE",
  "1",
  "PAID",
  "COMPLETED",
  "SUCCESS",
  "ТӨЛСӨН",
  "ТӨЛӨГДСӨН",
]);

const PAYMENT_SETTLED_STATUSES = new Set([
  "PAID",
  "COMPLETED",
  "SUCCESS",
  "EXEMPTED",
  "WAIVED",
  "ТӨЛСӨН",
  "ТӨЛӨГДСӨН",
  "ЧӨЛӨӨЛСӨН",
]);

const RECORD_SETTLED_STATUSES = new Set([
  "PAYMENT_CONFIRMED",
  "PAID",
  "EXEMPTED",
  "WAIVED",
]);

const PAYMENT_EXEMPTION_STATUSES = new Set([
  "EXEMPTED",
  "WAIVED",
  "ЧӨЛӨӨЛСӨН",
]);

const PAYMENT_UNPAID_STATUSES = new Set([
  "UNPAID",
  "NOT_PAID",
  "WAIT_PAYMENT",
  "PAYMENT_REQUIRED",
  "ТӨЛӨӨГҮЙ",
  "ТӨЛБӨР ТӨЛӨГДӨӨГҮЙ",
]);

const RECORD_UNPAID_STATUSES = new Set([
  "WAIT_PAYMENT",
  "PAYMENT_REQUIRED",
  "PAYMENT_PENDING",
]);

const getParkingRecordPaymentTokens = (record) => ({
  paymentValue: normalizePaymentToken(record?.payment),
  paymentStatusValues: [record?.paymentStatus, record?.payment_status]
    .map(normalizePaymentToken)
    .filter(Boolean),
  statusValues: [record?.status, record?.settlementStatus]
    .map(normalizePaymentToken)
    .filter(Boolean),
});

const isParkingRecordExempted = (record) => {
  const { paymentStatusValues, statusValues } =
    getParkingRecordPaymentTokens(record);
  const settlementType = normalizePaymentToken(record?.settlementType);
  const paymentType = getCanonicalPaymentType(record);

  return (
    paymentStatusValues.some((value) =>
      PAYMENT_EXEMPTION_STATUSES.has(value),
    ) ||
    statusValues.some((value) => PAYMENT_EXEMPTION_STATUSES.has(value)) ||
    settlementType === "EXEMPTION" ||
    (["doc", "otherReason"].includes(paymentType) &&
      paymentStatusValues.some((value) => PAYMENT_SETTLED_STATUSES.has(value)))
  );
};

const isParkingRecordSettled = (record) => {
  const { paymentValue, paymentStatusValues, statusValues } =
    getParkingRecordPaymentTokens(record);

  return (
    record?.payment === true ||
    record?.payment === 1 ||
    PAYMENT_TRUE_VALUES.has(paymentValue) ||
    paymentStatusValues.some((value) => PAYMENT_SETTLED_STATUSES.has(value)) ||
    statusValues.some((value) => RECORD_SETTLED_STATUSES.has(value)) ||
    isParkingRecordExempted(record)
  );
};

const getParkingRecordPayableAmount = (record) =>
  getCleanPrice(
    record?.amountDue ??
      record?.amount_due ??
      record?.price ??
      record?.totalPrice ??
      0,
  );

const isParkingRecordExplicitlyUnpaid = (record) => {
  const { paymentValue, paymentStatusValues, statusValues } =
    getParkingRecordPaymentTokens(record);

  return (
    ["FALSE", "0", "UNPAID", "NOT_PAID"].includes(paymentValue) ||
    paymentStatusValues.some((value) => PAYMENT_UNPAID_STATUSES.has(value)) ||
    statusValues.some((value) => RECORD_UNPAID_STATUSES.has(value))
  );
};

const getParkingRecordPaymentState = (record) => {
  const rawSettled = isParkingRecordSettled(record);
  const isExempted = rawSettled && isParkingRecordExempted(record);
  const payableAmount = getParkingRecordPayableAmount(record);
  const isExplicitlyUnpaid = isParkingRecordExplicitlyUnpaid(record);

  const priceAmount = getCleanPrice(
    record?.price ?? record?.totalPrice ?? record?.total_price ?? 0,
  );
  const explicitPaidAmount = getCleanPrice(
    record?.paidAmount ??
      record?.paid_amount ??
      record?.amountPaid ??
      record?.amount_paid ??
      0,
  );
  const paymentType = getCanonicalPaymentType(record);

  const paidAmount =
    explicitPaidAmount > 0
      ? explicitPaidAmount
      : rawSettled && priceAmount > 0
        ? priceAmount
        : 0;

  // 0₮ гарсан, чөлөөлөлт биш record бол үнэгүй.
  // status=COMPLETED/SUCCESS байлаа гээд төлсөн гэж ангилахгүй.
  const isFree =
    !isExempted &&
    payableAmount <= 0 &&
    priceAmount <= 0 &&
    paidAmount <= 0;

  const statusValues = getParkingRecordPaymentTokens(record).statusValues;
  const isLegacyPaidCompletion =
    statusValues.some((value) => ["COMPLETED", "SUCCESS"].includes(value)) &&
    Boolean(paymentType) &&
    paidAmount > 0;

  const isSettled =
    !isFree && (isExempted || rawSettled || isLegacyPaidCompletion);

  const isUnpaid =
    !isSettled &&
    !isFree &&
    (isExplicitlyUnpaid || payableAmount > 0);

  return {
    isSettled,
    isExempted,
    isUnpaid,
    isFree,
    payableAmount,
    paidAmount,
    paymentType,
  };
};

const filterRecordsByPaymentStatus = (records, paymentFilter) => {
  if (paymentFilter === "all") return records;

  if (paymentFilter === "paid") {
    return records.filter(
      (record) => getParkingRecordPaymentState(record).isSettled,
    );
  }

  if (paymentFilter === "unpaid") {
    // Зөвхөн бодитоор төлбөр хүлээгдэж буй record үлдээнэ.
    // Төлсөн, чөлөөлсөн болон 0₮/төлбөргүй record энд орохгүй.
    return records.filter(
      (record) => getParkingRecordPaymentState(record).isUnpaid,
    );
  }

  return records;
};

const filterRecordsByPaymentType = (records, paymentTypeFilter) => {
  if (paymentTypeFilter === "all") return records;

  return records.filter((record) => {
    const paymentState = getParkingRecordPaymentState(record);

    return (
      paymentState.isSettled &&
      !paymentState.isFree &&
      paymentState.paymentType === paymentTypeFilter
    );
  });
};

const FALLBACK_AUDIT_ENTRY_TOLERANCE_MS = 60 * 1000;

const mergeParkingRecordsWithFallbackAudits = (records, auditRecords) =>
  records.map((record) => {
    const recordIdentity = getParkingRecordIdentity(record);
    const recordPlate = normalizePlateForMatching(
      record?.car_plate || record?.plate,
    );
    const recordEntryTime = getTimestampMillis(record?.entry_time);

    const matchingAudits = (auditRecords || []).filter((auditRecord) => {
      const auditRecordId = String(auditRecord?.sourceRecordId || "").trim();

      // Хоёр талд document ID байгаа үед зөвхөн exact ID match зөвшөөрнө.
      // Ижил улсын дугаар богино хугацаанд давтагдсан тохиолдолд өөр record-ийн
      // audit андуурч merge болохоос хамгаална.
      if (recordIdentity && auditRecordId) {
        return auditRecordId === recordIdentity;
      }

      // Legacy audit дээр ID байхгүй үед л plate + entry_time fallback ашиглана.
      const auditPlate = normalizePlateForMatching(auditRecord?.car_plate);
      const auditEntryTime = Number(auditRecord?.entryTimeMillis || 0);

      return (
        Boolean(recordPlate) &&
        auditPlate === recordPlate &&
        recordEntryTime > 0 &&
        auditEntryTime > 0 &&
        Math.abs(recordEntryTime - auditEntryTime) <=
          FALLBACK_AUDIT_ENTRY_TOLERANCE_MS
      );
    });

    // Зөвхөн баталгаажсан төлбөр/чөлөөлөлтийн audit-ийг merge хийнэ.
    // FAILED/PENDING audit нь original record-ийн төлөвийг дарж болохгүй.
    const settledAudit = matchingAudits.find((auditRecord) =>
      isParkingRecordSettled(auditRecord),
    );

    if (!settledAudit) return record;

    const auditPaymentType = getCanonicalPaymentType(settledAudit);
    const auditPaymentState = getParkingRecordPaymentState(settledAudit);
    const isExemption = auditPaymentState.isExempted;
    const settledAt = settledAudit.settledAt ?? settledAudit.payment_time;

    return {
      ...record,
      payment: true,
      paymentStatus:
        settledAudit.paymentStatus ||
        (isExemption ? "EXEMPTED" : "PAID"),
      paymentType: settledAudit.paymentType || auditPaymentType,
      paymentMethod: settledAudit.paymentMethod,
      payment_time: settledAt,
      paymentConfirmedAt:
        settledAudit.paymentConfirmedAt ?? settledAt ?? record?.paymentConfirmedAt,
      settledAt: settledAt ?? record?.settledAt,
      paidAmount: isExemption
        ? 0
        : getCleanPrice(
            settledAudit.paidAmount ??
              settledAudit.paid_amount ??
              settledAudit.price,
          ),
      amountDue: 0,
      originalPrice:
        settledAudit.originalPrice ?? record?.originalPrice ?? record?.price,
      price: isExemption
        ? 0
        : getCleanPrice(
            settledAudit.paidAmount ??
              settledAudit.paid_amount ??
              settledAudit.price ??
              record?.paidAmount ??
              record?.price,
          ),
      settlementType:
        settledAudit.settlementType ||
        (isExemption ? "EXEMPTION" : record?.settlementType),
      settlementReason: settledAudit.settlementReason,
      settlementLabel: settledAudit.settlementLabel,
      doctorNoteApplied:
        settledAudit.doctorNoteApplied ?? record?.doctorNoteApplied,
      doctorNoteFreeMinutes:
        settledAudit.doctorNoteFreeMinutes ?? record?.doctorNoteFreeMinutes,
      doctorNoteDiscountAmount:
        settledAudit.doctorNoteDiscountAmount ??
        record?.doctorNoteDiscountAmount,
      doctorNotePayableMinutes:
        settledAudit.doctorNotePayableMinutes ??
        record?.doctorNotePayableMinutes,
      ebarimtStatus: settledAudit.ebarimtStatus ?? record?.ebarimtStatus,
      ebarimtAmount: settledAudit.ebarimtAmount ?? record?.ebarimtAmount,
      ebarimtPaymentType:
        settledAudit.ebarimtPaymentType ?? record?.ebarimtPaymentType,
      ebarimtGiven: settledAudit.ebarimtGiven ?? record?.ebarimtGiven,
      ebarimtPrintConfirmed:
        settledAudit.ebarimtPrintConfirmed ?? record?.ebarimtPrintConfirmed,
      ebarimtPrintedAt:
        settledAudit.ebarimtPrintedAt ?? record?.ebarimtPrintedAt,
      ebarimtGivenAt: settledAudit.ebarimtGivenAt ?? record?.ebarimtGivenAt,
      ebarimtBillId: settledAudit.ebarimtBillId ?? record?.ebarimtBillId,
      ebarimtLottery: settledAudit.ebarimtLottery ?? record?.ebarimtLottery,
      ebarimtDate: settledAudit.ebarimtDate ?? record?.ebarimtDate,
      ebarimtQrData: settledAudit.ebarimtQrData ?? record?.ebarimtQrData,
      auditFallback: true,
    };
  });

const getDoctorNoteDiscountForRecord = (record) => {
  const paymentType = getCanonicalPaymentType(record);
  const discountType = normalizePaymentToken(record?.discountType);
  const settlementReason = normalizePaymentToken(record?.settlementReason);
  const settlementType = normalizePaymentToken(record?.settlementType);
  const settlementLabel = normalizePaymentToken(record?.settlementLabel);

  const doctorNoteApplied =
    record?.doctorNoteApplied === true ||
    record?.doctor_note_applied === true ||
    paymentType === "doc" ||
    discountType === "DOCTOR NOTE" ||
    discountType === "DOCTOR_NOTE" ||
    settlementReason === "DOCTOR NOTE" ||
    settlementReason === "DOCTOR_NOTE" ||
    settlementLabel.includes("ЭМНЭЛГИЙН БИЧИГ") ||
    (
      settlementType === "PARTIAL DISCOUNT PAYMENT" &&
      (
        record?.doctorNoteFreeMinutes !== undefined ||
        record?.doctor_note_free_minutes !== undefined
      )
    );

  if (!doctorNoteApplied) {
    return {
      applied: false,
      amount: 0,
    };
  }

  // Шинэ болон хуучин schema дээр хадгалагдсан шууд хөнгөлөлтийн талбарууд.
  const explicitDiscountAmount = getCleanPrice(
    record?.doctorNoteDiscountAmount ??
      record?.doctor_note_discount_amount ??
      record?.waivedAmount ??
      record?.waived_amount ??
      record?.discountAmount ??
      record?.discount_amount ??
      0,
  );

  if (explicitDiscountAmount > 0) {
    return {
      applied: true,
      amount: explicitDiscountAmount,
    };
  }

  /*
   * Хуучин record дээр doctorNoteDiscountAmount байхгүй бол:
   * original amount - бодитоор төлсөн amount гэж сэргээн тооцно.
   */
  const originalAmount = getFirstPositiveAmount(
    record?.doctorNoteOriginalAmount,
    record?.doctor_note_original_amount,
    record?.originalPrice,
    record?.original_price,
    record?.totalPrice,
    record?.total_price,
  );

  const paidAmount = getCleanPrice(
    record?.doctorNotePayableAmount ??
      record?.doctor_note_payable_amount ??
      record?.paidAmount ??
      record?.paid_amount ??
      record?.amountPaid ??
      record?.amount_paid ??
      record?.price ??
      0,
  );

  return {
    applied: true,
    amount:
      originalAmount > 0
        ? Math.max(0, originalAmount - paidAmount)
        : 0,
  };
};

const createEmptyPaymentBreakdown = () => ({
  cash: { count: 0, revenue: 0 },
  qpay: { count: 0, revenue: 0 },
  card: { count: 0, revenue: 0 },
  doc: { count: 0, revenue: 0 },
  otherReason: { count: 0, revenue: 0 },
  doctorDiscount: { count: 0, amount: 0 },
});

/**
 * Сонгосон өдрийн төлөгдсөн гүйлгээг төлбөрийн сувгаар нэгтгэнэ.
 * Canonical paymentType байхгүй/танигдахгүй бол QPay гэж тооцно.
 * doc болон otherReason нь тоонд орно, харин орлого нь үргэлж 0 байна.
 */
const summarizePaymentBreakdown = (records) => {
  const summary = createEmptyPaymentBreakdown();

  records.forEach((record) => {
    const paymentState = getParkingRecordPaymentState(record);
    const paymentType = paymentState.paymentType;
    const isExempted = paymentState.isExempted;

    /*
     * Эмнэлгийн бичгийн хөнгөлөлтийг төлбөрийн сувгаас тусад нь нийлбэрлэнэ.
     * CASH / POS-оор үлдэгдлээ төлсөн partial discount болон хуучин
     * waivedAmount/originalPrice schema-г мөн танина.
     */
    const doctorDiscount = getDoctorNoteDiscountForRecord(record);

    if (doctorDiscount.applied) {
      summary.doctorDiscount.count += 1;
      summary.doctorDiscount.amount += doctorDiscount.amount;
    }

    if (
      !paymentState.isSettled ||
      paymentState.isFree ||
      !paymentType ||
      !Object.prototype.hasOwnProperty.call(summary, paymentType)
    ) {
      return;
    }

    summary[paymentType].count += 1;
    summary[paymentType].revenue += isExempted ? 0 : paymentState.paidAmount;
  });

  return summary;
};

const mergePaymentBreakdowns = (...summaries) => {
  const merged = createEmptyPaymentBreakdown();

  summaries.forEach((summary) => {
    ["cash", "qpay", "card", "doc", "otherReason"].forEach(
      (bucketName) => {
        merged[bucketName].count += Number(
          summary?.[bucketName]?.count || 0,
        );
        merged[bucketName].revenue += Number(
          summary?.[bucketName]?.revenue || 0,
        );
      },
    );

    merged.doctorDiscount.count += Number(
      summary?.doctorDiscount?.count || 0,
    );
    merged.doctorDiscount.amount += Number(
      summary?.doctorDiscount?.amount || 0,
    );
  });

  return merged;
};

const getSettlementAmount = (parkingRecord, settlement) =>
  getCleanPrice(
    settlement?.amount ??
      parkingRecord?.amountDue ??
      parkingRecord?.price,
  );

const getSettlementOriginalAmount = (parkingRecord, settlement, amount) =>
  getCleanPrice(
    settlement?.originalAmount ??
      parkingRecord?.originalPrice ??
      parkingRecord?.price,
  ) || amount;

const getSettlementMetadata = (settlement) => {
  if (!settlement?.doctorNoteApplied) return {};

  return {
    doctorNoteApplied: true,
    doctorNoteFreeMinutes: DOCTOR_NOTE_FREE_MINUTES,
    doctorNoteTotalMinutes: Number(settlement.doctorNoteTotalMinutes || 0),
    doctorNotePayableMinutes: Number(
      settlement.doctorNotePayableMinutes || 0,
    ),
    doctorNoteOriginalAmount: getCleanPrice(settlement.originalAmount),
    doctorNotePayableAmount: getCleanPrice(settlement.amount),
    doctorNoteDiscountAmount: getCleanPrice(settlement.discountAmount),
    doctorNotePaymentMethod: settlement.doctorNotePaymentMethod || "NONE",
    discountType: "DOCTOR_NOTE",
  };
};

const getEbarimtAuditFields = (amount, paymentType, requestedAt) => {
  const cleanAmount = getCleanPrice(amount);

  if (cleanAmount <= 0) return {};

  return {
    ebarimtRequired: true,
    ebarimtPrintRequested: false,
    ebarimtPreparedAt: requestedAt,
    ebarimtStatus: "PENDING_PRINT",
    ebarimtAmount: cleanAmount,
    ebarimtPaymentType: paymentType,
  };
};


const EBARIMT_GIVEN_STATUSES = new Set([
  "GIVEN",
  "PRINTED",
  "PRINT_CONFIRMED",
]);

const isEbarimtGiven = (record) => {
  const status = normalizePaymentToken(record?.ebarimtStatus);

  return (
    record?.ebarimtGiven === true ||
    record?.ebarimtPrintConfirmed === true ||
    EBARIMT_GIVEN_STATUSES.has(status)
  );
};

const getEbarimtReceiptAuditFields = ({
  result,
  amount,
  paymentType,
  timestamp,
}) => {
  const cleanAmount = getCleanPrice(amount);
  const status = String(result?.status || "").trim().toUpperCase();
  const receiptData = result?.receiptData || {};

  const receiptFields = {
    ebarimtAmount: cleanAmount,
    ebarimtPaymentType: paymentType,
    ...(receiptData?.billId
      ? { ebarimtBillId: String(receiptData.billId) }
      : {}),
    ...(receiptData?.lottery
      ? { ebarimtLottery: String(receiptData.lottery) }
      : {}),
    ...(receiptData?.date
      ? { ebarimtDate: String(receiptData.date) }
      : {}),
    ...(receiptData?.qrData
      ? { ebarimtQrData: String(receiptData.qrData) }
      : {}),
  };

  if (status === "PRINT_CONFIRMED") {
    return {
      ...receiptFields,
      ebarimtRequired: true,
      ebarimtPrintRequested: true,
      ebarimtPrintConfirmed: true,
      ebarimtGiven: true,
      ebarimtStatus: "GIVEN",
      ebarimtPrintedAt: timestamp,
      ebarimtGivenAt: timestamp,
    };
  }

  if (
    ["PRINT_WINDOW_CLOSED", "PRINT_CONFIRM_TIMEOUT"].includes(status)
  ) {
    return {
      ...receiptFields,
      ebarimtRequired: true,
      ebarimtPrintRequested: true,
      ebarimtPrintConfirmed: false,
      ebarimtGiven: false,
      ebarimtStatus: "CREATED_NOT_PRINTED",
      ebarimtPreparedAt: timestamp,
    };
  }

  if (status === "ERROR") {
    return {
      ...receiptFields,
      ebarimtRequired: true,
      ebarimtPrintRequested: true,
      ebarimtPrintConfirmed: false,
      ebarimtGiven: false,
      ebarimtStatus: "ERROR",
      ebarimtPreparedAt: timestamp,
    };
  }

  if (
    [
      "SKIPPED_BY_OPERATOR",
      "PREVIEW_CANCELLED",
      "NOT_REQUESTED",
    ].includes(status)
  ) {
    return {
      ...receiptFields,
      ebarimtRequired: false,
      ebarimtPrintRequested: false,
      ebarimtPrintConfirmed: false,
      ebarimtGiven: false,
      ebarimtStatus: "NOT_REQUESTED",
    };
  }

  if (status === "NOT_REQUIRED") {
    return {
      ebarimtRequired: false,
      ebarimtPrintRequested: false,
      ebarimtPrintConfirmed: false,
      ebarimtGiven: false,
      ebarimtStatus: "NOT_REQUIRED",
    };
  }

  return {
    ...receiptFields,
    ebarimtRequired: true,
    ebarimtPrintRequested: false,
    ebarimtPrintConfirmed: false,
    ebarimtGiven: false,
    ebarimtStatus: "PENDING_PRINT",
    ebarimtPreparedAt: timestamp,
  };
};

const getEbarimtPrintableAmount = (record) =>
  getCleanPrice(
    record?.ebarimtAmount ??
      record?.ebarimt_amount ??
      record?.paidAmount ??
      record?.paid_amount ??
      record?.amountDue ??
      record?.amount_due ??
      record?.price,
  );

const getEbarimtPaymentMethod = (record) => {
  const paymentType = getCanonicalPaymentType(record);
  const explicitMethod = String(
    record?.paymentMethod ??
      record?.payment_method ??
      record?.manualSettlementPaymentMethod ??
      "",
  )
    .trim()
    .toUpperCase();

  if (explicitMethod) return explicitMethod;
  if (paymentType === "qpay") return "QPAY";
  if (paymentType === "card") return "CARD";
  if (paymentType === "cash") return "CASH";

  return paymentType.toUpperCase();
};

const getEbarimtReference = (record) =>
  firstNonEmptyString(
    record?.posTransactionId,
    record?.transactionId,
    record?.transaction_id,
    record?.invoiceId,
    record?.invoice_id,
    record?.referenceNumber,
    record?.reference_number,
  );

const getEbarimtPaymentDate = (record) =>
  record?.payment_time ||
  record?.paymentTime ||
  record?.paymentConfirmedAt ||
  record?.paidAt ||
  record?.posPaidAt ||
  record?.manualPosPaidAt ||
  record?.settledAt ||
  record?.exit_time ||
  record?.entry_time;

const buildMinuXprinterEbarimtPayload = ({
  parkId,
  parkingName,
  parkingConfig,
  parkingRecord,
  amount,
  paymentType,
  paymentMethod,
  transactionId,
  sourceDocumentPath,
  billType = "1",
  customerNo = "",
  customerName = "",
}) => {
  const cleanAmount = getCleanPrice(amount);
  const printerConfig = getMinuXprinterEbarimtConfig(
    parkingConfig,
    parkingName,
    parkId,
  );
  const apiPaymentType = getMinuEbarimtPaymentType(paymentType, paymentMethod);
  const amountMinor = formatEbarimtApiAmount(cleanAmount);
  const vehiclePlate = String(
    parkingRecord?.car_plate || parkingRecord?.plate || "-",
  );
  const paymentDateText = formatDateTime(getEbarimtPaymentDate(parkingRecord));
  const paymentLabel =
    PAYMENT_TYPE_LABELS[paymentType] || String(paymentMethod || "QPay / QR");
  const normalizedBillType = String(billType || "1") === "3" ? "3" : "1";
  const cleanCustomerNo = String(customerNo || "").trim();
  const cleanCustomerName = String(customerName || "").trim();

  const receiptRows = [
    { label: "Салбар", value: printerConfig.branchName },
    { label: "Төрөл", value: paymentLabel },
    {
      label: "Баримт",
      value: normalizedBillType === "3" ? "Байгууллага" : "Хувь хүн",
    },
    { label: "Огноо", value: paymentDateText },
    { label: "Улсын дугаар", value: vehiclePlate },
    { label: "Мөнгөн дүн", value: `${cleanAmount.toLocaleString()}₮` },
  ];

  if (normalizedBillType === "3") {
    receiptRows.splice(
      3,
      0,
      { label: "Байгууллага", value: cleanCustomerName || "-" },
      { label: "РД", value: cleanCustomerNo || "-" },
    );
  }

  return {
    provider: MINU_EBARIMT_PROVIDER,
    receiptPrinterType: XPRINTER_RECEIPT_TYPE,
    terminalId: printerConfig.terminalId,
    amount: cleanAmount,
    amountMinor,
    type: apiPaymentType,
    minuEbarimt: {
      provider: MINU_EBARIMT_PROVIDER,
      terminalId: printerConfig.terminalId,
      amount: amountMinor,
      type: apiPaymentType,
      sendData: {
        url: MINU_EBARIMT_SENDDATA_URL,
        method: "POST",
        contentType: "application/json; charset=UTF-8",
        body: {
          terminalId: printerConfig.terminalId,
        },
      },
      ebarimtPut: {
        url: MINU_EBARIMT_PUT_URL,
        method: "POST",
        contentType: "application/json",
        body: {
          terminalId: printerConfig.terminalId,
          amount: amountMinor,
          type: apiPaymentType,
          billType: normalizedBillType,
          ...(normalizedBillType === "3" && cleanCustomerNo
            ? { customerNo: cleanCustomerNo }
            : {}),
        },
      },
      encryption: {
        algorithm: "AES-CBC",
        keyRef: "MINU_EBARIMT_AES_KEY",
        ivRef: "MINU_EBARIMT_AES_IV",
        iv: "",
      },
      expectedResponseFields: {
        date: "data.date",
        qrData: "data.qrData",
        billId: "data.billId",
        lottery: "data.lottery",
      },
    },
    xprinterReceipt: {
      printerType: XPRINTER_RECEIPT_TYPE,
      printerName: printerConfig.printerName,
      paperWidthMm: printerConfig.paperWidthMm,
      protocol: "ESC_POS",
      title: EBARIMT_RECEIPT_TITLE,
      branchName: printerConfig.branchName,
      carPlate: vehiclePlate,
      paymentType,
      paymentMethod,
      paymentLabel,
      amount: cleanAmount,
      amountText: `${cleanAmount.toLocaleString()}₮`,
      transactionId: String(transactionId || ""),
      sourceDocumentPath: String(sourceDocumentPath || ""),
      billType: normalizedBillType,
      customerNo: cleanCustomerNo,
      customerName: cleanCustomerName,
      rows: receiptRows,
      qrDataField: "qrData",
      billIdField: "billId",
      lotteryField: "lottery",
      cutPaper: true,
    },
    receiptPreview: {
      title: EBARIMT_RECEIPT_TITLE,
      branchName: printerConfig.branchName || parkingName || "IPARKING",
      printerName: printerConfig.printerName,
      terminalId: printerConfig.terminalId,
      amountText: `${cleanAmount.toLocaleString()}₮`,
      amountMinor,
      type: apiPaymentType,
      billType: normalizedBillType,
      customerNo: cleanCustomerNo,
      customerName: cleanCustomerName,
      rows: receiptRows,
    },
  };
};

const extractMinuEbarimtReceiptData = (responseData) => {
  const candidates = [
    responseData?.data?.data,
    responseData?.data,
    responseData?.result?.data,
    responseData?.result,
    responseData?.receipt,
    responseData?.ebarimt,
    responseData,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;

    const date = firstNonEmptyString(
      candidate.date,
      candidate.ebarimtDate,
      candidate.billDate,
    );
    const qrData = firstNonEmptyString(
      candidate.qrData,
      candidate.qr_data,
      candidate.qr,
    );
    const billId = firstNonEmptyString(
      candidate.billId,
      candidate.bill_id,
      candidate.id,
    );
    const lottery = firstNonEmptyString(
      candidate.lottery,
      candidate.lotteryNumber,
      candidate.lottery_number,
    );

    if (date || qrData || billId || lottery) {
      return {
        hasReceiptData: true,
        date,
        qrData,
        billId,
        lottery,
      };
    }
  }

  return {
    hasReceiptData: false,
    date: "",
    qrData: "",
    billId: "",
    lottery: "",
  };
};

const getWindowObject = () => {
  if (typeof window === "undefined") {
    throw new Error("Browser орчин олдсонгүй. EBarimt-ийг dashboard browser дээрээс үүсгэнэ.");
  }

  return window;
};

const isCorsLikeFetchError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();

  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed") ||
    message.includes("network request failed")
  );
};

// Browser-side Minu AES and direct API calls removed intentionally.
// Server validates payment, reserves the receipt ID, and protects the credentials.

const normalizeEbarimtMerchantInfo = (payload, requestedRegNo) => {
  const cleanRegNo = String(requestedRegNo || "").trim().toUpperCase();
  const source =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const nestedSource =
    source?.data && typeof source.data === "object" ? source.data : {};
  const merchantName = firstNonEmptyString(
    source?.name,
    source?.merchantName,
    source?.customerName,
    source?.companyName,
    nestedSource?.name,
    nestedSource?.merchantName,
    nestedSource?.customerName,
    nestedSource?.companyName,
  );
  const merchantRegNo = firstNonEmptyString(
    source?.regNo,
    source?.regno,
    source?.registerNo,
    source?.tin,
    nestedSource?.regNo,
    nestedSource?.regno,
    nestedSource?.registerNo,
    nestedSource?.tin,
    cleanRegNo,
  ).toUpperCase();

  if (!merchantName) {
    throw new Error("Байгууллага олдсонгүй эсвэл регистрийн дугаар буруу байна.");
  }

  return {
    regNo: merchantRegNo,
    name: merchantName,
    raw: source,
  };
};

const getEbarimtMerchantInfoEndpoint = (projectId) => {
  const cleanProjectId = firstNonEmptyString(
    projectId,
    DEFAULT_FIREBASE_PROJECT_ID,
  );

  return `https://${EBARIMT_MERCHANT_INFO_REGION}-${cleanProjectId}.cloudfunctions.net/${EBARIMT_MERCHANT_INFO_FUNCTION_NAME}`;
};

const fetchEbarimtMerchantInfo = async (projectId, regNo) => {
  const cleanRegNo = String(regNo || "").trim().toUpperCase();

  if (!cleanRegNo) {
    throw new Error("Байгууллагын регистрийн дугаараа оруулна уу.");
  }

  try {
    const response = await fetch(getEbarimtMerchantInfoEndpoint(projectId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        regNo: cleanRegNo,
        regno: cleanRegNo,
      }),
    });
    const responseText = await response.text();
    let responseJson = {};

    try {
      responseJson = responseText ? JSON.parse(responseText) : {};
    } catch (parseError) {
      throw new Error(
        `Байгууллагын мэдээлэл JSON биш хариу буцаалаа: ${responseText.slice(0, 180)}`,
      );
    }

    if (!response.ok) {
      throw new Error(
        responseJson?.message ||
          responseJson?.error?.message ||
          "Байгууллагын мэдээлэл шалгахад серверийн алдаа гарлаа.",
      );
    }

    return normalizeEbarimtMerchantInfo(responseJson?.data || responseJson, cleanRegNo);
  } catch (error) {
    const message = String(error?.message || error || "");

    throw new Error(
      message || "Байгууллагын мэдээлэл шалгахад холболтын алдаа гарлаа.",
    );
  }
};

const getDirectEbarimtQrImageUrl = (qrData) => {
  const browserWindow = getWindowObject();
  const cleanQrData = String(qrData || "").trim();

  if (!cleanQrData) return "";

  return `${DIRECT_EBARIMT_QR_IMAGE_URL}?size=220x220&margin=1&data=${browserWindow.encodeURIComponent(cleanQrData)}`;
};

const buildDirectEbarimtReceiptHtml = ({
  ebarimtPrintPayload,
  receiptData,
  includePrintControls = false,
  printConfirmationId = "",
  asDocument = true,
}) => {
  const receipt = ebarimtPrintPayload?.xprinterReceipt || {};
  const preview = ebarimtPrintPayload?.receiptPreview || {};
  const qrImageUrl = getDirectEbarimtQrImageUrl(receiptData?.qrData);
  const rows = Array.isArray(receipt.rows) ? receipt.rows : [];
  const receiptPaperWidthMm = Number(receipt.paperWidthMm || 58);
  const safePaperWidthMm =
    Number.isFinite(receiptPaperWidthMm) && receiptPaperWidthMm > 0
      ? receiptPaperWidthMm
      : 58;
  const printableRowsCount = rows.length + 3;
  const receiptPageHeightMm = Math.ceil(
    30 + printableRowsCount * 4.8 + (qrImageUrl ? 42 : 0) + 10,
  );
  const rowsHtml = rows
    .map(
      (row) => `
        <div class="row">
          <span>${escapeHtml(row.label)}</span>
          <b>${escapeHtml(row.value)}</b>
        </div>
      `,
    )
    .join("");
  const printControlsHtml = includePrintControls
    ? `
      <div class="screen-actions">
        <button id="ebarimt-print-confirm-button" type="button">Баримт хэвлэх</button>
        <button type="button" onclick="window.close()">Хаах</button>
      </div>
    `
    : "";
  const printConfirmationScript =
    includePrintControls && printConfirmationId
      ? `
        <script>
          (() => {
            const confirmationId = ${JSON.stringify(String(printConfirmationId))};
            const printButton = document.getElementById("ebarimt-print-confirm-button");

            if (!printButton) return;

            printButton.addEventListener("click", () => {
              if (printButton.dataset.confirmed === "1") return;

              printButton.dataset.confirmed = "1";
              printButton.disabled = true;
              printButton.textContent = "Хэвлэхээр баталгаажлаа";

              try {
                if (window.opener && !window.opener.closed) {
                  window.opener.postMessage(
                    {
                      type: "IPARKING_EBARIMT_PRINT_CONFIRMED",
                      confirmationId,
                    },
                    "*",
                  );
                }
              } catch (error) {
                // Parent window notification алдаа хэвлэлтийг зогсоохгүй.
              }

              window.print();

              setTimeout(() => {
                try {
                  window.close();
                } catch (error) {
                  // Browser хаахыг хориглосон байж болно.
                }
              }, 350);
            });
          })();
        <\/script>
      `
      : "";
  const stylesHtml = `
    <style>
      @page {
        size: ${safePaperWidthMm}mm ${receiptPageHeightMm}mm;
        margin: 0;
      }

      * {
        box-sizing: border-box;
      }

      html,
      body {
        width: 100%;
        min-width: 0;
        margin: 0;
        padding: 0;
        background: #f8fafc;
        color: #0f172a;
        font-family: Arial, "Helvetica Neue", sans-serif;
        -webkit-text-size-adjust: 100%;
      }

      body {
        min-height: 100vh;
        padding: 12px;
      }

      .screen-actions {
        position: sticky;
        top: 0;
        z-index: 10;
        width: min(100%, 380px);
        margin: 0 auto 10px;
        display: flex;
        gap: 8px;
        padding: 8px;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        background: rgba(255,255,255,.96);
      }

      .screen-actions button {
        min-width: 0;
        flex: 1 1 0;
        min-height: 42px;
        border: 1px solid #dbe2ea;
        border-radius: 10px;
        padding: 9px 12px;
        color: #0f172a;
        background: #ffffff;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
      }

      .screen-actions button:first-child {
        border-color: #0f172a;
        background: #0f172a;
        color: #ffffff;
      }

      .screen-actions button:disabled {
        opacity: .65;
        cursor: default;
      }

      .receipt {
        width: min(100%, 380px);
        margin: 0 auto;
        padding: 18px;
        border: 1px solid #e2e8f0;
        border-radius: 16px;
        background: #ffffff;
        overflow-wrap: anywhere;
      }

      .title {
        margin: 0;
        text-align: center;
        font-size: 16px;
        font-weight: 800;
      }

      .subtitle {
        margin: 4px 0 0;
        text-align: center;
        font-size: 11px;
        color: #64748b;
      }

      .amount {
        margin: 14px 0 12px;
        text-align: center;
        font-size: clamp(24px, 8vw, 32px);
        line-height: 1;
        font-weight: 800;
        letter-spacing: -0.03em;
      }

      .row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
        align-items: start;
        gap: 10px;
        border-top: 1px solid #e2e8f0;
        padding: 8px 0;
        font-size: 11px;
        line-height: 1.35;
      }

      .row span {
        color: #64748b;
      }

      .row b {
        min-width: 0;
        text-align: right;
        font-weight: 700;
        overflow-wrap: anywhere;
      }

      .qr {
        display: block;
        width: min(62vw, 190px);
        height: auto;
        aspect-ratio: 1 / 1;
        margin: 14px auto 10px;
      }

      .footer {
        margin-top: 8px;
        border-top: 1px solid #e2e8f0;
        padding-top: 10px;
        text-align: center;
        font-size: 10px;
        line-height: 1.35;
        color: #64748b;
      }

      @media (max-width: 480px) {
        body {
          padding: 0;
          background: #ffffff;
        }

        .screen-actions {
          position: sticky;
          width: 100%;
          margin: 0;
          padding: 10px;
          border: 0;
          border-bottom: 1px solid #e2e8f0;
          border-radius: 0;
        }

        .screen-actions button {
          min-height: 44px;
          padding: 9px 8px;
          font-size: 12px;
        }

        .receipt {
          width: 100%;
          max-width: none;
          margin: 0;
          padding: 16px 14px 24px;
          border: 0;
          border-radius: 0;
        }

        .row {
          grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr);
          gap: 8px;
        }
      }

      @media print {
        html,
        body {
          width: ${safePaperWidthMm}mm;
          min-width: ${safePaperWidthMm}mm;
          min-height: 0;
          margin: 0;
          padding: 0;
          background: #ffffff;
        }

        .screen-actions {
          display: none !important;
        }

        .receipt {
          width: ${safePaperWidthMm}mm;
          max-width: ${safePaperWidthMm}mm;
          margin: 0;
          padding: 2mm 3mm 1mm;
          border: 0;
          border-radius: 0;
          box-shadow: none;
          page-break-after: avoid;
        }

        .title {
          font-size: 14px;
        }

        .subtitle {
          font-size: 10px;
        }

        .amount {
          margin: 4px 0 6px;
          font-size: 20px;
        }

        .row {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          border-top: 1px dashed #94a3b8;
          padding: 3px 0;
          font-size: 10px;
        }

        .row span {
          color: #111827;
        }

        .row b {
          text-align: right;
        }

        .qr {
          width: 34mm;
          height: 34mm;
          margin: 6px auto 4px;
        }

        .footer {
          margin-top: 4px;
          border-top: 1px dashed #94a3b8;
          padding-top: 4px;
          font-size: 9px;
        }
      }
    </style>
  `;
  const receiptHtml = `
    ${printControlsHtml}
    <main class="receipt">
      <h1 class="title">${escapeHtml(preview.title || EBARIMT_RECEIPT_TITLE)}</h1>
      <p class="subtitle">${escapeHtml(preview.branchName || receipt.branchName || "-")}</p>
      <div class="amount">${escapeHtml(receipt.amountText || preview.amountText || "")}</div>
      ${rowsHtml}
      ${
        qrImageUrl
          ? `<img class="qr" src="${escapeHtml(qrImageUrl)}" alt="EBarimt QR" />`
          : ""
      }
      <div class="row">
        <span>Bill ID</span>
        <b>${escapeHtml(receiptData?.billId || "-")}</b>
      </div>
      <div class="row">
        <span>Сугалаа</span>
        <b>${escapeHtml(receiptData?.lottery || "-")}</b>
      </div>
      <div class="row">
        <span>EBarimt огноо</span>
        <b>${escapeHtml(receiptData?.date || "-")}</b>
      </div>
      <div class="footer">
        Баярлалаа<br/>
        ${escapeHtml(receipt.carPlate || "")}
      </div>
    </main>
    ${printConfirmationScript}
  `;

  if (!asDocument) {
    return `${stylesHtml}${receiptHtml}`;
  }

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(EBARIMT_RECEIPT_TITLE)}</title>
        ${stylesHtml}
      </head>
      <body style="margin: 0; background: #f8fafc; color: #111827; font-family: Arial, 'Helvetica Neue', sans-serif;">
        ${receiptHtml}
      </body>
    </html>
  `;
};

const openDirectEbarimtPrintWindow = ({
  ebarimtPrintPayload,
  receiptData,
  printConfirmationId,
}) => {
  const browserWindow = getWindowObject();
  const printWindow = browserWindow.open("", "_blank", "width=460,height=760,resizable=yes,scrollbars=yes");

  if (!printWindow) {
    throw new Error("Print цонх нээгдсэнгүй. Browser popup blocker-ийг зөвшөөрнө үү.");
  }

  printWindow.document.open();
  printWindow.document.write(
    buildDirectEbarimtReceiptHtml({
      ebarimtPrintPayload,
      receiptData,
      includePrintControls: true,
      printConfirmationId,
    }),
  );
  printWindow.document.close();
  printWindow.focus();

  return printWindow;
};

const waitForEbarimtPrintConfirmation = ({
  printWindow,
  confirmationId,
  timeoutMilliseconds = 10 * 60 * 1000,
}) =>
  new Promise((resolve) => {
    const browserWindow = getWindowObject();
    let isResolved = false;
    let closePoll = null;
    let timeoutId = null;

    const cleanup = () => {
      browserWindow.removeEventListener("message", handleMessage);

      if (closePoll) {
        browserWindow.clearInterval(closePoll);
      }

      if (timeoutId) {
        browserWindow.clearTimeout(timeoutId);
      }
    };

    const finish = (status) => {
      if (isResolved) return;
      isResolved = true;
      cleanup();
      resolve(status);
    };

    const handleMessage = (event) => {
      if (
        event?.source === printWindow &&
        event?.data?.type === "IPARKING_EBARIMT_PRINT_CONFIRMED" &&
        event?.data?.confirmationId === confirmationId
      ) {
        finish("PRINT_CONFIRMED");
      }
    };

    browserWindow.addEventListener("message", handleMessage);

    closePoll = browserWindow.setInterval(() => {
      if (printWindow?.closed) {
        finish("PRINT_WINDOW_CLOSED");
      }
    }, 500);

    timeoutId = browserWindow.setTimeout(() => {
      finish("PRINT_CONFIRM_TIMEOUT");
    }, timeoutMilliseconds);
  });

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .split('"')
    .join("&quot;")
    .split("'")
    .join("&#039;");

/**
 * Firebase болон Cloud Function алдааны
 * хэрэглэгчид харуулах мессеж.
 */
const getErrorMessage = (
  error,
  fallbackMessage = "Тодорхойгүй алдаа гарлаа.",
) => {
  if (typeof error?.message === "string") {
    return error.message;
  }

  if (typeof error?.details === "string") {
    return error.details;
  }

  return fallbackMessage;
};

const LEGACY_GATE_CAMERAS = [
  {
    camera_id: "enter_1",
    name: "Орох хаалга",
    role: "enter",
    enabled: true,
    configured: true,
    legacyField: "gate.enterOpen",
  },
  {
    camera_id: "exit_1",
    name: "Гарах хаалга",
    role: "exit",
    enabled: true,
    configured: true,
    legacyField: "gate.exitOpen",
  },
];

const normalizeDashboardCameraPlan = (rawPlan, rawCameras) => {
  const rawRows = Array.isArray(rawCameras)
    ? rawCameras
    : rawCameras && typeof rawCameras === "object"
      ? Object.entries(rawCameras).map(([key, value]) => ({
          ...(value && typeof value === "object" ? value : {}),
          camera_id: value?.camera_id || value?.id || key,
        }))
      : [];

  const countConfiguredRole = (role) =>
    rawRows.filter((camera) => {
      const cameraRole =
        String(camera?.role || "")
          .trim()
          .toLowerCase() === "exit"
          ? "exit"
          : "enter";

      return cameraRole === role && camera?.configured !== false;
    }).length;

  const actualEnter = countConfiguredRole("enter");
  const actualExit = countConfiguredRole("exit");

  const hasPlan =
    rawPlan && typeof rawPlan === "object" && !Array.isArray(rawPlan);

  let enterCount = hasPlan ? Number(rawPlan.enterCount) : actualEnter;

  let exitCount = hasPlan ? Number(rawPlan.exitCount) : actualExit;

  if (!Number.isInteger(enterCount) || enterCount < 0) {
    enterCount = actualEnter;
  }

  if (!Number.isInteger(exitCount) || exitCount < 0) {
    exitCount = actualExit;
  }

  enterCount = Math.min(32, Math.max(0, enterCount));
  exitCount = Math.min(32, Math.max(0, exitCount));

  if (!hasPlan && actualEnter === 0 && actualExit === 0) {
    enterCount = 1;
    exitCount = 1;
  }

  return {
    enterCount,
    exitCount,
    totalCount: enterCount + exitCount,
  };
};

const normalizeGateDisplayName = (value, role, index) => {
  const fallback =
    role === "exit" ? `Гарах хаалга ${index}` : `Орох хаалга ${index}`;

  const raw = String(value || "").trim();
  if (!raw) return fallback;

  return raw
    .replace(/Орох\s+камер/gi, "Орох хаалга")
    .replace(/Гарах\s+камер/gi, "Гарах хаалга")
    .replace(/\bENTER\s+CAMERA\b/gi, "Орох хаалга")
    .replace(/\bEXIT\s+CAMERA\b/gi, "Гарах хаалга");
};

const normalizeGateCameras = (
  rawValue,
  dashboardButtons = {},
  cameraPlan = null,
) => {
  const rows = Array.isArray(rawValue)
    ? rawValue
    : rawValue && typeof rawValue === "object"
      ? Object.entries(rawValue).map(([cameraId, value]) => ({
          ...(value && typeof value === "object" ? value : {}),
          camera_id: value?.camera_id || value?.id || cameraId,
        }))
      : [];

  const buttonSettings =
    dashboardButtons &&
    typeof dashboardButtons === "object" &&
    !Array.isArray(dashboardButtons)
      ? dashboardButtons
      : {};

  const hasExplicitCameraPlan =
    cameraPlan && typeof cameraPlan === "object" && !Array.isArray(cameraPlan);

  /*
   * Backward compatibility:
   * Хуучин parking document дээр gate.cameras болон gate.cameraPlan
   * огт байхгүй бол planned placeholder камер үүсгэхгүй.
   *
   * Ингэснээр displayedGateCameras нь LEGACY_GATE_CAMERAS руу fallback
   * хийж gate.enterOpen / gate.exitOpen товчнууд идэвхтэй үлдэнэ.
   *
   * Харин шинэ parking дээр cameraPlan байгаа боловч local registry
   * хараахан publish хийгдээгүй бол placeholder камер disabled хэвээр
   * харагдана — энэ нь шинэ architecture-ийн хамгаалалт.
   */
  if (rows.length === 0 && !hasExplicitCameraPlan) {
    return [];
  }

  const actualById = new Map();

  rows.forEach((camera) => {
    const cameraId = String(camera?.camera_id || camera?.id || "")
      .trim()
      .toLowerCase();

    if (!cameraId) return;

    const role =
      String(camera?.role || "")
        .trim()
        .toLowerCase() === "exit"
        ? "exit"
        : "enter";

    actualById.set(cameraId, {
      camera_id: cameraId,
      name: normalizeGateDisplayName(
        camera?.name,
        role,
        Number(String(cameraId).split("_").pop()) || 1,
      ),
      role,
      vendor: String(camera?.vendor || "")
        .trim()
        .toLowerCase(),
      enabled: camera?.enabled !== false,
      configured: camera?.configured !== false,
    });
  });

  const plan = normalizeDashboardCameraPlan(cameraPlan, rawValue);

  const plannedIds = [];

  for (let index = 1; index <= plan.enterCount; index += 1) {
    plannedIds.push({
      cameraId: `enter_${index}`,
      role: "enter",
      index,
    });
  }

  for (let index = 1; index <= plan.exitCount; index += 1) {
    plannedIds.push({
      cameraId: `exit_${index}`,
      role: "exit",
      index,
    });
  }

  return plannedIds.map(({ cameraId, role, index }, plannedIndex) => {
    const actual = actualById.get(cameraId);

    const button = buttonSettings[cameraId] || {};

    return {
      ...(actual || {}),
      camera_id: cameraId,
      role,
      name: normalizeGateDisplayName(actual?.name, role, index),
      vendor: actual?.vendor || "",
      enabled: actual?.enabled === true,
      configured: Boolean(actual) && actual?.configured !== false,
      dashboardLabel: normalizeGateDisplayName(
        button?.label || actual?.name,
        role,
        index,
      ),
      dashboardVisible: button?.visible !== false,
      dashboardOrder: Number.isFinite(Number(button?.order))
        ? Math.max(0, Math.trunc(Number(button.order)))
        : plannedIndex + 1,
    };
  });
};

const createGateCommandId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const GATE_COMMAND_TIMEOUT_MS = 45000;

const normalizeCommandStatus = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

const timestampKey = (value) => {
  if (!value) return "";

  if (
    typeof value?.seconds === "number" &&
    typeof value?.nanoseconds === "number"
  ) {
    return `${value.seconds}:${value.nanoseconds}`;
  }

  if (typeof value?.toMillis === "function") {
    return String(value.toMillis());
  }

  if (value instanceof Date) {
    return String(value.getTime());
  }

  return String(value);
};

const waitForDynamicGateCommand = (
  parkingReference,
  cameraId,
  requestId,
  timeoutMs = GATE_COMMAND_TIMEOUT_MS,
) =>
  new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe = () => {};

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);

      try {
        unsubscribe();
      } catch (_) {}

      callback(value);
    };

    const timer = setTimeout(() => {
      const error = new Error(
        "Firestore-д команд хадгалагдсан боловч local parking server хугацаандаа хариу өгсөнгүй.",
      );
      error.code = "gate/server-timeout";
      finish(reject, error);
    }, timeoutMs);

    unsubscribe = onSnapshot(
      parkingReference,
      (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data() || {};
        const command = data.gate?.commands?.[cameraId];

        if (!command) return;

        if (String(command.requestId || "") !== String(requestId || "")) {
          return;
        }

        const status = normalizeCommandStatus(command.status);

        if (status === "SUCCESS") {
          finish(resolve, {
            status,
            message:
              command.message ||
              "Сервер хаалт нээх командыг амжилттай гүйцэтгэлээ.",
            attempt: Number(command.attempt) || 1,
          });
          return;
        }

        if (
          ["FAILED", "SCHEDULE_FAILED", "CANCELLED", "CONFLICT"].includes(
            status,
          )
        ) {
          const error = new Error(
            command.message || `Хаалт нээх команд ${status} төлөвтэй дууслаа.`,
          );
          error.code = `gate/${status.toLowerCase()}`;
          error.commandStatus = status;
          error.attempt = Number(command.attempt) || 0;
          finish(reject, error);
        }
      },
      (snapshotError) => {
        const error = new Error(
          getErrorMessage(
            snapshotError,
            "Хаалганы command status уншиж чадсангүй.",
          ),
        );
        error.code = "gate/status-listener-error";
        finish(reject, error);
      },
    );
  });

const waitForLegacyGateCommand = (
  parkingReference,
  commandId,
  previousUpdatedAtKey = "",
  timeoutMs = GATE_COMMAND_TIMEOUT_MS,
) =>
  new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe = () => {};

    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);

      try {
        unsubscribe();
      } catch (_) {}

      callback(value);
    };

    const timer = setTimeout(() => {
      const error = new Error(
        "Firestore-д legacy gate command хадгалагдсан боловч local parking server хугацаандаа хариу өгсөнгүй.",
      );
      error.code = "gate/server-timeout";
      finish(reject, error);
    }, timeoutMs);

    unsubscribe = onSnapshot(
      parkingReference,
      (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data() || {};
        const commandStatus = data.remoteCommandStatus?.[commandId];

        if (!commandStatus) return;

        const updatedAtKey = timestampKey(commandStatus.updatedAt);

        // Old SUCCESS/FAILED status-ыг шинэ command-ийн үр дүн гэж
        // андуурахгүй. Server шинэ status бичсэн үед updatedAt өөрчлөгдөнө.
        if (previousUpdatedAtKey && updatedAtKey === previousUpdatedAtKey) {
          return;
        }

        const status = normalizeCommandStatus(commandStatus.status);

        if (status === "SUCCESS") {
          finish(resolve, {
            status,
            message:
              commandStatus.message ||
              "Сервер хаалт нээх командыг амжилттай гүйцэтгэлээ.",
            attempt: Number(commandStatus.attempt) || 1,
          });
          return;
        }

        if (
          ["FAILED", "SCHEDULE_FAILED", "CANCELLED", "CONFLICT"].includes(
            status,
          )
        ) {
          const error = new Error(
            commandStatus.message ||
              `Хаалт нээх command ${status} төлөвтэй дууслаа.`,
          );
          error.code = `gate/${status.toLowerCase()}`;
          error.commandStatus = status;
          error.attempt = Number(commandStatus.attempt) || 0;
          finish(reject, error);
        }
      },
      (snapshotError) => {
        const error = new Error(
          getErrorMessage(
            snapshotError,
            "Legacy gate command status уншиж чадсангүй.",
          ),
        );
        error.code = "gate/status-listener-error";
        finish(reject, error);
      },
    );
  });

/**
 * KPI үзүүлэлтийн карт.
 */
const StatCard = ({ label, value }) => {
  return (
    <div className="tw-min-w-0 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 sm:tw-p-5">
      <p className="tw-m-0 tw-text-[11px] sm:tw-text-xs tw-font-medium tw-text-slate-500">
        {label}
      </p>
      <div className="tw-mt-1.5 tw-break-words tw-text-xl sm:tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-slate-950">
        {value}
      </div>
    </div>
  );
};

/**
 * Төлбөрийн суваг тус бүрийн машины тоо болон орлогын карт.
 */
const PaymentMethodCard = ({
  label,
  count,
  revenue,
  isLoading,
}) => {
  return (
    <div className="tw-min-w-0 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-p-4 sm:tw-p-5">
      <p className="tw-m-0 tw-text-[11px] sm:tw-text-xs tw-font-medium tw-text-slate-500">
        {label}
      </p>
      <div className="tw-mt-1.5 tw-text-lg sm:tw-text-xl tw-font-semibold tw-text-slate-950">
        {isLoading ? "..." : count.toLocaleString()}
      </div>
      <div className="tw-mt-0.5 tw-text-xs sm:tw-text-sm tw-text-slate-500">
        {isLoading ? "..." : `${revenue.toLocaleString()}₮`}
      </div>
    </div>
  );
};

/**
 * Хаалга нээх товч.
 */
const GateButton = ({
  onClick,
  label,
  gradientClass,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (isLoading || disabled) return;

    setIsLoading(true);

    try {
      await onClick();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading || disabled}
      className={`
        tw-w-full tw-min-w-0 tw-h-11 sm:tw-h-12
        tw-inline-flex tw-items-center tw-justify-center tw-gap-2
        ${gradientClass}
        tw-rounded-xl tw-px-4
        tw-text-sm tw-font-medium tw-text-white
        tw-transition-opacity
        hover:tw-opacity-95 active:tw-opacity-90
        disabled:tw-cursor-not-allowed disabled:tw-opacity-50
      `}
    >
      {isLoading && (
        <FiLoader className="tw-h-4 tw-w-4 tw-animate-spin tw-shrink-0" />
      )}
      <span className="tw-truncate">{label}</span>
    </button>
  );
};

export default function Dashboard() {
  const pageSize = 50;

  const [parkingLogs, setParkingLogs] = useState([]);

  const [totalEnteredCount, setTotalEnteredCount] = useState(0);

  const [occupiedSpots, setOccupiedSpots] = useState(0);

  const [totalLoss, setTotalLoss] = useState(0);

  const [isAggregating, setIsAggregating] = useState(false);

  const [searchInput, setSearchInput] = useState("");

  const [searchPlate, setSearchPlate] = useState("");

  const [selectedDate, setSelectedDate] = useState(new Date());

  const [paymentFilter, setPaymentFilter] = useState("all");

  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");

  // Санхүүгийн мэдээллийг дэлгэц нээгдэхэд анхнаасаа нууцална.
  // Firestore-ийн өгөгдөл өөрчлөгдөхгүй, зөвхөн UI дээр харагдах эсэхийг удирдана.
  const [isPaymentSummaryVisible, setIsPaymentSummaryVisible] =
    useState(false);

  const [userRole, setUserRole] = useState("operator");

  const [isLoadingData, setIsLoadingData] = useState(true);

  const [pageCursors, setPageCursors] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);

  const [hasNextPage, setHasNextPage] = useState(true);

  const [userReady, setUserReady] = useState(false);

  const [parkId, setParkId] = useState("");

  const [parkingName, setParkingName] = useState("");

  const [parkingPricingConfig, setParkingPricingConfig] = useState({});

  const [gateCameras, setGateCameras] = useState([]);

  /*
   * Local server-ийн collection_url / branch_id өөр байж болох тул
   * record collection-ийн бодит path-ийг parking config эсвэл олдсон
   * DocumentReference-ээс санана.
   */
  const [recordsCollectionPath, setRecordsCollectionPath] = useState("");

  /**
   * Manual refresh үед өөрчлөгдөж, KPI болон
   * хүснэгтийн useEffect-үүдийг дахин ажиллуулна.
   */
  const [refreshKey, setRefreshKey] = useState(0);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  const [paymentBreakdown, setPaymentBreakdown] = useState(
    createEmptyPaymentBreakdown,
  );

  const [backendPaymentBreakdown, setBackendPaymentBreakdown] = useState(
    createEmptyPaymentBreakdown,
  );

  const [pagePaymentBreakdown, setPagePaymentBreakdown] = useState(
    createEmptyPaymentBreakdown,
  );

  const [realtimeDayRecordCount, setRealtimeDayRecordCount] = useState(0);

  const [backendDayRecordCount, setBackendDayRecordCount] = useState(0);

  const [pageRecordCount, setPageRecordCount] = useState(0);

  const [fallbackPaymentBreakdown, setFallbackPaymentBreakdown] = useState(
    createEmptyPaymentBreakdown,
  );

  const [fallbackPaymentRecords, setFallbackPaymentRecords] = useState([]);

  const [isPaymentBreakdownLoading, setIsPaymentBreakdownLoading] =
    useState(true);

  const currentPageRef = useRef(0);

  const [openingRecordId, setOpeningRecordId] = useState("");

  const [posConnectionStatus, setPosConnectionStatus] =
    useState("disconnected");

  const posPortRef = useRef(null);
  const posReaderRef = useRef(null);
  const posPaymentInProgressRef = useRef(false);

  const auth = getAuth();
  const db = getFirestore(auth.app);
  const firebaseProjectId =
    auth.app.options?.projectId || DEFAULT_FIREBASE_PROJECT_ID;

  // Бүх callable function энэ төслийн default region (us-central1) дээр ажиллана.
  // getDashboardStats болон searchParkingLogs нэг instance ашиглана.
  const functions = getFunctions(auth.app);

  const connectPosTerminal = useCallback(
    async ({ requestPermission = false } = {}) => {
      if (typeof navigator === "undefined" || !("serial" in navigator)) {
        throw new Error(
          "Энэ browser Web Serial дэмжихгүй байна. Windows дээр Chrome эсвэл Edge ашиглана уу.",
        );
      }

      let port = posPortRef.current;

      if (port?.readable && port?.writable) {
        setPosConnectionStatus("connected");
        return port;
      }

      setPosConnectionStatus("connecting");

      try {
        if (!port && requestPermission) {
          // requestPort нь заавал хэрэглэгчийн click дотроос дуудагдана.
          port = await navigator.serial.requestPort();
        }

        if (!port) {
          const permittedPorts = await navigator.serial.getPorts();

          if (permittedPorts.length !== 1) {
            throw new Error(
              permittedPorts.length > 1
                ? "Олон зөвшөөрөгдсөн COM байна. POS холбох товчоор A35 портоо сонгоно уу."
                : "A35 POS холбогдоогүй байна. Эхлээд POS холбох товчийг дарна уу.",
            );
          }

          [port] = permittedPorts;
        }

        if (!port.readable || !port.writable) {
          await port.open({
            baudRate: POS_BAUD_RATE,
            dataBits: 8,
            stopBits: 1,
            parity: "none",
            flowControl: "none",
            bufferSize: 4096,
          });
        }

        posPortRef.current = port;
        setPosConnectionStatus("connected");
        return port;
      } catch (error) {
        setPosConnectionStatus("disconnected");
        throw error;
      }
    },
    [],
  );

  const handleConnectPosTerminal = useCallback(async () => {
    try {
      await connectPosTerminal({ requestPermission: true });

      await MySwal.fire({
        icon: "success",
        title: "A35 POS холбогдлоо",
        text: `COM · ${POS_BAUD_RATE} baud`,
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      await MySwal.fire({
        icon: "error",
        title: "POS холбож чадсангүй",
        text: getErrorMessage(
          error,
          "A35-ын USB/COM холболт болон browser-ийн зөвшөөрлийг шалгана уу.",
        ),
        confirmButtonColor: "#ef4444",
      });
    }
  }, [connectPosTerminal]);

  const runPosPayment = useCallback(
    async (amount, transactionId) => {
      if (posPaymentInProgressRef.current) {
        throw new Error("Өөр картын гүйлгээ боловсруулагдаж байна.");
      }

      const displayAmount = Math.round(Number(amount));
      const posCommandAmount = toPosCommandAmount(displayAmount);

      if (!Number.isFinite(displayAmount) || displayAmount <= 0) {
        throw new Error("POS руу илгээх дүн буруу байна.");
      }

      if (!Number.isFinite(posCommandAmount) || posCommandAmount <= 0) {
        throw new Error("POS руу илгээх дүн буруу байна.");
      }

      const port = await connectPosTerminal();
      posPaymentInProgressRef.current = true;

      let reader = null;
      let timeoutId = null;
      let didTimeout = false;

      try {
        const writer = port.writable.getWriter();

        try {
          const command = buildPosCommand(posCommandAmount, transactionId);
          await writer.write(new TextEncoder().encode(command));
        } catch (error) {
          return {
            status: "UNKNOWN",
            reason: "POS_WRITE_OUTCOME_UNKNOWN",
            description:
              "POS руу команд бүрэн очсон эсэх тодорхойгүй байна.",
            transactionId,
            amount: displayAmount,
            posCommandAmount,
            error,
          };
        } finally {
          writer.releaseLock();
        }

        reader = port.readable.getReader();
        posReaderRef.current = reader;

        const decoder = new TextDecoder();
        let receiveBuffer = "";
        let mismatchSeen = false;

        timeoutId = setTimeout(() => {
          didTimeout = true;
          reader?.cancel().catch(() => {});
        }, POS_PAYMENT_TIMEOUT_MILLISECONDS);

        while (true) {
          const { value, done } = await reader.read();

          if (done) break;
          if (!value) continue;

          receiveBuffer += decoder.decode(value, { stream: true });

          const extracted = extractPosJsonObjects(receiveBuffer);
          receiveBuffer = extracted.remaining;

          for (const candidate of extracted.objects) {
            let payload;

            try {
              payload = JSON.parse(candidate);
            } catch {
              continue;
            }

            const result = parsePosResponse(
              payload,
              transactionId,
              posCommandAmount,
              displayAmount,
            );

            if (result.status === "MISMATCH") {
              mismatchSeen = true;
              continue;
            }

            return result;
          }
        }

        return {
          status: "UNKNOWN",
          reason: mismatchSeen
            ? "POS_RESPONSE_MISMATCH_TIMEOUT"
            : didTimeout
              ? "POS_RESPONSE_TIMEOUT"
              : "POS_CONNECTION_CLOSED",
          description: mismatchSeen
            ? "POS-ийн хариуны transaction ID эсвэл дүн таарсангүй."
            : "POS-ийн эцсийн хариу тодорхойгүй байна.",
          transactionId,
          amount: displayAmount,
          posCommandAmount,
        };
      } catch (error) {
        return {
          status: "UNKNOWN",
          reason: "POS_READ_OUTCOME_UNKNOWN",
          description: "POS-ийн хариуг уншиж чадсангүй.",
          transactionId,
          amount: displayAmount,
          posCommandAmount,
          error,
        };
      } finally {
        if (timeoutId) clearTimeout(timeoutId);

        if (reader) {
          try {
            reader.releaseLock();
          } catch {
            // Reader аль хэдийн суллагдсан байж болно.
          }
        }

        posReaderRef.current = null;
        posPaymentInProgressRef.current = false;

        if (didTimeout && posPortRef.current === port) {
          try {
            await port.close();
          } catch {
            // Дараагийн connect үед портыг дахин нээнэ.
          }

          posPortRef.current = null;
          setPosConnectionStatus("disconnected");
        }
      }
    },
    [connectPosTerminal],
  );

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serial" in navigator)) {
      setPosConnectionStatus("unsupported");
      return undefined;
    }

    let isActive = true;

    const reconnectPermittedPort = async () => {
      try {
        await connectPosTerminal();
      } catch {
        if (isActive) setPosConnectionStatus("disconnected");
      }
    };

    reconnectPermittedPort();

    const handleSerialDisconnect = (event) => {
      const disconnectedPort = event?.port || event?.target;
      if (disconnectedPort !== posPortRef.current) return;

      posPortRef.current = null;
      setPosConnectionStatus("disconnected");
    };

    navigator.serial?.addEventListener("disconnect", handleSerialDisconnect);

    return () => {
      isActive = false;
      navigator.serial?.removeEventListener(
        "disconnect",
        handleSerialDisconnect,
      );

      const reader = posReaderRef.current;
      if (reader) reader.cancel().catch(() => {});
    };
  }, [connectPosTerminal]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  /**
   * parkings/{parkId} document-оос нэр уншина.
   *
   * Дараах бүх бүтэц ажиллана:
   *
   * {
   *     name: "Зогсоолын нэр"
   * }
   *
   * {
   *     config: {
   *         name: "Зогсоолын нэр"
   *     }
   * }
   *
   * {
   *     settings: {
   *         name: "Зогсоолын нэр"
   *     }
   * }
   */
  const fetchParkingName = useCallback(
    async (currentParkId) => {
      if (!currentParkId) {
        setParkingName("");
        setRecordsCollectionPath("");
        setParkingPricingConfig({});
        return;
      }

      try {
        const parkingReference = doc(db, "parkings", currentParkId);

        const parkingSnapshot = await getDoc(parkingReference);

        if (!parkingSnapshot.exists()) {
          console.warn(
            `Зогсоолын document олдсонгүй: parkings/${currentParkId}`,
          );

          setParkingName(currentParkId);
          setGateCameras([]);
          setRecordsCollectionPath(`${currentParkId}/cars/records`);
          setParkingPricingConfig({});
          return;
        }

        const parkingData = parkingSnapshot.data() || {};
        setParkingPricingConfig({
          ...(parkingData || {}),
          ...(parkingData.config || {}),
          ...(parkingData.settings || {}),
        });

        setRecordsCollectionPath(
          getConfiguredRecordsCollectionPath(parkingData, currentParkId),
        );

        setGateCameras(
          normalizeGateCameras(
            parkingData.gate?.cameras,
            parkingData.gate?.dashboardButtons,
            parkingData.gate?.cameraPlan,
          ),
        );

        const rawName =
          parkingData.name ||
          parkingData.config?.name ||
          parkingData.settings?.name ||
          parkingData.parkingName ||
          currentParkId;

        const safeName = String(rawName || "").trim();

        setParkingName(safeName || currentParkId);
      } catch (error) {
        console.error("Зогсоолын нэр уншихад алдаа гарлаа:", error);

        setParkingName(currentParkId);
        setGateCameras([]);
        setRecordsCollectionPath(`${currentParkId}/cars/records`);
        setParkingPricingConfig({});
      }
    },
    [db],
  );

  /**
   * Нэвтэрсэн хэрэглэгчийн role уншина.
   */
  const fetchUserRole = useCallback(
    async (user, currentParkId) => {
      if (!user?.email || !currentParkId) {
        setUserRole("operator");
        return;
      }

      try {
        const userReference = doc(
          db,
          "parkings",
          currentParkId,
          "users",
          user.email,
        );

        const userSnapshot = await getDoc(userReference);

        if (!userSnapshot.exists()) {
          setUserRole("operator");
          return;
        }

        const role = userSnapshot.data()?.role;

        if (role === "master") {
          setUserRole("master");
          return;
        }

        if (role === "admin") {
          setUserRole("admin");
          return;
        }

        setUserRole("operator");
      } catch (error) {
        console.error("Хэрэглэгчийн эрх уншихад алдаа:", error);

        setUserRole("operator");
      }
    },
    [db],
  );

  /**
   * Firebase Authentication хэрэглэгчийг сонсоно.
   *
   * Энэ системд:
   * user.displayName = parkId
   */
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setParkId("");
        setParkingName("");
        setGateCameras([]);
        setRecordsCollectionPath("");
        setParkingPricingConfig({});
        setUserRole("operator");
        setUserReady(false);
        setParkingLogs([]);
        setPageCursors([]);
        setCurrentPage(0);
        setHasNextPage(false);
        setPaymentTypeFilter("all");
        setIsLoadingData(false);
        setPaymentBreakdown(createEmptyPaymentBreakdown());
        setBackendPaymentBreakdown(createEmptyPaymentBreakdown());
        setPagePaymentBreakdown(createEmptyPaymentBreakdown());
        setRealtimeDayRecordCount(0);
        setBackendDayRecordCount(0);
        setPageRecordCount(0);
        setFallbackPaymentBreakdown(createEmptyPaymentBreakdown());
        setFallbackPaymentRecords([]);
        setIsPaymentBreakdownLoading(false);

        return;
      }

      setUserReady(false);

      try {
        await user.getIdToken(true);

        const currentParkId = String(user.displayName || "").trim();

        if (!currentParkId) {
          throw new Error(
            "Firebase Auth хэрэглэгчийн displayName буюу parkId тохируулагдаагүй байна.",
          );
        }

        setParkId(currentParkId);

        await Promise.all([
          fetchParkingName(currentParkId),
          fetchUserRole(user, currentParkId),
        ]);

        setUserReady(true);
      } catch (error) {
        console.error("Firebase хэрэглэгчийн мэдээлэл бэлтгэхэд алдаа:", error);

        setParkId("");
        setParkingName("");
        setParkingPricingConfig({});
        setUserReady(false);
        setIsLoadingData(false);

        await MySwal.fire({
          icon: "error",
          title: "Тохиргооны алдаа",
          text: getErrorMessage(
            error,
            "Firebase хэрэглэгчийн тохиргоог шалгана уу.",
          ),
          confirmButtonColor: "#ef4444",
        });
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, [auth, fetchParkingName, fetchUserRole]);

  /**
   * Хайлтын debounce.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchPlate(searchInput.trim().toUpperCase());
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  /**
   * Системээс гарах.
   */
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      await MySwal.fire({
        icon: "error",
        title: "Алдаа",
        text: getErrorMessage(error, "Системээс гарахад алдаа гарлаа."),
        confirmButtonColor: "#ef4444",
      });
    }
  };

  /**
   * Сонгосон өдрийн нийт статистик татах.
   */
  useEffect(() => {
    if (!userReady || !parkId) {
      return undefined;
    }

    let isMounted = true;

    const loadAggregates = async () => {
      setIsAggregating(true);

      const startTime = new Date(selectedDate);

      startTime.setHours(0, 0, 0, 0);

      const endTime = new Date(selectedDate);

      endTime.setHours(23, 59, 59, 999);

      try {
        const getDashboardStats = httpsCallable(functions, "getDashboardStats");

        const response = await getDashboardStats({
          parkId,
          startTimeISO: startTime.toISOString(),
          endTimeISO: endTime.toISOString(),
        });

        if (!isMounted) return;

        const data = response.data || {};

        setTotalEnteredCount(Number(data.totalEntered) || 0);

        setOccupiedSpots(Number(data.occupied) || 0);

        setTotalLoss(Number(data.loss) || 0);
      } catch (error) {
        console.error("Статистик татахад алдаа:", error);

        if (isMounted) {
          setTotalEnteredCount(0);
          setOccupiedSpots(0);
          setTotalLoss(0);
        }
      } finally {
        if (isMounted) {
          setIsAggregating(false);
        }
      }
    };

    loadAggregates();

    return () => {
      isMounted = false;
    };
  }, [selectedDate, userReady, parkId, functions, refreshKey]);

  /**
   * Эхний хуудасны зогсоолын бүртгэл татах.
   */
  useEffect(() => {
    if (!userReady || !parkId) {
      return undefined;
    }

    let isMounted = true;

    const loadParkingLogs = async () => {
      setIsLoadingData(true);

      const startTime = new Date(selectedDate);

      startTime.setHours(0, 0, 0, 0);

      const endTime = new Date(selectedDate);

      endTime.setHours(23, 59, 59, 999);

      try {
        const searchParkingLogs = httpsCallable(functions, "searchParkingLogs");

        const response = await searchParkingLogs({
          parkId,
          startTimeISO: startTime.toISOString(),
          endTimeISO: endTime.toISOString(),
          paymentFilter,
          paymentTypeFilter,
          searchPlate,
          limitSize: pageSize,
          lastDocId: null,
        });

        if (!isMounted) return;

        const logsFromBackend = Array.isArray(response.data?.logs)
          ? response.data.logs
          : [];

        setPagePaymentBreakdown(summarizePaymentBreakdown(logsFromBackend));
        setPageRecordCount(logsFromBackend.length);

        const backendRecordsCollectionPath =
          getRecordsCollectionPathFromLogs([
            response.data || {},
            ...logsFromBackend,
          ]);

        if (backendRecordsCollectionPath) {
          setRecordsCollectionPath(backendRecordsCollectionPath);
        }

        setParkingLogs(logsFromBackend);

        setHasNextPage(Boolean(response.data?.hasNextPage));

        setCurrentPage(0);

        if (logsFromBackend.length > 0) {
          const lastLog = logsFromBackend[logsFromBackend.length - 1];

          setPageCursors(lastLog?.id ? [lastLog.id] : []);
        } else {
          setPageCursors([]);
        }
      } catch (error) {
        console.error("Жагсаалт татахад алдаа:", error);

        if (isMounted) {
          setParkingLogs([]);
          setPagePaymentBreakdown(createEmptyPaymentBreakdown());
          setPageRecordCount(0);
          setPageCursors([]);
          setHasNextPage(false);
          setCurrentPage(0);
        }
      } finally {
        if (isMounted) {
          setIsLoadingData(false);
          setIsRefreshing(false);
        }
      }
    };

    loadParkingLogs();

    return () => {
      isMounted = false;
    };
  }, [
    selectedDate,
    paymentFilter,
    paymentTypeFilter,
    searchPlate,
    userReady,
    parkId,
    functions,
    refreshKey,
  ]);

  /**
   * Parking record collection-ийн parent document дээр FREE_MINUTES,
   * PAYMENT_TYPE, PRICE_PER_HOUR, FIXED_PRICE зэрэг бодит billing config
   * хадгалагдсан байдаг. Dashboard-ийн parkings/{parkId} config-тэй merge хийнэ.
   */
  useEffect(() => {
    if (!userReady || !recordsCollectionPath) return undefined;

    let isMounted = true;

    const loadRecordsParentPricingConfig = async () => {
      try {
        const normalizedPath = String(recordsCollectionPath || "")
          .trim()
          .replace(/^\/+|\/+$/g, "");
        const pathSegments = normalizedPath.split("/").filter(Boolean);

        if (
          pathSegments.length < 3 ||
          pathSegments[pathSegments.length - 1] !== "records"
        ) {
          return;
        }

        const pricingDocumentPath = pathSegments.slice(0, -1).join("/");

        // Firestore document path нь тэгш тооны segment-тэй байна.
        if (
          !pricingDocumentPath ||
          pricingDocumentPath.split("/").filter(Boolean).length % 2 !== 0
        ) {
          return;
        }

        const pricingSnapshot = await getDoc(doc(db, pricingDocumentPath));

        if (!isMounted || !pricingSnapshot.exists()) return;

        const pricingData = pricingSnapshot.data() || {};

        setParkingPricingConfig((previousConfig) => ({
          ...(previousConfig || {}),
          ...(pricingData || {}),
          ...(pricingData.config || {}),
          ...(pricingData.settings || {}),
        }));
      } catch (error) {
        console.warn(
          "Parking billing config parent document-оос уншиж чадсангүй:",
          error,
        );
      }
    };

    loadRecordsParentPricingConfig();

    return () => {
      isMounted = false;
    };
  }, [db, userReady, recordsCollectionPath]);

  /**
   * Firestore-ийн тухайн өдрийн бүх record-ийг realtime сонсоно.
   * Ингэснээр Бэлэн / QPay / Карт сувгийн тоо, орлого нь бүтэн өдрөөр
   * шууд шинэчлэгдэнэ. Хэрэглэгч эхний хуудсан дээр байвал хүснэгт болон
   * Cloud Function-ий KPI-г мөн дахин татна.
   *
   * Record path:
   *   {collection_url}/{branch_id}/records/{recordId}
   */
  useEffect(() => {
    if (!userReady || !parkId || !recordsCollectionPath) {
      setRealtimeStatus("connecting");
      setPaymentBreakdown(createEmptyPaymentBreakdown());
      setRealtimeDayRecordCount(0);
      setIsPaymentBreakdownLoading(true);
      return undefined;
    }

    setRealtimeStatus("connecting");
    setIsPaymentBreakdownLoading(true);

    const startTime = new Date(selectedDate);
    startTime.setHours(0, 0, 0, 0);

    const endTime = new Date(selectedDate);
    endTime.setHours(23, 59, 59, 999);

    const recordsReference = collection(db, recordsCollectionPath);

    const realtimeQuery = query(
      recordsReference,
      where("entry_time", ">=", startTime),
      where("entry_time", "<=", endTime),
      orderBy("entry_time", "desc"),
    );

    let isFirstSnapshot = true;
    let refreshTimer = null;

    const unsubscribe = onSnapshot(
      realtimeQuery,
      (snapshot) => {
        const dayRecords = snapshot.docs.map((recordSnapshot) => ({
          id: recordSnapshot.id,
          ...recordSnapshot.data(),
        }));

        setPaymentBreakdown(summarizePaymentBreakdown(dayRecords));
        setRealtimeDayRecordCount(dayRecords.length);
        setIsPaymentBreakdownLoading(false);
        setRealtimeStatus("live");

        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          return;
        }

        if (snapshot.docChanges().length === 0) {
          return;
        }

        // Хуучин хуудсыг үзэж байхад хэрэглэгчийг 1-р хуудас руу
        // автоматаар буцаахгүй. Сувгийн задаргаа бол үргэлж realtime байна.
        if (currentPageRef.current !== 0) {
          return;
        }

        if (refreshTimer) {
          clearTimeout(refreshTimer);
        }

        refreshTimer = setTimeout(() => {
          setRefreshKey((previousValue) => previousValue + 1);
        }, 300);
      },
      (error) => {
        console.error("Parking realtime listener алдаа:", error);
        setRealtimeDayRecordCount(0);
        setIsPaymentBreakdownLoading(false);
        setRealtimeStatus("error");
      },
    );

    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }

      unsubscribe();
    };
  }, [db, userReady, parkId, recordsCollectionPath, selectedDate]);

  /**
   * Direct records path буруу/permission-гүй үед хураангуй 0 болохоос
   * хамгаална. Хүснэгттэй ижил searchParkingLogs source-оос сонгосон өдрийн
   * record-уудыг pagination-тай татаж 15 секунд тутам шинэчилнэ.
   */
  useEffect(() => {
    if (!userReady || !parkId) {
      setBackendPaymentBreakdown(createEmptyPaymentBreakdown());
      setBackendDayRecordCount(0);
      return undefined;
    }

    let isMounted = true;
    let isRequestRunning = false;
    let pollTimer = null;

    const loadBackendPaymentBreakdown = async () => {
      if (isRequestRunning) return;

      isRequestRunning = true;

      try {
        const startTime = new Date(selectedDate);
        startTime.setHours(0, 0, 0, 0);

        const endTime = new Date(selectedDate);
        endTime.setHours(23, 59, 59, 999);

        const searchParkingLogs = httpsCallable(functions, "searchParkingLogs");
        const recordMap = new Map();
        let lastDocId = null;
        let previousCursor = "";
        let hasNextPageForBreakdown = true;
        let loadedPageCount = 0;

        while (
          isMounted &&
          hasNextPageForBreakdown &&
          loadedPageCount < PAYMENT_BREAKDOWN_FALLBACK_MAX_PAGES
        ) {
          const response = await searchParkingLogs({
            parkId,
            startTimeISO: startTime.toISOString(),
            endTimeISO: endTime.toISOString(),
            paymentFilter: "all",
            searchPlate: "",
            limitSize: PAYMENT_BREAKDOWN_FALLBACK_PAGE_SIZE,
            lastDocId,
          });

          const pageRecords = Array.isArray(response.data?.logs)
            ? response.data.logs
            : [];

          pageRecords.forEach((record, index) => {
            const recordIdentity = getParkingRecordIdentity(record);
            const fallbackIdentity = `${normalizePlateForMatching(
              record?.car_plate || record?.plate,
            )}-${getTimestampMillis(record?.entry_time)}-${loadedPageCount}-${index}`;

            recordMap.set(recordIdentity || fallbackIdentity, record);
          });

          loadedPageCount += 1;
          hasNextPageForBreakdown = Boolean(response.data?.hasNextPage);

          if (!hasNextPageForBreakdown || pageRecords.length === 0) break;

          const nextCursor = String(
            pageRecords[pageRecords.length - 1]?.id || "",
          ).trim();

          if (!nextCursor || nextCursor === previousCursor) break;

          previousCursor = nextCursor;
          lastDocId = nextCursor;
        }

        if (!isMounted) return;

        const dayRecords = [...recordMap.values()];
        setBackendPaymentBreakdown(summarizePaymentBreakdown(dayRecords));
        setBackendDayRecordCount(dayRecords.length);
        setIsPaymentBreakdownLoading(false);
      } catch (error) {
        console.error("Төлбөрийн хураангуй backend-ээс татахад алдаа:", error);

        if (isMounted) {
          setBackendPaymentBreakdown(createEmptyPaymentBreakdown());
          setBackendDayRecordCount(0);
          setIsPaymentBreakdownLoading(false);
        }
      } finally {
        isRequestRunning = false;
      }
    };

    setBackendPaymentBreakdown(createEmptyPaymentBreakdown());
    setBackendDayRecordCount(0);
    setIsPaymentBreakdownLoading(true);
    loadBackendPaymentBreakdown();

    const today = new Date();
    const isSelectedDateToday =
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();

    if (isSelectedDateToday) {
      pollTimer = setInterval(
        loadBackendPaymentBreakdown,
        PAYMENT_BREAKDOWN_FALLBACK_POLL_MILLISECONDS,
      );
    }

    return () => {
      isMounted = false;

      if (pollTimer) clearInterval(pollTimer);
    };
  }, [functions, userReady, parkId, selectedDate, refreshKey]);

  /**
   * Original parking record нь frontend-ийн Rules-д харагдахгүй үед
   * үүссэн manualPaymentAudits-ийг мөн сувгийн орлогод realtime нэмнэ.
   * Fallback audit нь original record update амжилтгүй үед л үүсдэг тул
   * нэг төлбөр давхар тоологдохгүй.
   */
  useEffect(() => {
    if (!userReady || !parkId) {
      setFallbackPaymentBreakdown(createEmptyPaymentBreakdown());
      setFallbackPaymentRecords([]);
      return undefined;
    }

    const startTime = new Date(selectedDate);
    startTime.setHours(0, 0, 0, 0);

    const endTime = new Date(selectedDate);
    endTime.setHours(23, 59, 59, 999);

    const auditReference = collection(
      db,
      "parkings",
      parkId,
      "manualPaymentAudits",
    );
    const auditQuery = query(
      auditReference,
      where("settledAt", ">=", startTime),
      where("settledAt", "<=", endTime),
      orderBy("settledAt", "desc"),
    );

    const unsubscribe = onSnapshot(
      auditQuery,
      (snapshot) => {
        const auditRecords = snapshot.docs.map((auditSnapshot) => ({
          id: auditSnapshot.id,
          ...auditSnapshot.data(),
        }));

        setFallbackPaymentBreakdown(
          summarizePaymentBreakdown(auditRecords),
        );
        setFallbackPaymentRecords(auditRecords);
      },
      (error) => {
        console.warn("Manual payment audit realtime уншиж чадсангүй:", error);
        setFallbackPaymentBreakdown(createEmptyPaymentBreakdown());
        setFallbackPaymentRecords([]);
      },
    );

    return () => unsubscribe();
  }, [db, userReady, parkId, selectedDate]);

  /**
   * Pagination хуудас татах.
   */
  const fetchPage = async (pageIndex) => {
    if (!parkId || isLoadingData || pageIndex < 0) {
      return;
    }

    setIsLoadingData(true);

    const startTime = new Date(selectedDate);

    startTime.setHours(0, 0, 0, 0);

    const endTime = new Date(selectedDate);

    endTime.setHours(23, 59, 59, 999);

    const lastDocId = pageIndex > 0 ? pageCursors[pageIndex - 1] || null : null;

    try {
      const searchParkingLogs = httpsCallable(functions, "searchParkingLogs");

      const response = await searchParkingLogs({
        parkId,
        startTimeISO: startTime.toISOString(),
        endTimeISO: endTime.toISOString(),
        paymentFilter,
          paymentTypeFilter,
        searchPlate,
        limitSize: pageSize,
        lastDocId,
      });

      const logsFromBackend = Array.isArray(response.data?.logs)
        ? response.data.logs
        : [];

      setPagePaymentBreakdown(summarizePaymentBreakdown(logsFromBackend));
      setPageRecordCount(logsFromBackend.length);

      const backendRecordsCollectionPath =
        getRecordsCollectionPathFromLogs([
          response.data || {},
          ...logsFromBackend,
        ]);

      if (backendRecordsCollectionPath) {
        setRecordsCollectionPath(backendRecordsCollectionPath);
      }

      setParkingLogs(logsFromBackend);

      setHasNextPage(Boolean(response.data?.hasNextPage));

      setCurrentPage(pageIndex);

      if (logsFromBackend.length > 0) {
        const lastLog = logsFromBackend[logsFromBackend.length - 1];

        if (lastLog?.id) {
          setPageCursors((previousCursors) => {
            const updatedCursors = [...previousCursors];

            updatedCursors[pageIndex] = lastLog.id;

            return updatedCursors;
          });
        }
      }
    } catch (error) {
      console.error("Хуудас шилжихэд алдаа:", error);

      await MySwal.fire({
        icon: "error",
        title: "Алдаа",
        text: getErrorMessage(error, "Хуудасны мэдээлэл татаж чадсангүй."),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleNext = () => {
    if (hasNextPage && !isLoadingData) {
      fetchPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0 && !isLoadingData) {
      fetchPage(currentPage - 1);
    }
  };

  /**
   * KPI болон эхний хуудсын мэдээллийг гараар шинэчилнэ.
   * Realtime listener алдаа гарсан үед fallback байдлаар мөн ашиглана.
   */
  const handleRefresh = () => {
    if (
      isLoadingData ||
      isAggregating ||
      isRefreshing ||
      !userReady ||
      !parkId
    ) {
      return;
    }

    setIsRefreshing(true);

    setRefreshKey((previousValue) => previousValue + 1);
  };

  /**
   * Хаалганы командыг Firestore-д бичээд local server-ийн
   * SUCCESS / FAILED хариуг хүлээнэ.
   *
   * Firestore write амжилттай болсон нь physical command
   * амжилттай болсон гэсэн үг биш. Dynamic command дээр requestId,
   * legacy command дээр remoteCommandStatus.updatedAt ашиглан яг
   * энэ удаагийн request-ийн server result-ийг баталгаажуулна.
   */
  const openGate = useCallback(
    async (camera, options = {}) => {
      const showSuccess = options?.showSuccess !== false;

      if (!parkId) {
        await MySwal.fire({
          icon: "warning",
          title: "Зогсоолын тохиргоо олдсонгүй",
          text: "Firebase Auth displayName буюу parkId-ийг шалгана уу.",
          confirmButtonColor: "#f59e0b",
        });

        return false;
      }

      const cameraId = String(camera?.camera_id || "")
        .trim()
        .toLowerCase();

      const role =
        String(camera?.role || "")
          .trim()
          .toLowerCase() === "exit"
          ? "exit"
          : "enter";

      if (!cameraId) {
        await MySwal.fire({
          icon: "warning",
          title: "Камерын ID олдсонгүй",
          confirmButtonColor: "#f59e0b",
        });
        return false;
      }

      const parkingReference = doc(db, "parkings", parkId);

      try {
        let result;

        /*
         * Backward compatibility:
         * Dynamic registry байхгүй хуучин parking:
         *   gate.enterOpen / gate.exitOpen
         *
         * Шинэ parking:
         *   gate.commands.<cameraId>
         */
        if (gateCameras.length === 0 && camera?.legacyField) {
          const commandId =
            String(camera.legacyField).split(".").pop() ||
            (role === "exit" ? "exitOpen" : "enterOpen");

          const beforeSnapshot = await getDoc(parkingReference);

          const beforeData = beforeSnapshot.exists()
            ? beforeSnapshot.data() || {}
            : {};

          const previousUpdatedAtKey = timestampKey(
            beforeData.remoteCommandStatus?.[commandId]?.updatedAt,
          );

          await updateDoc(parkingReference, {
            [camera.legacyField]: true,
          });

          result = await waitForLegacyGateCommand(
            parkingReference,
            commandId,
            previousUpdatedAtKey,
          );
        } else {
          const requestId = createGateCommandId();

          await updateDoc(parkingReference, {
            [`gate.commands.${cameraId}`]: {
              requestId,
              cameraId,
              role,
              action: "OPEN",
              status: "PENDING",
              requestedAt: serverTimestamp(),
              requestedBy:
                auth.currentUser?.email || auth.currentUser?.uid || "dashboard",
            },
          });

          result = await waitForDynamicGateCommand(
            parkingReference,
            cameraId,
            requestId,
          );
        }

        if (showSuccess) {
          await MySwal.fire({
            icon: "success",
            title: camera?.dashboardLabel || camera?.name || cameraId,
            text:
              result?.message ||
              "Сервер хаалт нээх командыг амжилттай гүйцэтгэлээ.",
            timer: 1800,
            showConfirmButton: false,
          });
        }

        return true;
      } catch (error) {
        console.error("Хаалга нээхэд алдаа:", error);

        if (error?.code === "gate/server-timeout") {
          await MySwal.fire({
            icon: "warning",
            title: "Серверээс хариу ирсэнгүй",
            text: "Firestore-д хаалт нээх command хадгалагдсан боловч local parking server 45 секундын дотор гүйцэтгэлийн хариу өгсөнгүй. Сервер, интернет болон Firestore listener-ийг шалгана уу.",
            confirmButtonColor: "#f59e0b",
          });
          return false;
        }

        await MySwal.fire({
          icon: "error",
          title: "Хаалт нээж чадсангүй",
          text: getErrorMessage(
            error,
            "Local server хаалт нээх командыг амжилттай гүйцэтгэсэнгүй.",
          ),
          confirmButtonColor: "#ef4444",
        });

        return false;
      }
    },
    [auth, db, parkId, gateCameras],
  );
  // Direct onSnapshot path, backend search, visible table page гурваас
  // хамгийн олон record агуулж буй source-ийг ашиглана. Ингэснээр Firestore
  // direct path буруу/permission-гүй байсан ч хүснэгтэд ирсэн QPay гүйлгээ 0
  // гэж харагдахгүй.
  const primaryPaymentBreakdown = [
    {
      breakdown: backendPaymentBreakdown,
      recordCount: backendDayRecordCount,
    },
    {
      breakdown: paymentBreakdown,
      recordCount: realtimeDayRecordCount,
    },
    {
      breakdown: pagePaymentBreakdown,
      recordCount: pageRecordCount,
    },
  ].reduce((bestSource, currentSource) =>
    currentSource.recordCount > bestSource.recordCount
      ? currentSource
      : bestSource,
  ).breakdown;

  const displayedPaymentBreakdown = mergePaymentBreakdowns(
    primaryPaymentBreakdown,
    fallbackPaymentBreakdown,
  );

  // Өдрийн нийт орлого = QPay + POS карт + бэлэн төлбөр.
  // Эмнэлгийн бичгээр бүрэн чөлөөлсөн болон бусад үйлчилгээ нь орлого үүсгэхгүй.
  const displayedDailyRevenue =
    displayedPaymentBreakdown.qpay.revenue +
    displayedPaymentBreakdown.card.revenue +
    displayedPaymentBreakdown.cash.revenue;

  const displayedParkingLogs = mergeParkingRecordsWithFallbackAudits(
    parkingLogs,
    fallbackPaymentRecords,
  );

  // Backend paymentFilter нь original record дээр ажилладаг.
  // Харин manualPaymentAudits merge хийсний дараа тухайн record төлөгдсөн/чөлөөлсөн
  // болж өөрчлөгдөж болох тул UI дээр paymentFilter-ийг дахин шалгана.
  const paymentStatusFilteredParkingLogs = filterRecordsByPaymentStatus(
    displayedParkingLogs,
    paymentFilter,
  );

  // Төлөөгүй record дээр төлбөрийн суваг хараахан тогтоогдоогүй байдаг.
  // Тиймээс unpaid үед paymentTypeFilter-ийг зориудаар хэрэглэхгүй.
  const filteredParkingLogs =
    paymentFilter === "unpaid"
      ? paymentStatusFilteredParkingLogs
      : filterRecordsByPaymentType(
          paymentStatusFilteredParkingLogs,
          paymentTypeFilter,
        );

  const displayedGateCameras =
    gateCameras.length > 0
      ? [...gateCameras]
          .filter((camera) => camera.dashboardVisible !== false)
          .sort((a, b) => {
            const orderDiff =
              Number(a.dashboardOrder || 0) - Number(b.dashboardOrder || 0);

            if (orderDiff !== 0) {
              return orderDiff;
            }

            if (a.role !== b.role) {
              return a.role === "enter" ? -1 : 1;
            }

            return String(a.camera_id).localeCompare(String(b.camera_id));
          })
      : LEGACY_GATE_CAMERAS;

  const gateGridClass =
    displayedGateCameras.length <= 1
      ? "lg:tw-grid-cols-1"
      : displayedGateCameras.length === 2
        ? "lg:tw-grid-cols-2"
        : displayedGateCameras.length === 3
          ? "lg:tw-grid-cols-3"
          : "lg:tw-grid-cols-4";

  /**
   * Record дээр хадгалагдсан pending exit camera-г түрүүлж сонгоно.
   * Олон гарах хаалгатай боловч record дээр camera id байхгүй бол
   * admin-аас яг аль хаалгыг нээхийг сонгуулна.
   */
  const selectExitCamera = async (parkingRecord) => {
    const exitCameras = displayedGateCameras.filter(
      (camera) =>
        camera.role === "exit" &&
        camera.enabled !== false &&
        camera.configured !== false,
    );

    if (exitCameras.length === 0) {
      await MySwal.fire({
        icon: "warning",
        title: "Гарах хаалга тохируулаагүй байна",
        text: "Зогсоолын gate camera тохиргоог шалгана уу.",
        confirmButtonColor: "#f59e0b",
      });
      return null;
    }

    const requestedCameraId = String(
      parkingRecord?.pendingExitCameraId || parkingRecord?.exitCameraId || "",
    )
      .trim()
      .toLowerCase();

    const requestedCamera = exitCameras.find(
      (camera) =>
        String(camera.camera_id || "")
          .trim()
          .toLowerCase() === requestedCameraId,
    );

    if (requestedCamera) {
      return requestedCamera;
    }

    if (exitCameras.length === 1) {
      return exitCameras[0];
    }

    const inputOptions = Object.fromEntries(
      exitCameras.map((camera) => [
        camera.camera_id,
        camera.dashboardLabel || camera.name || camera.camera_id,
      ]),
    );

    const result = await MySwal.fire({
      icon: "question",
      title: "Гарах хаалга сонгоно уу",
      input: "select",
      inputOptions,
      inputPlaceholder: "Хаалга сонгох",
      showCancelButton: true,
      confirmButtonText: "Үргэлжлүүлэх",
      cancelButtonText: "Цуцлах",
      confirmButtonColor: "#059669",
      inputValidator: (value) =>
        value ? undefined : "Гарах хаалгаа сонгоно уу.",
    });

    if (!result.isConfirmed) {
      return null;
    }

    return (
      exitCameras.find(
        (camera) => camera.camera_id === String(result.value || ""),
      ) || null
    );
  };

  /**
   * Cloud Function-ийн log.id нь зарим хуучин record дээр Firestore-ийн
   * document ID биш, document доторх UUID байдаг. updateDoc хийхээс өмнө
   * бодит DocumentReference-ийг баталгаажуулж олно.
   */
  const resolveParkingRecordReference = async (parkingRecord) => {
    const activeRecordsCollectionPath =
      recordsCollectionPath || `${parkId}/cars/records`;
    const recordsReference = collection(db, activeRecordsCollectionPath);
    const rememberResolvedReference = (documentReference) => {
      const resolvedCollectionPath = documentReference?.parent?.path || "";

      if (
        resolvedCollectionPath &&
        resolvedCollectionPath !== recordsCollectionPath
      ) {
        setRecordsCollectionPath(resolvedCollectionPath);
      }

      return documentReference;
    };

    /*
     * Backend бүтэн document path өгсөн бол parent collection-ийг дахин
     * таахгүй, тэр reference-ийг яг хэвээр нь ашиглана.
     */
    const fullDocumentPaths = getParkingRecordDocumentPaths(parkingRecord);

    for (const documentPath of fullDocumentPaths) {
      try {
        const exactReference = doc(db, documentPath);
        const exactSnapshot = await getDoc(exactReference);

        if (exactSnapshot.exists()) {
          return rememberResolvedReference(exactReference);
        }
      } catch (pathError) {
        console.debug(
          `Firestore documentPath шалгаж чадсангүй: ${documentPath}`,
          pathError,
        );
      }
    }

    const referencePathDocumentIds = fullDocumentPaths
      .map((value) => value.split("/").filter(Boolean).slice(-1)[0])
      .filter(Boolean);

    const directDocumentIds = [
      parkingRecord?.docId,
      parkingRecord?.documentId,
      parkingRecord?.firestoreId,
      parkingRecord?.firestoreDocId,
      parkingRecord?._docId,
      parkingRecord?._id,
      parkingRecord?.id,
      ...referencePathDocumentIds,
    ]
      .map((value) => String(value || "").trim())
      .filter(
        (value, index, values) =>
          Boolean(value) &&
          !value.includes("/") &&
          values.indexOf(value) === index,
      );

    for (const documentId of directDocumentIds) {
      const candidateReference = doc(recordsReference, documentId);
      const candidateSnapshot = await getDoc(candidateReference);

      if (candidateSnapshot.exists()) {
        return rememberResolvedReference(candidateReference);
      }
    }

    const candidateDocuments = new Map();

    const addCandidateDocuments = (querySnapshot) => {
      querySnapshot.docs.forEach((documentSnapshot) => {
        candidateDocuments.set(documentSnapshot.ref.path, documentSnapshot);
      });
    };

    const identityValues = [
      parkingRecord?.id,
      parkingRecord?.recordId,
      parkingRecord?.record_id,
      parkingRecord?.uuid,
    ]
      .map((value) => String(value || "").trim())
      .filter(
        (value, index, values) =>
          Boolean(value) && values.indexOf(value) === index,
      );

    for (const fieldName of ["id", "recordId", "record_id", "uuid"]) {
      for (const identityValue of identityValues) {
        const identitySnapshot = await getDocs(
          query(
            recordsReference,
            where(fieldName, "==", identityValue),
            limit(2),
          ),
        );

        if (identitySnapshot.size === 1) {
          return rememberResolvedReference(identitySnapshot.docs[0].ref);
        }

        addCandidateDocuments(identitySnapshot);
      }
    }

    const transactionValues = [
      parkingRecord?.referenceNumber,
      parkingRecord?.reference_number,
      parkingRecord?.invoiceId,
      parkingRecord?.invoice_id,
      parkingRecord?.invoice_number,
      parkingRecord?.transactionId,
      parkingRecord?.transaction_id,
    ]
      .map((value) => String(value || "").trim())
      .filter(
        (value, index, values) =>
          Boolean(value) && values.indexOf(value) === index,
      );

    const transactionFieldNames = [
      "referenceNumber",
      "reference_number",
      "invoiceId",
      "invoice_id",
      "invoice_number",
      "transactionId",
      "transaction_id",
    ];

    for (const fieldName of transactionFieldNames) {
      for (const transactionValue of transactionValues) {
        const transactionSnapshot = await getDocs(
          query(
            recordsReference,
            where(fieldName, "==", transactionValue),
            limit(2),
          ),
        );

        if (transactionSnapshot.size === 1) {
          return rememberResolvedReference(transactionSnapshot.docs[0].ref);
        }

        addCandidateDocuments(transactionSnapshot);
      }
    }

    const rawVehiclePlateValues = getRecordPlateValues(parkingRecord)
      .map((value) => String(value || "").trim().toUpperCase())
      .filter(Boolean);

    const vehiclePlate = normalizePlateForMatching(rawVehiclePlateValues[0]);

    const plateLookupValues = [vehiclePlate, ...rawVehiclePlateValues].filter(
      (value, index, values) =>
        Boolean(value) && values.indexOf(value) === index,
    );

    if (plateLookupValues.length > 0) {
      for (const fieldName of [
        "car_plate",
        "plate",
        "license_plate",
        "licensePlate",
        "plate_number",
        "plateNumber",
      ]) {
        for (const plateLookupValue of plateLookupValues) {
          const plateSnapshot = await getDocs(
            query(
              recordsReference,
              where(fieldName, "==", plateLookupValue),
              limit(25),
            ),
          );

          addCandidateDocuments(plateSnapshot);
        }
      }
    }

    const targetEntryTime = getTimestampMillis(parkingRecord?.entry_time);
    let collectionGroupLookupError = null;

    /*
     * Plate талбарын формат өөр байсан ч entry_time орчмын record-уудыг
     * авч, улсын дугаарыг client талд Latin/Cyrillic normalization-тай шалгана.
     */
    if (targetEntryTime > 0) {
      const entryWindowMilliseconds = 60 * 1000;
      const entryWindowSnapshot = await getDocs(
        query(
          recordsReference,
          where(
            "entry_time",
            ">=",
            new Date(targetEntryTime - entryWindowMilliseconds),
          ),
          where(
            "entry_time",
            "<=",
            new Date(targetEntryTime + entryWindowMilliseconds),
          ),
          limit(100),
        ),
      );

      entryWindowSnapshot.docs.forEach((documentSnapshot) => {
        const data = documentSnapshot.data() || {};
        const recordTransactionValues = [
          data.referenceNumber,
          data.reference_number,
          data.invoiceId,
          data.invoice_id,
          data.invoice_number,
          data.transactionId,
          data.transaction_id,
        ].map((value) => String(value || "").trim());

        const transactionMatches = recordTransactionValues.some((value) =>
          transactionValues.includes(value),
        );

        if (
          recordMatchesNormalizedPlate(data, vehiclePlate) ||
          transactionMatches
        ) {
          candidateDocuments.set(documentSnapshot.ref.path, documentSnapshot);
        }
      });
    }

    /*
     * Parent path өөр үед хамгийн найдвартай холбоос нь document ID эсвэл
     * орсон цаг + normalized улсын дугаар байдаг. Энэ query нь `cars` гэсэн
     * hard-coded branch-аас үл хамааран бүх `records` collection-оос зөв
     * document-ийг олно.
     */
    if (candidateDocuments.size === 0 && targetEntryTime > 0) {
      const groupEntryWindowMilliseconds = 2 * 60 * 1000;

      try {
        const entryTimeGroupSnapshot = await getDocs(
          query(
            collectionGroup(db, "records"),
            where(
              "entry_time",
              ">=",
              new Date(targetEntryTime - groupEntryWindowMilliseconds),
            ),
            where(
              "entry_time",
              "<=",
              new Date(targetEntryTime + groupEntryWindowMilliseconds),
            ),
            limit(250),
          ),
        );

        entryTimeGroupSnapshot.docs.forEach((documentSnapshot) => {
          const data = documentSnapshot.data() || {};
          const candidateIdentityValues = [
            documentSnapshot.id,
            data.id,
            data.recordId,
            data.record_id,
            data.uuid,
          ].map((value) => String(value || "").trim());
          const identityMatches = candidateIdentityValues.some(
            (value) =>
              Boolean(value) &&
              [...directDocumentIds, ...identityValues].includes(value),
          );
          const candidateTransactionValues = [
            data.referenceNumber,
            data.reference_number,
            data.invoiceId,
            data.invoice_id,
            data.invoice_number,
            data.transactionId,
            data.transaction_id,
          ].map((value) => String(value || "").trim());
          const transactionMatches = candidateTransactionValues.some(
            (value) => Boolean(value) && transactionValues.includes(value),
          );

          if (
            identityMatches ||
            transactionMatches ||
            recordMatchesNormalizedPlate(data, vehiclePlate)
          ) {
            candidateDocuments.set(
              documentSnapshot.ref.path,
              documentSnapshot,
            );
          }
        });
      } catch (groupError) {
        collectionGroupLookupError = groupError;
        console.debug(
          "Firestore records entry_time collection-group fallback ажилласангүй:",
          groupError,
        );
      }
    }

    /*
     * searchParkingLogs өөр parent path ашиглаж байвал configured/default
     * source-оос document олдохгүй. Ийм үед бүх `records` collection
     * group-оос car_plate-аар хайж, зөв parking/time-тайг авна.
     */
    if (candidateDocuments.size === 0 && plateLookupValues.length > 0) {
      for (const plateLookupValue of plateLookupValues) {
        try {
          const collectionGroupSnapshot = await getDocs(
            query(
              collectionGroup(db, "records"),
              where("car_plate", "==", plateLookupValue),
              limit(50),
            ),
          );

          collectionGroupSnapshot.docs.forEach((documentSnapshot) => {
            const data = documentSnapshot.data() || {};
            const pathSegments = documentSnapshot.ref.path
              .split("/")
              .filter(Boolean);
            const recordParkId = String(
              data.parkId ||
                data.parkingId ||
                data.collectionUrl ||
                data.collection_url ||
                "",
            ).trim();
            const candidateEntryTime = getTimestampMillis(data.entry_time);
            const parkingMatches =
              pathSegments.includes(parkId) || recordParkId === parkId;
            const entryTimeMatches =
              targetEntryTime > 0 &&
              candidateEntryTime > 0 &&
              Math.abs(candidateEntryTime - targetEntryTime) <= 60 * 1000;

            if (parkingMatches || entryTimeMatches) {
              candidateDocuments.set(
                documentSnapshot.ref.path,
                documentSnapshot,
              );
            }
          });
        } catch (groupError) {
          collectionGroupLookupError = groupError;
          console.debug(
            "Firestore records collection-group fallback ажилласангүй:",
            groupError,
          );
          break;
        }

        if (candidateDocuments.size > 0) break;
      }
    }

    /*
     * Хуучин record дээр entry_time төрөл өөр байвал хамгийн сүүлийн
     * record-уудаас normalized plate-аар нэг удаагийн fallback хайлт хийнэ.
     */
    if (candidateDocuments.size === 0 && vehiclePlate) {
      const recentSnapshot = await getDocs(
        query(recordsReference, orderBy("entry_time", "desc"), limit(250)),
      );

      recentSnapshot.docs.forEach((documentSnapshot) => {
        if (
          recordMatchesNormalizedPlate(
            documentSnapshot.data() || {},
            vehiclePlate,
          )
        ) {
          candidateDocuments.set(documentSnapshot.ref.path, documentSnapshot);
        }
      });
    }

    const matchingDocuments = [...candidateDocuments.values()];

    if (transactionValues.length > 0) {
      const transactionMatches = matchingDocuments.filter(
        (documentSnapshot) => {
          const data = documentSnapshot.data() || {};

          return [
            data.referenceNumber,
            data.reference_number,
            data.invoiceId,
            data.invoice_id,
            data.invoice_number,
            data.transactionId,
            data.transaction_id,
          ]
            .map((value) => String(value || "").trim())
            .some((value) => transactionValues.includes(value));
        },
      );

      if (transactionMatches.length === 1) {
        return rememberResolvedReference(transactionMatches[0].ref);
      }
    }

    if (targetEntryTime > 0) {
      const entryTimeMatches = matchingDocuments.filter((documentSnapshot) => {
        const candidateEntryTime = getTimestampMillis(
          documentSnapshot.data()?.entry_time,
        );

        return (
          candidateEntryTime > 0 &&
          Math.abs(candidateEntryTime - targetEntryTime) <= 60 * 1000
        );
      });

      if (entryTimeMatches.length === 1) {
        return rememberResolvedReference(entryTimeMatches[0].ref);
      }
    }

    if (!parkingRecord?.exit_time) {
      const openMatches = matchingDocuments.filter(
        (documentSnapshot) => !documentSnapshot.data()?.exit_time,
      );

      if (openMatches.length === 1) {
        return rememberResolvedReference(openMatches[0].ref);
      }
    }

    if (matchingDocuments.length === 1) {
      return rememberResolvedReference(matchingDocuments[0].ref);
    }

    const error = new Error(
      matchingDocuments.length > 1
        ? `${vehiclePlate || "Энэ машин"}-ийн ${matchingDocuments.length} ижил Firestore record олдлоо. Entry time эсвэл document ID-г шалгана уу.`
        : collectionGroupLookupError
          ? `${vehiclePlate || "Энэ машин"}-ийн record өөр Firestore path-д байна. Collection-group query-д эрх/index байхгүй байна. Шалгасан source: ${activeRecordsCollectionPath}`
          : `${vehiclePlate || "Энэ машин"}-ийн Firestore record олдсонгүй. Шалгасан source: ${activeRecordsCollectionPath}. parkings/${parkId} тохиргоонд collection_url болон branch_id-г шалгана уу.`,
    );
    error.code =
      matchingDocuments.length > 1
        ? "parking/record-ambiguous"
        : "parking/record-not-found";
    throw error;
  };

  /**
   * searchParkingLogs-ийн source document frontend-д харагдахгүй үед
   * төлбөрийн баримтыг admin-ийн өөрийн namespace-д алдагдуулахгүй хадгална.
   * Original record амжилттай update болсон үед энэ fallback үүсэхгүй.
   */
  const saveFallbackManualAudit = async ({
    parkingRecord,
    settlementMethod,
    settlementLabel,
    amount,
    transactionId,
    requestId,
    posOutcome,
    gateOpened,
    status,
    operator,
    settlementMetadata = {},
    ebarimtFields = {},
  }) => {
    const rawAuditId =
      String(transactionId || requestId || createManualPosRequestId()).trim();
    const safeAuditId = rawAuditId.replace(/[^0-9A-Za-z_-]/g, "-");
    const auditReference = doc(
      db,
      "parkings",
      parkId,
      "manualPaymentAudits",
      safeAuditId,
    );
    const isExemption = EXEMPTION_METHODS.has(settlementMethod);
    const isPaid = status === "SUCCESS" && !isExemption;

    await setDoc(
      auditReference,
      {
        auditFallback: true,
        sourceRecordId: getParkingRecordIdentity(parkingRecord),
        sourceCollectionPath: recordsCollectionPath || "",
        car_plate: String(
          parkingRecord?.car_plate || parkingRecord?.plate || "-",
        ),
        entryTimeMillis: getTimestampMillis(parkingRecord?.entry_time),

        payment: isPaid || isExemption,
        paymentStatus: isExemption
          ? "EXEMPTED"
          : isPaid
            ? "PAID"
            : status,
        paymentType: getPaymentTypeForSettlement(settlementMethod),
        paymentMethod: settlementMethod,
        paymentSource:
          settlementMethod === "CARD"
            ? "POS_WEB_SERIAL_FALLBACK"
            : "ADMIN_MANUAL_FALLBACK",
        paidAmount: isPaid ? amount : 0,
        originalPrice:
          settlementMetadata?.doctorNoteOriginalAmount || amount,
        waivedAmount: settlementMetadata?.doctorNoteApplied
          ? settlementMetadata?.doctorNoteDiscountAmount || 0
          : 0,
        settlementType: isExemption
          ? "EXEMPTION"
          : settlementMetadata?.doctorNoteApplied
            ? "PARTIAL_DISCOUNT_PAYMENT"
            : "PAYMENT",
        settlementReason: settlementMetadata?.doctorNoteApplied
          ? "DOCTOR_NOTE"
          : settlementMethod,
        settlementLabel,
        settlementStatus: status,
        settledBy: operator,
        settledAt: serverTimestamp(),
        ...settlementMetadata,
        ...ebarimtFields,

        posTransactionId: String(transactionId || ""),
        posRequestId: String(requestId || ""),
        posStatus: String(posOutcome?.status || ""),
        posCode: posOutcome?.code ?? null,
        posDescription: String(
          posOutcome?.description || posOutcome?.reason || "",
        ),
        posTerminal: String(posOutcome?.terminal || ""),
        posRrn: String(posOutcome?.rrn || ""),

        gateOpened: Boolean(gateOpened),
        gateStatus: gateOpened ? "SUCCESS" : "FAILED",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    return auditReference;
  };

  const selectSettlementMethod = (parkingRecord) => {
    const price = getCleanPrice(parkingRecord?.price);
    const vehiclePlate = String(
      parkingRecord?.car_plate || parkingRecord?.plate || "-",
    );

    return new Promise((resolve) => {
      let isResolved = false;

      MySwal.fire({
        icon: "question",
        title: `${vehiclePlate} · ${price.toLocaleString()}₮`,
        html: `
          <div style="margin: 4px 0 14px; color: #64748b; font-size: 14px;">
            Гаралтын төлөв сонгоно уу. “Картаар төлөх” дарвал үнийн дүн POS руу очиж, төлбөр амжилттай болсны дараа хаалт нээгдэнэ. Эмнэлгийн бичгийг хүснэгтийн тусдаа товчноос сонгоно.
          </div>
          <div style="display: grid; gap: 10px;">
            <button
              type="button"
              data-settlement-method="CASH"
              style="width: 100%; padding: 13px 16px; border: 0; border-radius: 12px; background: #059669; color: white; font-weight: 600; cursor: pointer;"
            >
              Бэлнээр төлсөн
            </button>
            <button
              type="button"
              data-settlement-method="CARD"
              style="width: 100%; padding: 13px 16px; border: 0; border-radius: 12px; background: #4f46e5; color: white; font-weight: 600; cursor: pointer;"
            >
              Картаар төлөх
            </button>
            <button
              type="button"
              data-settlement-method="OTHER_SERVICE"
              style="width: 100%; padding: 13px 16px; border: 0; border-radius: 12px; background: #d97706; color: white; font-weight: 600; cursor: pointer;"
            >
              Бусад үйлчилгээ
            </button>
          </div>
        `,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: "Цуцлах",
        cancelButtonColor: "#94a3b8",
        allowOutsideClick: false,
        didOpen: (popup) => {
          popup
            .querySelectorAll("[data-settlement-method]")
            .forEach((button) => {
              button.addEventListener("click", async () => {
                if (isResolved) return;

                const method = String(
                  button.getAttribute("data-settlement-method") || "",
                );

                if (!PAYMENT_METHOD_OPTIONS[method]) return;

                if (method === "CARD") {
                  popup
                    .querySelectorAll("[data-settlement-method]")
                    .forEach((methodButton) => {
                      methodButton.disabled = true;
                      methodButton.style.opacity = "0.65";
                    });

                  try {
                    await connectPosTerminal({ requestPermission: true });
                  } catch (error) {
                    isResolved = true;
                    resolve(null);

                    await MySwal.fire({
                      icon: "error",
                      title: "A35 POS холбогдоогүй байна",
                      text: getErrorMessage(
                        error,
                        "Chrome/Edge дээр A35-ын COM портыг сонгоно уу.",
                      ),
                      confirmButtonColor: "#ef4444",
                    });
                    return;
                  }
                }

                isResolved = true;

                resolve({
                  method,
                  isExemption: EXEMPTION_METHODS.has(method),
                  label: PAYMENT_METHOD_OPTIONS[method],
                });

                Swal.close();
              });
            });
        },
        willClose: () => {
          if (!isResolved) {
            isResolved = true;
            resolve(null);
          }
        },
      });
    });
  };

  const selectDoctorNoteSettlementMethod = (parkingRecord) => {
    const doctorNoteAmount = getDoctorNoteParkingAmount(
      parkingRecord,
      parkingPricingConfig,
    );
    const vehiclePlate = String(
      parkingRecord?.car_plate || parkingRecord?.plate || "-",
    );

    const baseSettlement = {
      doctorNoteApplied: true,
      doctorNoteFreeMinutes: DOCTOR_NOTE_FREE_MINUTES,
      doctorNoteTotalMinutes: doctorNoteAmount.totalMinutes,
      doctorNotePayableMinutes: doctorNoteAmount.payableMinutes,
      originalAmount: doctorNoteAmount.originalAmount,
      amount: doctorNoteAmount.amountDue,
      discountAmount: doctorNoteAmount.discountAmount,
    };

    if (doctorNoteAmount.amountDue <= 0) {
      return MySwal.fire({
        icon: "info",
        title: `${vehiclePlate} · Эмнэлгийн бичгээр`,
        html: `
          <div style="text-align: left; color: #334155; font-size: 14px; line-height: 1.7;">
            <div><b>Нийт хугацаа:</b> ${doctorNoteAmount.totalMinutes.toLocaleString()} минут</div>
            <div><b>Эмнэлгийн үнэгүй:</b> ${DOCTOR_NOTE_FREE_MINUTES} минут</div>
            <div><b>Төлөх дүн:</b> 0₮</div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Хаалт нээх",
        cancelButtonText: "Цуцлах",
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#94a3b8",
      }).then((result) =>
        result.isConfirmed
          ? {
              ...baseSettlement,
              method: "DOCTOR_NOTE",
              doctorNotePaymentMethod: "NONE",
              isExemption: true,
              label: PAYMENT_METHOD_OPTIONS.DOCTOR_NOTE,
            }
          : null,
      );
    }

    return new Promise((resolve) => {
      let isResolved = false;

      MySwal.fire({
        icon: "question",
        title: `${vehiclePlate} · ${doctorNoteAmount.amountDue.toLocaleString()}₮`,
        html: `
          <div style="margin: 4px 0 14px; color: #475569; font-size: 14px; line-height: 1.65; text-align: left;">
            <div><b>Нийт хугацаа:</b> ${doctorNoteAmount.totalMinutes.toLocaleString()} минут</div>
            <div><b>Эмнэлгийн үнэгүй:</b> ${DOCTOR_NOTE_FREE_MINUTES} минут</div>
            <div><b>Тооцох хугацаа:</b> ${doctorNoteAmount.payableMinutes.toLocaleString()} минут</div>
            ${
              doctorNoteAmount.paymentMode === "HOURLY"
                ? `<div><b>Тооцох цаг:</b> ${doctorNoteAmount.payableBillingUnits.toLocaleString()} цаг × ${doctorNoteAmount.unitAmount.toLocaleString()}₮</div>`
                : ""
            }
            <div><b>Хөнгөлсөн дүн:</b> ${doctorNoteAmount.discountAmount.toLocaleString()}₮</div>
            <div><b>Төлөх дүн:</b> ${doctorNoteAmount.amountDue.toLocaleString()}₮</div>
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px;">
            <button
              type="button"
              data-doctor-payment-method="CASH"
              style="width: 100%; padding: 13px 14px; border: 0; border-radius: 12px; background: #059669; color: white; font-weight: 700; cursor: pointer;"
            >
              Бэлнээр төлөх
            </button>
            <button
              type="button"
              data-doctor-payment-method="CARD"
              style="width: 100%; padding: 13px 14px; border: 0; border-radius: 12px; background: #4f46e5; color: white; font-weight: 700; cursor: pointer;"
            >
              POS / картаар
            </button>
          </div>
        `,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: "Цуцлах",
        cancelButtonColor: "#94a3b8",
        allowOutsideClick: false,
        didOpen: (popup) => {
          popup
            .querySelectorAll("[data-doctor-payment-method]")
            .forEach((button) => {
              button.addEventListener("click", async () => {
                if (isResolved) return;

                const paymentMethod = String(
                  button.getAttribute("data-doctor-payment-method") || "",
                );

                if (!["CARD", "CASH"].includes(paymentMethod)) return;

                if (paymentMethod === "CARD") {
                  popup
                    .querySelectorAll("[data-doctor-payment-method]")
                    .forEach((methodButton) => {
                      methodButton.disabled = true;
                      methodButton.style.opacity = "0.65";
                    });

                  try {
                    await connectPosTerminal({ requestPermission: true });
                  } catch (error) {
                    isResolved = true;
                    resolve(null);

                    await MySwal.fire({
                      icon: "error",
                      title: "A35 POS холбогдоогүй байна",
                      text: getErrorMessage(
                        error,
                        "Chrome/Edge дээр A35-ын COM портыг сонгоно уу.",
                      ),
                      confirmButtonColor: "#ef4444",
                    });
                    return;
                  }
                }

                isResolved = true;

                resolve({
                  ...baseSettlement,
                  method: paymentMethod,
                  doctorNotePaymentMethod: paymentMethod,
                  isExemption: false,
                  label:
                    paymentMethod === "CARD"
                      ? "Эмнэлгийн бичиг + POS"
                      : "Эмнэлгийн бичиг + бэлэн",
                });

                Swal.close();
              });
            });
        },
        willClose: () => {
          if (!isResolved) {
            isResolved = true;
            resolve(null);
          }
        },
      });
    });
  };

  const requestEbarimtPrint = async ({
    parkingRecord,
    settlement,
    amount,
    paymentType,
    transactionId = "",
    ebarimtBillOptions = {},
    previewConfirmed = false,
  }) => {
    const cleanAmount = getCleanPrice(amount);

    // EBarimt-ийг зөвхөн төлбөр баталгаажсан record дээр үүсгэнэ.
    if (!isParkingRecordSettled(parkingRecord)) {
      return { status: "SKIPPED_UNPAID" };
    }

    if (!parkId || cleanAmount <= 0) {
      return { status: "SKIPPED" };
    }

    const [sourceDocumentPath = ""] =
      getParkingRecordDocumentPaths(parkingRecord);
    const paymentMethodForPrint = settlement?.method || paymentType;
    const ebarimtPrinterConfig = getMinuXprinterEbarimtConfig(
      parkingPricingConfig,
      parkingName,
      parkId,
    );
    const ebarimtPrintPayload = buildMinuXprinterEbarimtPayload({
      parkId,
      parkingName,
      parkingConfig: parkingPricingConfig,
      parkingRecord,
      amount: cleanAmount,
      paymentType,
      paymentMethod: paymentMethodForPrint,
      transactionId,
      sourceDocumentPath,
      billType: ebarimtBillOptions.billType,
      customerNo: ebarimtBillOptions.customerNo,
      customerName: ebarimtBillOptions.customerName,
    });

    try {
      const secureRecordId = getParkingRecordIdentity(parkingRecord);
      if (!secureRecordId) throw new Error("И-Баримтад recordId шаардлагатай.");
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      if (!token) throw new Error("Ажилтны эрхийг шалгаж чадсангүй.");
      const response = await fetch(
        `https://us-central1-${firebaseProjectId}.cloudfunctions.net/requestPaidParkingEbarimt`,
        { method: "POST", headers: {
            "Content-Type": "application/json", Authorization: `Bearer ${token}`,
          }, body: JSON.stringify({
            parkId, recordId: secureRecordId,
            billType: ebarimtBillOptions.billType || "1",
            customerNo: ebarimtBillOptions.customerNo || "",
          }) },
      );
      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error(result.message || "И-Баримт үүсгэх серверийн алдаа.");
      const receiptData = result.receipt;

      /*
       * НЭГ Л хэвлэх товч:
       * EBarimt үүссэний дараа дахин SweetAlert "Хэвлэх" preview гаргахгүй.
       * Шууд receipt цонх нээгдэж, тэнд ганц "Баримт хэвлэх" товч байна.
       */
      const printConfirmationId = createManualPosRequestId();
      const printWindow = openDirectEbarimtPrintWindow({
        ebarimtPrintPayload,
        receiptData,
        printConfirmationId,
      });

      const printStatus = await waitForEbarimtPrintConfirmation({
        printWindow,
        confirmationId: printConfirmationId,
      });

      return {
        status: printStatus,
        receiptData,
        previewConfirmed,
        previewAmountShown: previewConfirmed ? cleanAmount : 0,
      };
    } catch (error) {
      const suffix = isCorsLikeFetchError(error)
        ? " Minu API browser CORS зөвшөөрөөгүй бол сервисгүй шууд хувилбар ажиллахгүй."
        : "";

      throw new Error(
        `${getErrorMessage(error, "EBarimt үүсгэх эсвэл хэвлэх үед алдаа гарлаа.")}${suffix}`,
      );
    }
  };

  const persistEbarimtResultForExistingRecord = async ({
    parkingRecord,
    result,
    amount,
    paymentType,
  }) => {
    const recordId = getParkingRecordIdentity(parkingRecord);
    const savedAt = serverTimestamp();
    const auditFields = getEbarimtReceiptAuditFields({
      result,
      amount,
      paymentType,
      timestamp: savedAt,
    });

    try {
      const recordReference =
        await resolveParkingRecordReference(parkingRecord);

      await updateDoc(recordReference, {
        ...auditFields,
        updatedAt: serverTimestamp(),
      });

      setRefreshKey((previousValue) => previousValue + 1);
      return true;
    } catch (originalUpdateError) {
      console.info(
        "EBarimt төлөв original record дээр хадгалагдсангүй; fallback audit шинэчилнэ:",
        originalUpdateError?.code ||
          originalUpdateError?.message ||
          originalUpdateError,
      );
    }

    try {
      const auditsReference = collection(
        db,
        "parkings",
        parkId,
        "manualPaymentAudits",
      );

      let updatedFallback = false;

      if (recordId) {
        const auditSnapshot = await getDocs(
          query(
            auditsReference,
            where("sourceRecordId", "==", recordId),
            limit(10),
          ),
        );

        for (const auditDocument of auditSnapshot.docs) {
          if (!isParkingRecordSettled(auditDocument.data())) continue;

          await updateDoc(auditDocument.ref, {
            ...auditFields,
            updatedAt: serverTimestamp(),
          });
          updatedFallback = true;
        }
      }

      if (!updatedFallback) {
        const fallbackKey = String(
          getEbarimtReference(parkingRecord) ||
            recordId ||
            createManualPosRequestId(),
        )
          .trim()
          .replace(/[^0-9A-Za-z_-]/g, "-");

        const fallbackReference = doc(
          db,
          "parkings",
          parkId,
          "manualPaymentAudits",
          `ebarimt-${fallbackKey}`,
        );

        await setDoc(
          fallbackReference,
          {
            auditFallback: true,
            sourceRecordId: recordId,
            sourceCollectionPath: recordsCollectionPath || "",
            car_plate: String(
              parkingRecord?.car_plate || parkingRecord?.plate || "-",
            ),
            entryTimeMillis: getTimestampMillis(parkingRecord?.entry_time),
            payment: true,
            paymentStatus: "PAID",
            paymentType,
            paymentMethod: getEbarimtPaymentMethod(parkingRecord),
            paidAmount: getCleanPrice(
              parkingRecord?.paidAmount ??
                parkingRecord?.price ??
                amount,
            ),
            price: getCleanPrice(parkingRecord?.price ?? amount),
            settledAt:
              parkingRecord?.settledAt ||
              parkingRecord?.payment_time ||
              serverTimestamp(),
            ...auditFields,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }

      setRefreshKey((previousValue) => previousValue + 1);
      return true;
    } catch (fallbackError) {
      console.error("EBarimt fallback төлөв хадгалахад алдаа:", fallbackError);
      return false;
    }
  };

  /**
   * Төлбөр баталгаажсаны дараа, хаалт нээхээс ӨМНӨ EBarimt өгөх эсэхийг асууна.
   *
   * - CASH: "Бэлнээр төлсөн" сонголт нь кассчин мөнгө хүлээж авсны баталгаа.
   * - CARD: зөвхөн POS SUCCESS ирсний дараа энэ асуулт гарна.
   * - "EBarimt өгөх" сонговол хувь хүн / байгууллага сонгуулаад баримт үүсгэнэ.
   * - "Баримтгүй үргэлжлүүлэх" сонговол баримтгүйгээр хаалт нээнэ.
   * - EBarimt API/хэвлэлт алдаа гарсан ч төлбөрийг дахин авахгүй; анхааруулсны
   *   дараа хаалт нээх урсгал үргэлжилнэ.
   */
  const offerEbarimtBeforeGate = async ({
    parkingRecord,
    amount,
    paymentType,
    paymentMethod,
    settlement,
    transactionReference = "",
    originalPrice = 0,
    settlementReason = "",
    settlementMetadata = {},
  }) => {
    const cleanAmount = getCleanPrice(amount);

    if (
      cleanAmount <= 0 ||
      ["doc", "otherReason"].includes(paymentType)
    ) {
      return {
        requested: false,
        completed: false,
        printConfirmed: false,
        status: "NOT_REQUIRED",
        receiptData: null,
      };
    }

    const vehiclePlate = String(
      parkingRecord?.car_plate || parkingRecord?.plate || "-",
    );
    const paymentLabel =
      PAYMENT_TYPE_LABELS[paymentType] ||
      String(paymentMethod || "Төлбөр төлсөн");

    const receiptChoice = await MySwal.fire({
      icon: "question",
      title: paymentLabel,
      html: `
        <div style="text-align:left;color:#334155;font-size:14px;line-height:1.7;">
          <div><b>Машин:</b> ${escapeHtml(vehiclePlate)}</div>
          <div><b>Төлсөн дүн:</b> ${cleanAmount.toLocaleString()}₮</div>
          <div style="margin-top:10px;color:#475569;">
            Хаалт нээхээс өмнө EBarimt өгөх үү?
          </div>
        </div>
      `,
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: "EBarimt өгөх",
      denyButtonText: "Баримтгүй үргэлжлүүлэх",
      confirmButtonColor: "#4f46e5",
      denyButtonColor: "#64748b",
      reverseButtons: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (!receiptChoice.isConfirmed) {
      return {
        requested: false,
        completed: false,
        printConfirmed: false,
        status: "SKIPPED_BY_OPERATOR",
        receiptData: null,
      };
    }

    const receiptDate = new Date();
    const receiptRecord = {
      ...parkingRecord,
      payment: true,
      paymentStatus: "PAID",
      paymentType,
      paymentMethod,
      paidAmount: cleanAmount,
      amountDue: 0,
      originalPrice:
        getCleanPrice(originalPrice) ||
        getCleanPrice(parkingRecord?.originalPrice) ||
        cleanAmount,
      price: cleanAmount,
      settlementReason,
      settlementLabel: settlement?.label || paymentLabel,
      settledAt: receiptDate,
      payment_time: receiptDate,
      paymentConfirmedAt: receiptDate,
      ebarimtRequired: true,
      ebarimtPrintRequested: false,
      ebarimtPreparedAt: receiptDate,
      ebarimtStatus: "PENDING_PRINT",
      ebarimtAmount: cleanAmount,
      ebarimtPaymentType: paymentType,
      ...settlementMetadata,
    };

    const receiptReference = firstNonEmptyString(
      transactionReference,
      getEbarimtReference(parkingRecord),
      getParkingRecordIdentity(parkingRecord),
      `${vehiclePlate}-${receiptDate.getTime()}`,
    );

    const previewResult = await showEbarimtPrintPreview({
      parkingRecord: receiptRecord,
      amount: cleanAmount,
      paymentType,
      paymentMethod,
      transactionReference: receiptReference,
    });

    if (!previewResult.isConfirmed) {
      await MySwal.fire({
        icon: "info",
        title: "EBarimt цуцлагдлаа",
        text: "Төлбөр баталгаажсан тул баримтгүйгээр хаалт нээх урсгал үргэлжилнэ.",
        timer: 1600,
        showConfirmButton: false,
      });

      return {
        requested: true,
        completed: false,
        printConfirmed: false,
        status: "PREVIEW_CANCELLED",
        receiptData: null,
      };
    }

    const ebarimtBillOptions = previewResult.value || {
      billType: "1",
      customerNo: "",
      customerName: "",
    };

    try {
      const result = await requestEbarimtPrint({
        parkingRecord: receiptRecord,
        settlement: {
          ...settlement,
          method: paymentMethod,
          label: `${paymentLabel} · EBarimt`,
        },
        amount: cleanAmount,
        paymentType,
        transactionId: receiptReference,
        ebarimtBillOptions,
        previewConfirmed: true,
      });

      const printConfirmed = result?.status === "PRINT_CONFIRMED";

      return {
        requested: true,
        completed: printConfirmed,
        printConfirmed,
        status: result?.status || "UNKNOWN",
        receiptData: result?.receiptData || null,
      };
    } catch (error) {
      await MySwal.fire({
        icon: "warning",
        title: "EBarimt үүсгэж чадсангүй",
        html: `
          <div style="text-align:left;color:#334155;font-size:14px;line-height:1.65;">
            <div>${escapeHtml(
              getErrorMessage(
                error,
                "EBarimt үүсгэх эсвэл хэвлэх үед алдаа гарлаа.",
              ),
            )}</div>
            <div style="margin-top:10px;color:#b45309;font-weight:700;">
              Төлбөрийг дахин авахгүй. Хаалт нээх урсгал үргэлжилнэ.
            </div>
          </div>
        `,
        confirmButtonText: "Үргэлжлүүлэх",
        confirmButtonColor: "#f59e0b",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      return {
        requested: true,
        completed: false,
        printConfirmed: false,
        status: "ERROR",
        receiptData: null,
        error,
      };
    }
  };

  /**
   * QR төлбөрийг одоо байгаа автомат payment flow боловсруулна.
   * Энэ функц зөвхөн admin-ийн manual гаралтад зориулагдсан:
   * - CARD: POS SUCCESS -> EBarimt сонголт -> хаалт нээх -> audit хадгалах.
   * - CASH: мөнгө хүлээж авсан -> EBarimt сонголт -> хаалт нээх -> audit хадгалах.
   * - Чөлөөлөлт: төлбөргүй тул EBarimt асуухгүй, хаалтыг шууд нээнэ.
   */
  const settleAndOpenVehicle = async (parkingRecord, presetSettlement = null) => {
    const recordId = getParkingRecordIdentity(parkingRecord);

    if (!recordId || !parkId || openingRecordId) {
      if (!recordId) {
        await MySwal.fire({
          icon: "error",
          title: "Record ID олдсонгүй",
          text: "Энэ машины бүртгэлийг шинэчлээд дахин оролдоно уу.",
          confirmButtonColor: "#ef4444",
        });
      }
      return;
    }

    setOpeningRecordId(recordId);

    try {
      const vehiclePlate = String(
        parkingRecord.car_plate || parkingRecord.plate || "-",
      );

      // Хаалт нээх бүрд төлбөр/чөлөөлөлтийн төрлийг заавал сонгуулна.
      const settlement =
        presetSettlement || (await selectSettlementMethod(parkingRecord));
      if (!settlement) return;

      const price = getSettlementAmount(parkingRecord, settlement);
      if (!Number.isFinite(price) || price < 0) {
        await MySwal.fire({icon:"error",title:"Төлбөрийн дүн буруу байна",text:"Дүнг шалгах хүртэл POS болон хаалтны үйлдэл хийхгүй."});
        return;
      }
      const originalPrice = getSettlementOriginalAmount(
        parkingRecord,
        settlement,
        price,
      );
      const settlementMetadata = getSettlementMetadata(settlement);
      const settlementReason = settlement.doctorNoteApplied
        ? "DOCTOR_NOTE"
        : settlement.method;

      const exitCamera = await selectExitCamera(parkingRecord);
      if (!exitCamera) return;
      const operator =
        auth.currentUser?.email || auth.currentUser?.uid || "dashboard";

      /*
       * Original record харагдахгүй/permission-гүй байсан ч төлбөр болон
       * хаалтыг зогсоохгүй. Audit шатанд л resolve хийж, бүтэлгүйтвэл
       * parkings/{parkId}/manualPaymentAudits дотор fallback хадгална.
       */
      const tryUpdateOriginalRecord = async (updateData) => {
        try {
          const recordReference =
            await resolveParkingRecordReference(parkingRecord);
          await updateDoc(recordReference, updateData);
          return true;
        } catch (auditError) {
          console.info(
            "Original parking record update хийгдсэнгүй; fallback audit ашиглана:",
            auditError?.code || auditError?.message || auditError,
          );
          return false;
        }
      };

      if (settlement.method === "CARD") {
        if (price <= 0) {
          await MySwal.fire({
            icon: "warning",
            title: "POS дүн буруу байна",
            text: "Картаар төлүүлэх дүн 0-ээс их байх ёстой.",
            confirmButtonColor: "#f59e0b",
          });
          return;
        }

        const posAttemptStorageKey = getPosAttemptStorageKey(
          parkId,
          parkingRecord,
        );
        const previousAttempt = readStoredPosAttempt(posAttemptStorageKey);
        const previousStatus = String(
          previousAttempt?.status || "",
        ).toUpperCase();

        if (["PROCESSING", "UNKNOWN"].includes(previousStatus)) {
          const cancelPendingResult = await MySwal.fire({
            icon: "warning",
            title: "Өмнөх POS гүйлгээ дуусаагүй байна",
            text: `Transaction: ${previousAttempt?.transactionId || "-"}. Давхар суутгал үүсэхээс хамгаалж шинэ төлбөр илгээсэнгүй. POS дээр гүйлгээний төлөвийг шалгана уу.`,
            footer:
              "“Хүлээлтийг цуцлах” нь банкны төлбөрийг буцаахгүй, зөвхөн dashboard-ийн хамгаалалтын түгжээг цэвэрлэнэ.",
            showCancelButton: true,
            confirmButtonText: "Хүлээлтийг цуцлах",
            cancelButtonText: "Буцах",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#64748b",
          });

          if (!cancelPendingResult.isConfirmed) return;

          const finalConfirmation = await MySwal.fire({
            icon: "question",
            title: "POS дээр төлбөр ороогүйг шалгасан уу?",
            text: `Transaction: ${previousAttempt?.transactionId || "-"}. Төлбөр амжилттай орсон байхад цуцалбал дараагийн оролдлогоор давхар суутгал үүсэж болзошгүй.`,
            showCancelButton: true,
            confirmButtonText: "Тийм, хүлээлтийг цуцлах",
            cancelButtonText: "Үгүй, буцах",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#64748b",
          });

          if (!finalConfirmation.isConfirmed) return;

          clearStoredPosAttempt(posAttemptStorageKey);

          await MySwal.fire({
            icon: "success",
            title: "Хүлээлт цуцлагдлаа",
            text: "Картаар төлөх товчийг дахин дарж шинэ гүйлгээ эхлүүлнэ үү.",
            timer: 1800,
            showConfirmButton: false,
          });
          return;
        }

        if (previousStatus === "SUCCESS") {
          // LocalStorage is not authoritative proof of a persisted payment.
          const freshRef = await resolveParkingRecordReference(parkingRecord);
          const fresh = await getDoc(freshRef);
          const persisted = fresh.exists() && fresh.data()?.payment === true &&
            fresh.data()?.posTransactionId === previousAttempt?.transactionId;
          if (!persisted) {
            await MySwal.fire({
              icon: "warning",
              title: "Өмнөх POS төлбөрийг тулгах шаардлагатай",
              text: "Төлбөр POS дээр амжилттай болсон ч үндсэн бүртгэлд баталгаажаагүй байна. Давтан суутгахгүй; ажилтан банкны RRN-ээр тулгана.",
              confirmButtonColor: "#f59e0b",
            });
            return;
          }
          // Manually reopening needs the normal gate action, never a new debit.
          const reopenConfirmation = await MySwal.fire({
            icon: "question",
            title: "Өмнөх төлбөр баталгаажсан",
            text: `${vehiclePlate} · Дахин төлбөр авахгүйгээр хаалт нээх үү?`,
            showCancelButton: true,
            confirmButtonText: "Хаалт нээх",
            cancelButtonText: "Буцах",
            confirmButtonColor: "#059669",
          });
          if (reopenConfirmation.isConfirmed) await openGate(exitCamera, { showSuccess: true });
          return;
        }

        const requestId = createManualPosRequestId();
        const transactionId = createPosTransactionId();

        // Ямар ч Firestore lookup/update хийхээс ӨМНӨ POS ажиллагаа эхэлнэ.
        writeStoredPosAttempt(posAttemptStorageKey, {
          status: "PROCESSING",
          transactionId,
          requestId,
          amount: price,
          vehiclePlate,
        });

        void MySwal.fire({
          icon: "info",
          title: "Картаа A35 POS дээр уншуулна уу",
          text: `${vehiclePlate} · ${price.toLocaleString()}₮ · Transaction: ${transactionId}`,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        let posOutcome;

        try {
          posOutcome = await runPosPayment(price, transactionId);
        } catch (posError) {
          posOutcome = {
            status: "UNKNOWN",
            reason: "POS_CONNECTION_OUTCOME_UNKNOWN",
            description:
              "POS холболт тасарсан тул гүйлгээний эцсийн төлөв тодорхойгүй байна.",
            transactionId,
            amount: price,
            error: posError,
          };
        } finally {
          Swal.close();
        }

        if (posOutcome.status === "SUCCESS") {
          writeStoredPosAttempt(posAttemptStorageKey, {
            status: "SUCCESS",
            transactionId,
            requestId,
            amount: price,
            vehiclePlate,
            code: posOutcome.code,
            rrn: posOutcome.rrn || "",
          });

          /*
           * Зөв дараалал:
           * 1. POS SUCCESS — төлбөр баталгаажсан
           * 2. EBarimt өгөх эсэхийг асуух
           * 3. Хаалт нээх command
           * 4. Original record эсвэл fallback audit хадгалах
           */
          const deferredCardEbarimt = async () => offerEbarimtBeforeGate({
            parkingRecord,
            amount: price,
            paymentType: "card",
            paymentMethod: "CARD",
            settlement,
            transactionReference: transactionId,
            originalPrice,
            settlementReason,
            settlementMetadata,
          });

          // No physical gate command until POS proof is persisted.
          const cardEbarimtResult = {requested:false,completed:false,status:"PENDING",receiptData:null};
          const paidAt = serverTimestamp();
          const cardEbarimtFields = getEbarimtReceiptAuditFields({
            result: cardEbarimtResult,
            amount: price,
            paymentType: "card",
            timestamp: paidAt,
          });
          const finalCardUpdate = {
            payment: true,
            payment_time: paidAt,
            paymentConfirmedAt: paidAt,
            paymentStatus: "PAID",
            paymentType: "card",
            paymentMethod: "CARD",
            paymentSource: "POS_WEB_SERIAL",
            paidAmount: price,
            originalPrice,
            price,
            waivedAmount: settlement.doctorNoteApplied
              ? Math.max(0, originalPrice - price)
              : deleteField(),
            amountDue: 0,
            settlementType: settlement.doctorNoteApplied
              ? "PARTIAL_DISCOUNT_PAYMENT"
              : "PAYMENT",
            settlementReason,
            settlementLabel: settlement.label,
            settledAt: paidAt,
            settledBy: operator,
            ...settlementMetadata,
            ...cardEbarimtFields,

            posStatus: "SUCCESS",
            posCode: posOutcome.code,
            posDescription: posOutcome.description || "",
            posTerminal: posOutcome.terminal || "",
            posRrn: posOutcome.rrn || "",
            posPaymentType: posOutcome.paymentType || "",
            posMaskedPan: posOutcome.maskedPan || "",
            posAppCode: posOutcome.appCode || "",
            posEntryMode: posOutcome.entryMode || "",
            posCardType: posOutcome.cardType || "",
            posPaidAt: paidAt,
            providerPaymentStatus: "POS_SUCCESS",

            manualPosStatus: "SUCCESS",
            manualPosCode: posOutcome.code,
            manualPosDescription: posOutcome.description || "",
            manualPosPaidAt: paidAt,
            manualSettlementMethod: "CARD_WEB_SERIAL",
            manualSettlementPaymentMethod: "CARD",
            manualSettlementChannel: "WEB_SERIAL",
            manualSettlementLabel: settlement.label,
            manualSettlementSelectedAt: paidAt,
            manualSettlementSelectedBy: operator,
            manualSettlementStatus: "POS_PAID_AWAITING_GATE",
            manualGateOpenStatus: "NOT_REQUESTED",
            manualGateOpenRequestedAt: paidAt,
            manualGateOpenRequestedBy: operator,
            manualGateOpenCameraId: exitCamera.camera_id,
            manualGateOpenedAt: deleteField(),
            manualGateOpenFailedAt: deleteField(),
            manualPosRequestId: requestId,
            manualPosTransactionId: transactionId,
            manualPosAmount: price,
            manualPosCameraId: exitCamera.camera_id,
            manualPosRequestedAt: paidAt,
            manualPosRequestedBy: operator,

            paymentProvider: "POS_WEB_SERIAL",
            posRequestId: requestId,
            posTransactionId: transactionId,
            posAmount: price,
            posCameraId: exitCamera.camera_id,
            posRequestedAt: paidAt,

            /*
             * payment=true-г хараад хуучин QR/payment listener хаалтыг давхар
             * нээхээс өмнө ownership-ийг энэ update дотроо атомикаар цэвэрлэнэ.
             */
            pendingExitCameraId: deleteField(),
            listenerActive: false,
            listenerToken: deleteField(),
            listenerLeaseUntil: deleteField(),
            exitAttemptLeaseUntil: deleteField(),
            exitAttemptStatus: "MANUAL_POS_PAID_AWAITING_GATE",
              exitAttemptCompletedAt: deleteField(),
          };

          const paymentAuditSaved = await tryUpdateOriginalRecord(finalCardUpdate);
          if (!paymentAuditSaved) {
            // Save fallback evidence, but do NOT pulse the gate without a paid
            // primary record. This avoids unreconciled unpaid visits and
            // duplicate collection after a card SUCCESS.
            try {
              await saveFallbackManualAudit({
                parkingRecord,
                settlementMethod: "CARD", settlementLabel: settlement.label,
                amount: price, transactionId, requestId, posOutcome,
                gateOpened: false, status: "POS_PAID_PENDING_RECONCILIATION",
                operator, settlementMetadata, ebarimtFields: cardEbarimtFields,
              });
            } catch (error) {
              console.error("POS амжилттай боловч audit хадгалагдсангүй:", error);
            }
            await MySwal.fire({
              icon: "warning",
              title: "POS төлбөр амжилттай, бүртгэл хадгалагдсангүй",
              text: "Давтан төлбөр эхлүүлэхгүй. Хаалт автоматаар нээгээгүй; банкны гүйлгээг ажилтнаар тулгаж бүртгэлийг сэргээнэ үү.",
              confirmButtonColor: "#f59e0b",
            });
            return;
          }
          // Payment is durably saved; it is now safe to request an EBarimt.
          const issuedCardEbarimt = await deferredCardEbarimt();
          await tryUpdateOriginalRecord(getEbarimtReceiptAuditFields({
            result: issuedCardEbarimt, amount: price, paymentType: "card", timestamp: serverTimestamp(),
          }));
          const gateOpened = await openGate(exitCamera, { showSuccess: false });
          const gateCompletedAt = serverTimestamp();
          const gateAuditSaved = await tryUpdateOriginalRecord({
            manualSettlementStatus: gateOpened ? "COMPLETED" : "POS_PAID_GATE_FAILED",
            manualGateOpenStatus: gateOpened ? "SUCCESS" : "FAILED",
            manualGateOpenedAt: gateOpened ? gateCompletedAt : deleteField(),
            manualGateOpenFailedAt: gateOpened ? deleteField() : gateCompletedAt,
            exitAttemptStatus: gateOpened ? "MANUAL_POS_OPENED" : "MANUAL_POS_GATE_FAILED",
            exitAttemptCompletedAt: gateCompletedAt,
          });
          setRefreshKey((v) => v + 1);
          await MySwal.fire({
            icon: gateOpened && gateAuditSaved ? "success" : "warning",
            title: gateOpened ? "Картын төлбөр баталгаажсан" : "Төлбөр амжилттай, хаалт нээгдсэнгүй",
            text: gateOpened
              ? (gateAuditSaved ? "Хаалт нээгдсэн." : "Хаалт нээгдсэн, төлбөрийн бүртгэл хадгалагдсан. Хаалтын audit-ыг ажилтнаар шалгуулна уу.")
              : "Дахин карт уншуулах шаардлагагүй. Хаалтыг ажилтнаар шалгуулна уу.",
            confirmButtonColor: "#059669",
          });
          return;
        }

        const isUnknown = posOutcome.status === "UNKNOWN";
        const isCancelled = posOutcome.status === "CANCELLED";
        const failedAt = serverTimestamp();
        const failedUpdate = {
          payment: false,
          posStatus: posOutcome.status,
          posCode: posOutcome.code ?? null,
          posDescription: posOutcome.description || posOutcome.reason || "",
          posCompletedAt: isUnknown ? deleteField() : failedAt,
          providerPaymentStatus: `POS_${posOutcome.status}`,

          manualPosStatus: posOutcome.status,
          manualPosCode: posOutcome.code ?? null,
          manualPosDescription:
            posOutcome.description || posOutcome.reason || "",
          manualPosCompletedAt: isUnknown ? deleteField() : failedAt,
          manualSettlementStatus: isUnknown
            ? "POS_UNKNOWN"
            : isCancelled
              ? "POS_CANCELLED"
              : "POS_FAILED",
          manualGateOpenStatus: isUnknown ? "WAITING_POS" : "NOT_OPENED",
          manualPosRequestId: requestId,
          manualPosTransactionId: transactionId,
          manualPosAmount: price,
          manualSettlementMethod: "CARD_WEB_SERIAL",
          manualSettlementPaymentMethod: "CARD",
          manualSettlementChannel: "WEB_SERIAL",
        };

        if (isUnknown) {
          writeStoredPosAttempt(posAttemptStorageKey, {
            status: "UNKNOWN",
            transactionId,
            requestId,
            amount: price,
            vehiclePlate,
          });
        } else {
          clearStoredPosAttempt(posAttemptStorageKey);
        }

        const failedAuditSaved = await tryUpdateOriginalRecord(failedUpdate);

        if (!failedAuditSaved) {
          try {
            await saveFallbackManualAudit({
              parkingRecord,
              settlementMethod: "CARD",
              settlementLabel: settlement.label,
              amount: price,
            transactionId,
            requestId,
            posOutcome,
            gateOpened: false,
            status: posOutcome.status,
            operator,
            settlementMetadata,
          });
          } catch (fallbackError) {
            console.warn("POS failed audit хадгалагдсангүй:", fallbackError);
          }
        }

        setRefreshKey((previousValue) => previousValue + 1);

        if (isUnknown) {
          await MySwal.fire({
            icon: "warning",
            title: "POS хариу тодорхойгүй байна",
            text:
              posOutcome.description ||
              "Гүйлгээ амжилттай болсон эсэхийг POS дээр шалгана уу.",
            footer:
              `Transaction ${transactionId} — давхар суутгал үүсэхээс хамгаалж дахин илгээхгүй.`,
            confirmButtonColor: "#f59e0b",
          });
          return;
        }

        await MySwal.fire({
          icon: isCancelled ? "info" : "error",
          title:
            isCancelled
              ? "Картын гүйлгээ цуцлагдсан"
              : "Картын төлбөр амжилтгүй",
          text:
            posOutcome.description ||
            posOutcome.reason ||
            "A35 POS гүйлгээг амжилтгүй гэж буцаалаа.",
          confirmButtonColor: isCancelled ? "#64748b" : "#ef4444",
        });
        return;
      }

      // CASH дээр мөнгө хүлээж авсан гэж баталгаажсаны дараа
      // хаалт нээхээс ӨМНӨ EBarimt өгөх эсэхийг заавал асууна.
      // Эмнэлгийн бичиг / бусад үйлчилгээ нь төлбөргүй тул EBarimt асуухгүй.
      let deferredCashReceipt = null;
      let cashEbarimtResult = {
        requested: false,
        completed: false,
        printConfirmed: false,
        status: "NOT_REQUIRED",
        receiptData: null,
      };

      if (settlement.method === "CASH" && price > 0) {
        deferredCashReceipt = () => offerEbarimtBeforeGate({
          parkingRecord,
          amount: price,
          paymentType: "cash",
          paymentMethod: "CASH",
          settlement,
          transactionReference: firstNonEmptyString(
            getEbarimtReference(parkingRecord),
            recordId,
          ),
          originalPrice,
          settlementReason,
          settlementMetadata,
        });
      }

      // Manual cash/exemption: no physical gate pulse before durable settlement.
      let gateOpened = false;

      const completedAt = serverTimestamp();
      const cashEbarimtFields =
        settlement.method === "CASH"
          ? getEbarimtReceiptAuditFields({
              result: cashEbarimtResult,
              amount: price,
              paymentType: "cash",
              timestamp: completedAt,
            })
          : {};

      const completedUpdate = {
        /*
         * Manual command аль хэдийн physical barrier-ийг нээсэн.
         * Pending payment flow-ийн талбаруудыг payment=true-тэй
         * нэг atomic update-аар цэвэрлэж, давхар pulse-ээс хамгаална.
         */
        pendingExitCameraId: deleteField(),
        listenerActive: false,
        listenerToken: deleteField(),
        listenerLeaseUntil: deleteField(),
        exitAttemptLeaseUntil: deleteField(),
        exitAttemptStatus: "MANUAL_SETTLEMENT_AWAITING_GATE",
        exitAttemptCompletedAt: completedAt,

        payment: true,
        payment_time: completedAt,
        settlementReason,
        settlementLabel: settlement.label,
        settledAt: completedAt,
        settledBy: operator,
        ...settlementMetadata,
        ...cashEbarimtFields,

        manualSettlementMethod: settlement.method,
        manualSettlementLabel: settlement.label,
        manualSettlementSelectedAt: completedAt,
        manualSettlementSelectedBy: operator,
        manualSettlementStatus: "SETTLED_AWAITING_GATE",
        manualGateOpenRequestedAt: completedAt,
        manualGateOpenRequestedBy: operator,
        manualGateOpenCameraId: exitCamera.camera_id,
        manualGateOpenStatus: "NOT_REQUESTED",
        manualGateOpenedAt: deleteField(),
        manualGateOpenFailedAt: deleteField(),
      };

      if (settlement.method === "CASH") {
        Object.assign(completedUpdate, {
          paymentStatus: "PAID",
          paymentType: "cash",
          paymentMethod: settlement.method,
          paidAmount: price,
          originalPrice,
          price,
          waivedAmount: settlement.doctorNoteApplied
            ? Math.max(0, originalPrice - price)
            : deleteField(),
          settlementType: settlement.doctorNoteApplied
            ? "PARTIAL_DISCOUNT_PAYMENT"
            : "PAYMENT",
        });
      } else {
        Object.assign(completedUpdate, {
          originalPrice,
          price: 0,
          paymentStatus: "EXEMPTED",
          paymentType:
            settlement.method === "DOCTOR_NOTE" ? "doc" : "otherReason",
          paymentMethod: settlement.method,
          paidAmount: 0,
          waivedAmount: originalPrice,
          settlementType: "EXEMPTION",
        });
      }

      const finalAuditSaved = await tryUpdateOriginalRecord(completedUpdate);
      if (!finalAuditSaved) {
        try {
          await saveFallbackManualAudit({
            parkingRecord, settlementMethod: settlement.method,
            settlementLabel: settlement.label, amount: price,
            transactionId: "", requestId: createManualPosRequestId(),
            posOutcome: null, gateOpened: false,
            status: "SETTLED_PENDING_RECONCILIATION", operator,
            settlementMetadata, ebarimtFields: cashEbarimtFields,
          });
        } catch (error) { console.error("Manual audit backup failed", error); }
        await MySwal.fire({
          icon: "warning", title: "Төлбөр/чөлөөлөлтийн бүртгэл хадгалагдсангүй",
          text: "Дахин төлбөр нэхэхгүй. Хаалт нээгээгүй; бүртгэлийг ажилтнаар тулгуулна уу.",
        });
        return;
      }
      if (deferredCashReceipt) {
        cashEbarimtResult = await deferredCashReceipt();
        await tryUpdateOriginalRecord(getEbarimtReceiptAuditFields({
          result: cashEbarimtResult, amount: price, paymentType: "cash", timestamp: serverTimestamp(),
        }));
      }
      gateOpened = await openGate(exitCamera, { showSuccess: false });
      const gateAt = serverTimestamp();
      await tryUpdateOriginalRecord({
        exitAttemptStatus: gateOpened ? "MANUAL_OPENED" : "MANUAL_GATE_FAILED",
        exitAttemptCompletedAt: gateAt,
        manualSettlementStatus: gateOpened ? "COMPLETED" : "GATE_FAILED",
        manualGateOpenStatus: gateOpened ? "SUCCESS" : "FAILED",
        manualGateOpenedAt: gateOpened ? gateAt : deleteField(),
        manualGateOpenFailedAt: gateOpened ? deleteField() : gateAt,
      });

      setRefreshKey((previousValue) => previousValue + 1);

      await MySwal.fire({
        icon: gateOpened && finalAuditSaved ? "success" : "warning",
        title: gateOpened ? "Хаалт нээгдсэн" : "Хаалт нээгдсэнгүй",
        text: gateOpened
          ? finalAuditSaved
            ? `${vehiclePlate} · ${settlement.label}`
            : "Хаалт нээгдсэн боловч сонголтын audit хадгалагдсангүй. Төлбөрийг дахин бүү ав."
          : `${vehiclePlate} · ${settlement.label} сонголт бүртгэгдсэн. Хаалтыг дахин нээж оролдоно уу.`,
        timer: gateOpened && finalAuditSaved ? 1800 : undefined,
        showConfirmButton: !(gateOpened && finalAuditSaved),
        confirmButtonColor: "#f59e0b",
      });
    } catch (error) {
      console.error("Төлөв хадгалж хаалт нээхэд алдаа:", error);

      await MySwal.fire({
        icon: "error",
        title: "Үйлдэл амжилтгүй",
        text: getErrorMessage(
          error,
          "Төлөв хадгалах эсвэл хаалт нээх үед алдаа гарлаа.",
        ),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setOpeningRecordId("");
    }
  };

  const settleDoctorNoteAndOpenVehicle = async (parkingRecord) => {
    const settlement = await selectDoctorNoteSettlementMethod(parkingRecord);

    if (!settlement) return;

    await settleAndOpenVehicle(parkingRecord, settlement);
  };

  const showEbarimtPrintPreview = async ({
    parkingRecord,
    amount,
    paymentType,
    paymentMethod,
  }) => {
    const cleanAmount = getCleanPrice(amount);
    const vehiclePlate = String(
      parkingRecord?.car_plate || parkingRecord?.plate || "-",
    );
    const paymentTypeLabel = PAYMENT_TYPE_LABELS[paymentType] || paymentMethod;
    const normalizeCustomerNoInput = (value) =>
      String(value || "")
        .trim()
        .toUpperCase()
        .replace(/\s/g, "");

    return MySwal.fire({
      title: "EBarimt",
      html: `
        <style>
          .ip-ebarimt {
            width: 100%;
            text-align: left;
            color: #0f172a;
          }
          .ip-ebarimt-summary {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 12px;
            align-items: center;
            padding: 14px;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            background: #f8fafc;
          }
          .ip-ebarimt-amount {
            font-size: clamp(24px, 8vw, 32px);
            line-height: 1;
            font-weight: 800;
            letter-spacing: -.03em;
          }
          .ip-ebarimt-meta {
            min-width: 0;
            text-align: right;
            font-size: 12px;
            color: #64748b;
          }
          .ip-ebarimt-plate {
            font-size: 13px;
            font-weight: 800;
            color: #0f172a;
          }
          .ip-ebarimt-types {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
            margin-top: 10px;
          }
          .ip-ebarimt-type {
            display: flex;
            align-items: center;
            gap: 8px;
            min-height: 46px;
            border: 1px solid #cbd5e1;
            border-radius: 12px;
            padding: 10px 11px;
            background: #ffffff;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
          }
          .ip-ebarimt-org {
            display: none;
            margin-top: 10px;
            padding: 10px;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            background: #ffffff;
          }
          .ip-ebarimt-org-row {
            display: flex;
            gap: 8px;
          }
          .ip-ebarimt-org input {
            min-width: 0;
            flex: 1;
            height: 42px;
            border: 1px solid #cbd5e1;
            border-radius: 10px;
            padding: 0 11px;
            font-size: 14px;
            font-weight: 700;
            outline: none;
          }
          .ip-ebarimt-org button {
            min-height: 42px;
            border: 1px solid #0f172a;
            border-radius: 10px;
            background: #0f172a;
            color: #ffffff;
            padding: 0 12px;
            font-size: 12px;
            font-weight: 700;
            white-space: nowrap;
            cursor: pointer;
          }
          .ip-ebarimt-status {
            display: none;
            margin-top: 8px;
            padding: 8px 9px;
            border-radius: 9px;
            font-size: 12px;
            line-height: 1.35;
          }
          @media (max-width: 480px) {
            .ip-ebarimt-summary {
              grid-template-columns: 1fr;
              gap: 6px;
            }
            .ip-ebarimt-meta {
              text-align: left;
            }
            .ip-ebarimt-types {
              grid-template-columns: 1fr;
            }
            .ip-ebarimt-org-row {
              flex-direction: column;
            }
            .ip-ebarimt-org button {
              width: 100%;
            }
          }
        </style>

        <div class="ip-ebarimt">
          <div class="ip-ebarimt-summary">
            <div class="ip-ebarimt-amount">${cleanAmount.toLocaleString()}₮</div>
            <div class="ip-ebarimt-meta">
              <div class="ip-ebarimt-plate">${escapeHtml(vehiclePlate)}</div>
              <div>${escapeHtml(paymentTypeLabel)}</div>
            </div>
          </div>

          <div class="ip-ebarimt-types">
            <label class="ip-ebarimt-type" data-ebarimt-bill-card="1">
              <input type="radio" name="ebarimt-bill-type" value="1" checked />
              Хувь хүн
            </label>
            <label class="ip-ebarimt-type" data-ebarimt-bill-card="3">
              <input type="radio" name="ebarimt-bill-type" value="3" />
              Байгууллага
            </label>
          </div>

          <div class="ip-ebarimt-org" data-ebarimt-org-box>
            <div class="ip-ebarimt-org-row">
              <input
                type="text"
                data-ebarimt-reg-no
                placeholder="Байгууллагын РД"
                inputmode="numeric"
              />
              <button type="button" data-ebarimt-check-org>Шалгах</button>
            </div>
            <div class="ip-ebarimt-status" data-ebarimt-org-status></div>
          </div>
        </div>
      `,
      width: "min(94vw, 420px)",
      padding: "1rem",
      showCancelButton: true,
      confirmButtonText: "Үүсгэх",
      cancelButtonText: "Цуцлах",
      confirmButtonColor: "#0f172a",
      cancelButtonColor: "#ffffff",
      reverseButtons: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: (popup) => {
        popup.style.maxWidth = "420px";
        popup.style.borderRadius = "18px";

        const actions = popup.querySelector(".swal2-actions");
        const cancelButton = popup.querySelector(".swal2-cancel");
        const confirmButton = popup.querySelector(".swal2-confirm");

        if (actions) {
          actions.style.width = "100%";
          actions.style.margin = "14px 0 0";
          actions.style.gap = "8px";
          actions.style.flexWrap = "wrap";
        }

        if (confirmButton) {
          confirmButton.style.flex = "1 1 150px";
          confirmButton.style.minHeight = "44px";
          confirmButton.style.margin = "0";
          confirmButton.style.borderRadius = "11px";
        }

        if (cancelButton) {
          cancelButton.style.flex = "1 1 110px";
          cancelButton.style.minHeight = "44px";
          cancelButton.style.margin = "0";
          cancelButton.style.border = "1px solid #cbd5e1";
          cancelButton.style.borderRadius = "11px";
          cancelButton.style.color = "#475569";
        }

        const organizationBox = popup.querySelector("[data-ebarimt-org-box]");
        const organizationStatus = popup.querySelector(
          "[data-ebarimt-org-status]",
        );
        const organizationCheckButton = popup.querySelector(
          "[data-ebarimt-check-org]",
        );
        const organizationRegInput = popup.querySelector(
          "[data-ebarimt-reg-no]",
        );
        const billTypeCards = Array.from(
          popup.querySelectorAll("[data-ebarimt-bill-card]"),
        );

        const setOrganizationStatus = ({
          message = "",
          color = "#475569",
          borderColor = "#e2e8f0",
          background = "#f8fafc",
        }) => {
          if (!organizationStatus) return;

          if (!message) {
            organizationStatus.textContent = "";
            organizationStatus.style.display = "none";
            return;
          }

          organizationStatus.textContent = message;
          organizationStatus.style.display = "block";
          organizationStatus.style.color = color;
          organizationStatus.style.border = `1px solid ${borderColor}`;
          organizationStatus.style.background = background;
        };

        const resetOrganizationCheck = () => {
          popup.dataset.ebarimtCustomerNo = "";
          popup.dataset.ebarimtCustomerName = "";
          popup.dataset.ebarimtCheckedInputNo = "";
          setOrganizationStatus({ message: "" });
        };

        const checkOrganization = async () => {
          const customerNo = normalizeCustomerNoInput(
            organizationRegInput?.value,
          );

          if (organizationRegInput) {
            organizationRegInput.value = customerNo;
          }

          if (!customerNo) {
            setOrganizationStatus({
              message: "РД оруулна уу.",
              color: "#b91c1c",
              borderColor: "#fecaca",
              background: "#fef2f2",
            });
            return null;
          }

          if (organizationCheckButton) {
            organizationCheckButton.disabled = true;
            organizationCheckButton.textContent = "Шалгаж байна";
            organizationCheckButton.style.opacity = "0.65";
          }

          try {
            const merchantInfo = await fetchEbarimtMerchantInfo(
              firebaseProjectId,
              customerNo,
            );

            popup.dataset.ebarimtCustomerNo = merchantInfo.regNo || customerNo;
            popup.dataset.ebarimtCustomerName = merchantInfo.name;
            popup.dataset.ebarimtCheckedInputNo = customerNo;

            setOrganizationStatus({
              message: merchantInfo.name,
              color: "#047857",
              borderColor: "#bbf7d0",
              background: "#ecfdf5",
            });

            return merchantInfo;
          } catch (error) {
            popup.dataset.ebarimtCustomerNo = "";
            popup.dataset.ebarimtCustomerName = "";
            popup.dataset.ebarimtCheckedInputNo = "";

            setOrganizationStatus({
              message: getErrorMessage(error, "Мэдээлэл олдсонгүй."),
              color: "#b45309",
              borderColor: "#fde68a",
              background: "#fffbeb",
            });

            return null;
          } finally {
            if (organizationCheckButton) {
              organizationCheckButton.disabled = false;
              organizationCheckButton.textContent = "Шалгах";
              organizationCheckButton.style.opacity = "1";
            }
          }
        };

        const updateOrganizationBox = () => {
          const billType = popup.querySelector(
            'input[name="ebarimt-bill-type"]:checked',
          )?.value;
          const isOrganization = billType === "3";

          if (organizationBox) {
            organizationBox.style.display = isOrganization ? "block" : "none";
          }

          billTypeCards.forEach((card) => {
            const cardInput = card.querySelector(
              'input[name="ebarimt-bill-type"]',
            );
            const isSelected = cardInput?.checked === true;

            card.style.borderColor = isSelected ? "#0f172a" : "#cbd5e1";
            card.style.background = isSelected ? "#f8fafc" : "#ffffff";
          });

          if (!isOrganization) {
            resetOrganizationCheck();
          }
        };

        billTypeCards.forEach((card) => {
          card.addEventListener("click", () => {
            const input = card.querySelector(
              'input[name="ebarimt-bill-type"]',
            );

            if (input) {
              input.checked = true;
            }

            updateOrganizationBox();
          });
        });

        organizationRegInput?.addEventListener("input", resetOrganizationCheck);
        organizationCheckButton?.addEventListener("click", checkOrganization);

        updateOrganizationBox();
      },
      preConfirm: () => {
        const popup = Swal.getPopup();
        const billType =
          popup?.querySelector('input[name="ebarimt-bill-type"]:checked')
            ?.value === "3"
            ? "3"
            : "1";

        if (billType !== "3") {
          return {
            billType: "1",
            customerNo: "",
            customerName: "",
          };
        }

        const customerNo = normalizeCustomerNoInput(
          popup?.querySelector("[data-ebarimt-reg-no]")?.value,
        );
        const checkedInputNo = normalizeCustomerNoInput(
          popup?.dataset?.ebarimtCheckedInputNo,
        );
        const checkedCustomerName = String(
          popup?.dataset?.ebarimtCustomerName || "",
        ).trim();

        if (!customerNo) {
          Swal.showValidationMessage("Байгууллагын РД оруулна уу.");
          return false;
        }

        if (customerNo.length < 5) {
          Swal.showValidationMessage("РД буруу байна.");
          return false;
        }

        return {
          billType: "3",
          customerNo,
          customerName:
            checkedInputNo === customerNo ? checkedCustomerName : "",
        };
      },
    });
  };

  const handleRequestEbarimtPrint = async (parkingRecord) => {
    // Баримт бол төлбөрийн дараах үйлдэл. Төлбөр баталгаажаагүй бол
    // preview/API руу огт оруулахгүй.
    if (!isParkingRecordSettled(parkingRecord)) {
      await MySwal.fire({
        icon: "warning",
        title: "Төлбөр төлөгдөөгүй байна",
        text: "EBarimt-ийг зөвхөн төлбөр амжилттай баталгаажсаны дараа олгоно.",
        confirmButtonColor: "#f59e0b",
      });
      return;
    }

    const amount = getEbarimtPrintableAmount(parkingRecord);
    const paymentType = getCanonicalPaymentType(parkingRecord);
    const paymentMethod = getEbarimtPaymentMethod(parkingRecord);
    const transactionReference = getEbarimtReference(parkingRecord);

    if (amount <= 0) {
      await MySwal.fire({
        icon: "info",
        title: "EBarimt хэвлэх дүн байхгүй",
        text: "Чөлөөлсөн эсвэл төлбөргүй бүртгэл дээр EBarimt хэвлэхгүй.",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    const previewResult = await showEbarimtPrintPreview({
      parkingRecord,
      amount,
      paymentType,
      paymentMethod,
      transactionReference,
    });

    if (!previewResult.isConfirmed) {
      return;
    }

    const ebarimtBillOptions = previewResult.value || {
      billType: "1",
      customerNo: "",
      customerName: "",
    };

    try {
      const result = await requestEbarimtPrint({
        parkingRecord,
        settlement: {
          method: paymentMethod,
          label: "EBarimt үүсгээд хэвлэх",
          doctorNoteApplied: Boolean(parkingRecord?.doctorNoteApplied),
          discountAmount: parkingRecord?.doctorNoteDiscountAmount,
        },
        amount,
        paymentType,
        transactionId: transactionReference,
        ebarimtBillOptions,
        previewConfirmed: true,
      });

      const saved = await persistEbarimtResultForExistingRecord({
        parkingRecord,
        result: {
          ...result,
          requested: true,
        },
        amount,
        paymentType,
      });

      if (result?.status === "PRINT_CONFIRMED") {
        await MySwal.fire({
          icon: saved ? "success" : "warning",
          title: "Баримт өгсөн",
          text: saved
            ? "Баримт хэвлэхийг баталгаажуулсан. Төлөв хадгалагдлаа."
            : "Баримт хэвлэхийг баталгаажуулсан боловч төлөв Firestore-д хадгалагдсан эсэхийг шалгана уу.",
          timer: saved ? 1600 : undefined,
          showConfirmButton: !saved,
          confirmButtonColor: "#f59e0b",
        });
      } else if (
        ["PRINT_WINDOW_CLOSED", "PRINT_CONFIRM_TIMEOUT"].includes(
          result?.status,
        )
      ) {
        await MySwal.fire({
          icon: "info",
          title: "Баримт хэвлэгдсэн гэж баталгаажаагүй",
          text: "EBarimt үүссэн боловч “Баримт хэвлэх” товч баталгаажаагүй тул “Баримт өгсөн” төлөвт оруулаагүй.",
          confirmButtonColor: "#64748b",
        });
      }
    } catch (error) {
      await MySwal.fire({
        icon: "error",
        title: "EBarimt хүсэлт амжилтгүй",
        text: getErrorMessage(error, "Хэвлэх хүсэлт хадгалж чадсангүй."),
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  /**
   * Тээврийн хэрэгслийг блоклох.
   */
  const addBlock = useCallback(
    async (plate, price) => {
      if (!parkId) {
        await MySwal.fire({
          icon: "warning",
          title: "Зогсоолын тохиргоо олдсонгүй",
          text: "Firebase Auth displayName буюу parkId-ийг шалгана уу.",
          confirmButtonColor: "#f59e0b",
        });

        return;
      }

      const normalizedPlate = String(plate || "")
        .trim()
        .toUpperCase();

      if (!normalizedPlate) {
        await MySwal.fire({
          icon: "warning",
          title: "Улсын дугаар байхгүй",
          confirmButtonColor: "#f59e0b",
        });

        return;
      }

      try {
        const registeredCarsReference = collection(
          db,
          "parkings",
          parkId,
          "registeredCars",
        );

        const plateQuery = query(
          registeredCarsReference,
          where("plate", "==", normalizedPlate),
        );

        const querySnapshot = await getDocs(plateQuery);

        if (!querySnapshot.empty) {
          await MySwal.fire({
            icon: "info",
            title: "Бүртгэлтэй байна",
            text: "Энэ машиныг бүртгэлтэй хэсгээс блоклоно уу.",
            confirmButtonColor: "#3b82f6",
          });

          return;
        }

        const result = await MySwal.fire({
          icon: "warning",
          title: "Блоклох уу?",
          text: `${normalizedPlate} дугаартай тээврийн хэрэгслийг блоклох уу?`,
          showCancelButton: true,
          confirmButtonColor: "#ef4444",
          cancelButtonColor: "#94a3b8",
          confirmButtonText: "Тийм, блоклох",
          cancelButtonText: "Цуцлах",
        });

        if (!result.isConfirmed) {
          return;
        }

        await addDoc(registeredCarsReference, {
          building: "Төлбөртэй зогсоол",
          number: "Бүртгэлгүй",
          isBlocked: true,
          plate: normalizedPlate,
          phone: "-",
          desc: `Төлөөгүй төлбөр ${getCleanPrice(price).toLocaleString()}₮`,
          createdAt: new Date(),
        });

        await MySwal.fire({
          icon: "success",
          title: "Амжилттай блоклолоо",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Блоклоход алдаа:", error);

        await MySwal.fire({
          icon: "error",
          title: "Алдаа гарлаа",
          text: getErrorMessage(error, "Хадгалж чадсангүй."),
          confirmButtonColor: "#ef4444",
        });
      }
    },
    [db, parkId],
  );

  return (
    <div className="tw-bg-slate-50 tw-min-h-screen tw-font-sans tw-text-slate-900 tw-max-w-[100vw] tw-overflow-x-hidden tw-pb-12">
      {/* HEADER */}
      <header className="tw-bg-white/95 tw-backdrop-blur tw-sticky tw-top-0 tw-z-40 tw-border-b tw-border-slate-200">
        <div className="tw-max-w-[1600px] tw-mx-auto tw-px-3 sm:tw-px-5 lg:tw-px-8 tw-py-3 sm:tw-py-4">
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-gap-4">
            <div className="tw-min-w-0">
              <h2 className="tw-m-0 tw-truncate tw-text-lg sm:tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-950">
                {parkingName || (userReady ? parkId : "Уншиж байна...")}
              </h2>
            </div>

            <div className="tw-flex tw-w-full md:tw-w-auto tw-items-center tw-justify-between md:tw-justify-end tw-gap-3">
              <div className="tw-flex tw-items-center tw-bg-slate-50 hover:tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-xl tw-px-4 tw-py-2 tw-w-full md:tw-w-auto tw-transition-colors">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                  locale="mn"
                  popperPlacement="bottom-end"
                  className="tw-bg-transparent tw-text-sm tw-font-normal tw-text-slate-900 tw-outline-none tw-w-full sm:tw-w-28 tw-cursor-pointer"
                />
              </div>

              <button
                type="button"
                onClick={handleConnectPosTerminal}
                disabled={
                  posConnectionStatus === "connecting" ||
                  posConnectionStatus === "unsupported" ||
                  Boolean(openingRecordId)
                }
                title={
                  posConnectionStatus === "unsupported"
                    ? "Web Serial дэмждэг Chrome эсвэл Edge ашиглана уу"
                    : posConnectionStatus === "connected"
                      ? "A35 POS холбогдсон"
                      : "A35 POS-ийн COM портыг сонгох"
                }
                className={`tw-flex tw-h-10 tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-border tw-px-3 tw-text-sm tw-font-medium tw-transition-colors tw-flex-shrink-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
                  posConnectionStatus === "connected"
                    ? "tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100"
                    : posConnectionStatus === "unsupported"
                      ? "tw-border-slate-200 tw-bg-slate-100 tw-text-slate-400"
                      : "tw-border-indigo-200 tw-bg-indigo-50 tw-text-indigo-700 hover:tw-bg-indigo-100"
                }`}
              >
                {posConnectionStatus === "connecting" && (
                  <FiLoader className="tw-h-4 tw-w-4 tw-animate-spin" />
                )}

                <span className="tw-hidden sm:tw-inline">
                  {posConnectionStatus === "connected"
                    ? "POS холбогдсон"
                    : posConnectionStatus === "connecting"
                      ? "POS холбож байна"
                      : posConnectionStatus === "unsupported"
                        ? "POS дэмжихгүй"
                        : "POS холбох"}
                </span>

                <span
                  className={`tw-h-2 tw-w-2 tw-rounded-full ${
                    posConnectionStatus === "connected"
                      ? "tw-bg-emerald-500"
                      : posConnectionStatus === "connecting"
                        ? "tw-bg-amber-500"
                        : "tw-bg-slate-400"
                  }`}
                />
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-10 tw-h-10 sm:tw-w-auto sm:tw-px-4 sm:tw-py-2.5 tw-bg-white tw-border tw-border-slate-200 hover:tw-border-rose-200 hover:tw-bg-rose-50 hover:tw-text-rose-600 tw-text-slate-900 tw-font-normal tw-text-sm tw-rounded-xl tw-transition-colors tw-flex-shrink-0"
              >
                <span>Гарах</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="tw-max-w-[1600px] tw-mx-auto tw-px-3 sm:tw-px-5 lg:tw-px-8 tw-py-4 sm:tw-py-6 lg:tw-py-8 tw-space-y-5 sm:tw-space-y-7 lg:tw-space-y-8">
        {/* ХААЛГА УДИРДАХ */}
        <div className="tw-rounded-2xl tw-bg-slate-950 tw-p-4 sm:tw-p-5">
          <div>
            <div className="tw-mb-3 sm:tw-mb-4">
              <h3 className="tw-m-0 tw-text-sm sm:tw-text-base tw-font-medium tw-text-white">
                Хаалга
              </h3>
            </div>
                          <div>
                <h3 className="tw-text-lg sm:tw-text-xl tw-font-normal tw-text-white tw-tracking-wide">
                  Зогсоолын хаалга удирдах
                </h3>

                <p className="tw-text-slate-400 tw-text-xs sm:tw-text-sm tw-mt-1">
                  Бодит хугацаанд хаалга нээх удирдлага
                </p>
              </div>
              <br/>

            <div
              className={`
                                tw-grid
                                tw-grid-cols-1
                                sm:tw-grid-cols-2
                                ${gateGridClass}
                                tw-gap-2.5
                                sm:tw-gap-3
                                lg:tw-gap-4
                            `}
            >
              {displayedGateCameras.map((camera) => (
                <GateButton
                  key={camera.camera_id}
                  onClick={() => openGate(camera)}
                  label={`${
                    camera.dashboardLabel || camera.name || camera.camera_id
                  } НЭЭХ`}
                  disabled={
                    !userReady ||
                    camera.enabled === false ||
                    camera.configured === false
                  }
                  gradientClass={
                    camera.role === "exit"
                      ? "tw-bg-gradient-to-b tw-from-blue-500 tw-to-blue-700 tw-border tw-border-blue-400/50"
                      : "tw-bg-gradient-to-b tw-from-emerald-400 tw-to-emerald-600 tw-border tw-border-emerald-400/50"
                  }
                  shadowClass={
                    camera.role === "exit"
                      ? "tw-shadow-[0_8px_20px_rgba(59,130,246,0.25)] hover:tw-shadow-[0_8px_25px_rgba(59,130,246,0.4)]"
                      : "tw-shadow-[0_8px_20px_rgba(16,185,129,0.25)] hover:tw-shadow-[0_8px_25px_rgba(16,185,129,0.4)]"
                  }
                />
              ))}
            </div>

            {gateCameras.length === 0 ? null : null}
          </div>
        </div>

        {/* KPI ҮЗҮҮЛЭЛТ */}
        <div>
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-4 tw-px-1">

            <button
              type="button"
              onClick={() =>
                setIsPaymentSummaryVisible((previousValue) => !previousValue)
              }
              aria-expanded={isPaymentSummaryVisible}
              aria-controls="payment-summary-content"
              className="tw-h-9 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-xs tw-font-medium tw-text-slate-600 hover:tw-bg-slate-50"
            >
              {isPaymentSummaryVisible ? "Хураах" : "Дэлгэрэнгүй"}
            </button>
          </div>

          {isPaymentSummaryVisible ? (
            <div id="payment-summary-content">
              <section className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-5 tw-gap-2.5 sm:tw-gap-3 tw-mb-4">
            <StatCard
              label="Өдрийн нийт орлого"
              value={
                isPaymentBreakdownLoading
                  ? "..."
                  : `${displayedDailyRevenue.toLocaleString()}₮`
              }
            />

            <StatCard
              label="Төлөгдөөгүй дүн"
              value={isAggregating ? "..." : `${totalLoss.toLocaleString()}₮`}
            />

            <StatCard
              label="Эмнэлгийн хөнгөлөлт"
              value={
                isPaymentBreakdownLoading
                  ? "..."
                  : `${displayedPaymentBreakdown.doctorDiscount.amount.toLocaleString()}₮`
              }
            />


            <StatCard
              label="Нийт орсон машин"
              value={isAggregating ? "..." : totalEnteredCount}
            />

            <StatCard
              label="Одоо зогсоолд"
              value={isAggregating ? "..." : occupiedSpots}
            />
          </section>

          <div className="tw-flex tw-items-end tw-justify-between tw-gap-3 tw-mb-3 tw-px-1">

          </div>

          <section className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-5 tw-gap-2.5 sm:tw-gap-3 tw-mb-5">
            <PaymentMethodCard
              label="QPay төлсөн"
              count={displayedPaymentBreakdown.qpay.count}
              revenue={displayedPaymentBreakdown.qpay.revenue}
              isLoading={isPaymentBreakdownLoading}
            />

            <PaymentMethodCard
              label="Картаар төлсөн"
              count={displayedPaymentBreakdown.card.count}
              revenue={displayedPaymentBreakdown.card.revenue}
              isLoading={isPaymentBreakdownLoading}
            />

            <PaymentMethodCard
              label="Бэлнээр төлсөн"
              count={displayedPaymentBreakdown.cash.count}
              revenue={displayedPaymentBreakdown.cash.revenue}
              isLoading={isPaymentBreakdownLoading}
            />

            <PaymentMethodCard
              label="Эмнэлгийн бичиг"
              count={displayedPaymentBreakdown.doctorDiscount.count}
              revenue={displayedPaymentBreakdown.doctorDiscount.amount}
              isLoading={isPaymentBreakdownLoading}
            />

            <PaymentMethodCard
              label="Бусад үйлчилгээ"
              count={displayedPaymentBreakdown.otherReason.count}
              revenue={displayedPaymentBreakdown.otherReason.revenue}
              isLoading={isPaymentBreakdownLoading}
            />
              </section>
            </div>
          ) : ""}
        </div>

        {/* БҮРТГЭЛИЙН ХҮСНЭГТ */}
        <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-200 tw-overflow-hidden tw-flex tw-flex-col">
          <div className="tw-px-4 sm:tw-px-6 tw-pt-5 sm:tw-pt-6">
            <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end sm:tw-justify-between tw-gap-2">


              <div className="tw-inline-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500">
                <span
                  className={`tw-h-2 tw-w-2 tw-rounded-full ${
                    realtimeStatus === "live"
                      ? "tw-bg-emerald-500"
                      : realtimeStatus === "error"
                        ? "tw-bg-rose-500"
                        : "tw-bg-slate-400"
                  }`}
                />
                <span>
                  {realtimeStatus === "live"
                    ? "Live"
                    : realtimeStatus === "error"
                      ? "Offline"
                      : "Connecting"}
                </span>
              </div>
            </div>
          </div>

          {/* ХАЙЛТ, ШҮҮЛТҮҮР */}
          <div className="tw-p-4 sm:tw-p-6 tw-border-b tw-border-slate-100 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-stretch lg:tw-items-center tw-gap-4">
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full lg:tw-w-auto">
              <div className="tw-relative tw-w-full lg:tw-w-80">
                <input
                  type="text"
                  placeholder="Улсын дугаараар хайх..."
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  className="tw-w-full tw-px-3 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-normal tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-bg-white focus:tw-border-emerald-400 focus:tw-ring-2 focus:tw-ring-emerald-100 tw-transition-all"
                />
              </div>

              <button
                type="button"
                onClick={handleRefresh}
                disabled={
                  isLoadingData || isAggregating || isRefreshing || !userReady
                }
                title="KPI болон хүснэгтийн мэдээллийг шинэчлэх"
                className="tw-flex tw-h-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-text-sm tw-font-medium tw-text-slate-700 tw-transition-colors hover:tw-bg-emerald-50 hover:tw-text-emerald-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              >
                <span>{isRefreshing ? "Шинэчилж байна" : "Шинэчлэх"}</span>
              </button>
            </div>

            <div className="tw-flex tw-bg-slate-50 tw-border tw-border-slate-200 tw-p-1 tw-rounded-xl tw-w-full lg:tw-w-auto tw-overflow-x-auto">
              <button
                type="button"
                onClick={() => setPaymentFilter("all")}
                className={`
                                    tw-flex-1
                                    lg:tw-flex-none
                                    tw-px-5
                                    tw-py-2
                                    tw-rounded-lg
                                    tw-text-xs
                                    sm:tw-text-sm
                                    tw-font-normal
                                    tw-transition-all
                                    tw-duration-200
                                    tw-whitespace-nowrap
                                    ${
                                      paymentFilter === "all"
                                        ? "tw-bg-white tw-text-slate-900 tw-shadow-sm tw-border tw-border-slate-200"
                                        : "tw-text-slate-600 hover:tw-text-slate-900"
                                    }
                                `}
              >
                Бүгд
              </button>

              <button
                type="button"
                onClick={() => setPaymentFilter("paid")}
                className={`
                                    tw-flex-1
                                    lg:tw-flex-none
                                    tw-px-5
                                    tw-py-2
                                    tw-rounded-lg
                                    tw-text-xs
                                    sm:tw-text-sm
                                    tw-font-normal
                                    tw-transition-all
                                    tw-duration-200
                                    tw-whitespace-nowrap
                                    ${
                                      paymentFilter === "paid"
                                        ? "tw-bg-emerald-500 tw-text-white tw-shadow-sm"
                                        : "tw-text-slate-600 hover:tw-text-emerald-600"
                                    }
                                `}
              >
                Төлөгдсөн
              </button>

              <button
                type="button"
                onClick={() => {
                  setPaymentFilter("unpaid");
                  setPaymentTypeFilter("all");
                }}
                className={`
                                    tw-flex-1
                                    lg:tw-flex-none
                                    tw-px-5
                                    tw-py-2
                                    tw-rounded-lg
                                    tw-text-xs
                                    sm:tw-text-sm
                                    tw-font-normal
                                    tw-transition-all
                                    tw-duration-200
                                    tw-whitespace-nowrap
                                    ${
                                      paymentFilter === "unpaid"
                                        ? "tw-bg-rose-500 tw-text-white tw-shadow-sm"
                                        : "tw-text-slate-600 hover:tw-text-rose-600"
                                    }
                                `}
              >
                Төлөөгүй
              </button>
            </div>
          </div>

          <div className="tw-px-4 sm:tw-px-6 tw-py-3 tw-border-b tw-border-slate-100 tw-bg-slate-50/50">
            <div className="tw-flex tw-items-center tw-gap-3 tw-overflow-x-auto">
              <span className="tw-text-xs tw-font-medium tw-text-slate-500 tw-whitespace-nowrap">
                Төлбөрийн төрөл:
              </span>

              {[
                { value: "all", label: "Бүх төрөл" },
                { value: "qpay", label: "QPay төлсөн" },
                { value: "card", label: "Картаар төлсөн" },
                { value: "cash", label: "Бэлнээр төлсөн" },
                { value: "doc", label: "Эмнэлгийн бичиг" },
                { value: "otherReason", label: "Бусад үйлчилгээ" },
              ].map((filterOption) => (
                <button
                  key={filterOption.value}
                  type="button"
                  onClick={() => {
                    setPaymentTypeFilter(filterOption.value);
                    if (filterOption.value !== "all") {
                      setPaymentFilter("paid");
                    }
                  }}
                  className={`tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-whitespace-nowrap tw-transition-all ${
                    paymentTypeFilter === filterOption.value
                      ? "tw-bg-indigo-600 tw-text-white tw-shadow-sm"
                      : "tw-bg-white tw-text-slate-600 tw-border tw-border-slate-200 hover:tw-border-indigo-300 hover:tw-text-indigo-700"
                  }`}
                >
                  {filterOption.label}
                </button>
              ))}
            </div>
          </div>

          {/* TABLE */}
          <div className="tw-overflow-x-auto tw-overflow-y-auto tw-w-full tw-max-h-[600px] tw-relative">
            <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[1120px] xl:tw-min-w-full">
              <thead className="tw-bg-slate-50 tw-sticky tw-top-0 tw-z-20 tw-shadow-sm">
                <tr>
                  <th className="tw-px-6 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-w-16 tw-border-b tw-border-slate-200">
                    №
                  </th>

                  <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                    Улсын дугаар
                  </th>

                  <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                    Ангилал
                  </th>

                  <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                    Орсон цаг
                  </th>

                  <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                    Гарсан цаг
                  </th>

                  <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                    Хугацаа
                  </th>

                  <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                    Дүн / гүйлгээ
                  </th>

                  <th className="tw-px-4 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-border-b tw-border-slate-200">
                    Төлбөрийн төлөв
                  </th>

                  <th className="tw-px-6 tw-py-4 tw-text-[11px] tw-font-medium tw-text-slate-900 tw-uppercase tw-tracking-wider tw-text-right tw-border-b tw-border-slate-200 tw-min-w-[520px]">
                    Зогсоолын төлөв
                  </th>
                </tr>
              </thead>

              <tbody className="tw-divide-y tw-divide-slate-100 tw-bg-white">
                {isLoadingData ? (
                  <tr>
                    <td colSpan={9} className="tw-px-6 tw-py-24 tw-text-center">
                      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-blue-500">
                        <div className="tw-w-10 tw-h-10 tw-border-4 tw-border-blue-100 tw-border-t-blue-600 tw-rounded-full tw-animate-spin tw-mb-4" />

                        <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                          Уншиж байна...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredParkingLogs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="tw-px-6 tw-py-24 tw-text-center">
                      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-slate-400">
                        <div className="tw-w-16 tw-h-16 tw-bg-slate-50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4 tw-border tw-border-slate-100">
                          <FiSearch size={24} className="tw-text-slate-400" />
                        </div>

                        <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                          {paymentTypeFilter === "all"
                            ? "Мэдээлэл олдсонгүй."
                            : "Сонгосон төлбөрийн төрлийн мэдээлэл олдсонгүй."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredParkingLogs.map((log, index) => {
                    const cleanPrice = getCleanPrice(log.price);

                    const recordIdentity = getParkingRecordIdentity(log);

                    const vehiclePlate = String(
                      log.car_plate || log.plate || "-",
                    );

                    const statusText =
                      log.status === "parked" ? "Зогсоолд" : log.status || "-";

                    const lowerStatus = String(log.status || "").toLowerCase();

                    const isActiveStatus =
                      log.status === "parked" ||
                      lowerStatus.includes("тооцоолж");

                    const isWarningStatus =
                      lowerStatus.includes("шаардлагатай") ||
                      lowerStatus.includes("хэтэрсэн");

                    const isVehicleInside =
                      !log.exit_time || lowerStatus === "parked";

                    const canOpenVehicleGate =
                      isVehicleInside && Boolean(recordIdentity);
                    const doctorNoteAmount = getDoctorNoteParkingAmount(
                      log,
                      parkingPricingConfig,
                    );

                    const paymentState = getParkingRecordPaymentState(log);
                    const isSettledRecord = paymentState.isSettled;
                    const isExempted = paymentState.isExempted;

                    // Төлбөр/чөлөөлөлт амжилттай болсон record дээр дахин
                    // "Хаалт нээх", "Эмнэлгийн бичиг" зэрэг settlement action
                    // харагдахгүй.
                    const canSettleAndOpenVehicle =
                      canOpenVehicleGate &&
                      !isSettledRecord &&
                      !isExempted;

                    const rowPaymentType = paymentState.paymentType;
                    const paymentMethod = paymentState.isFree
                      ? "Төлбөргүй"
                      : getPaymentMethodLabel(
                          rowPaymentType,
                          isSettledRecord,
                        );

                    const paymentText = isExempted
                      ? "Чөлөөлсөн"
                      : paymentState.isFree
                        ? "Төлбөргүй"
                        : isSettledRecord
                          ? paymentMethod
                          : paymentState.isUnpaid
                            ? "Төлөөгүй"
                            : "Төлбөргүй";

                    const paymentBadgeClass = isExempted
                      ? "tw-bg-amber-500 tw-text-white"
                      : paymentState.isFree
                        ? "tw-bg-slate-200 tw-text-slate-900"
                        : isSettledRecord
                          ? rowPaymentType === "qpay"
                            ? "tw-bg-sky-500 tw-text-white"
                            : rowPaymentType === "card"
                              ? "tw-bg-indigo-600 tw-text-white"
                              : rowPaymentType === "cash"
                                ? "tw-bg-emerald-500 tw-text-white"
                                : "tw-bg-blue-500 tw-text-white"
                          : paymentState.isUnpaid
                            ? "tw-bg-rose-500 tw-text-white"
                            : "tw-bg-slate-200 tw-text-slate-900";

                    const originalPrice = getCleanPrice(log.originalPrice);
                    const ebarimtPrintableAmount =
                      getEbarimtPrintableAmount(log);
                    const hasGivenEbarimt = isEbarimtGiven(log);

                    // "Баримт өгөх" нь төлбөр баталгаажсан, баримт өмнө нь
                    // өгөөгүй record дээр л харагдана.
                    const canPrintEbarimt =
                      isSettledRecord &&
                      !isExempted &&
                      !hasGivenEbarimt &&
                      ebarimtPrintableAmount > 0;

                    const transactionReference = String(
                      log.referenceNumber ||
                        log.invoiceId ||
                        log.transactionId ||
                        "",
                    ).trim();

                    return (
                      <tr
                        key={recordIdentity || `${vehiclePlate}-${index}`}
                        className="hover:tw-bg-slate-50 tw-transition-colors tw-duration-150"
                      >
                        <td className="tw-px-6 tw-py-4 tw-text-sm tw-font-normal tw-text-slate-900">
                          {paymentTypeFilter === "all"
                            ? currentPage * pageSize + index + 1
                            : index + 1}
                        </td>

                        <td className="tw-px-4 tw-py-4">
                          <div className="tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-bg-white tw-border tw-border-slate-300 tw-rounded-md tw-text-[13px] tw-font-bold tw-text-slate-900 tw-tracking-widest tw-uppercase">
                            {vehiclePlate}
                          </div>
                        </td>

                        <td className="tw-px-4 tw-py-4 tw-text-sm tw-font-normal tw-text-slate-900">
                          <div className="tw-font-medium">
                            {log.isLarge ? "Том оврын" : "Суудлын"}
                          </div>
                          <div className="tw-mt-0.5 tw-text-[11px] tw-text-slate-400">
                            {paymentMethod}
                          </div>
                        </td>

                        <td className="tw-px-4 tw-py-4">
                          <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                            {formatDateTime(log.entry_time)}
                          </span>
                        </td>

                        <td className="tw-px-4 tw-py-4">
                          <span className="tw-text-sm tw-font-normal tw-text-slate-900">
                            {formatDateTime(log.exit_time)}
                          </span>
                        </td>

                        <td className="tw-px-4 tw-py-4 tw-text-sm tw-font-normal tw-text-slate-900">
                          {log.duration || "-"}
                        </td>

                        <td className="tw-px-4 tw-py-4">
                          <div className="tw-text-sm tw-font-semibold tw-text-slate-900">
                            {`${cleanPrice.toLocaleString()}₮`}
                          </div>
                          {isExempted && originalPrice > 0 && (
                            <div className="tw-mt-0.5 tw-text-[10px] tw-text-amber-600">
                              {originalPrice.toLocaleString()}₮-өөс чөлөөлсөн
                            </div>
                          )}
                          {log.doctorNoteApplied && (
                            <div className="tw-mt-0.5 tw-text-[10px] tw-text-blue-600">
                              120 мин хөнгөлөлт ·{" "}
                              {getCleanPrice(
                                log.doctorNoteDiscountAmount,
                              ).toLocaleString()}
                              ₮ хассан
                            </div>
                          )}
                          {transactionReference && (
                            <div
                              className="tw-mt-0.5 tw-max-w-[150px] tw-truncate tw-text-[10px] tw-text-slate-400"
                              title={transactionReference}
                            >
                              #{transactionReference}
                            </div>
                          )}
                        </td>

                        <td className="tw-px-4 tw-py-4">
                          <span
                            className={`
                                                                tw-inline-flex
                                                                tw-items-center
                                                                tw-gap-1.5
                                                                tw-px-3
                                                                tw-py-1.5
                                                                tw-rounded-lg
                                                                tw-text-[11px]
                                                                tw-font-normal
                                                                tw-uppercase
                                                                tw-tracking-widest
                                                                tw-shadow-sm
                                                                ${paymentBadgeClass}
                                                            `}
                          >
                            {paymentText}
                          </span>
                          {log.payment_time && (
                            <div className="tw-mt-1 tw-text-[10px] tw-text-slate-400 tw-whitespace-nowrap">
                              {formatDateTime(log.payment_time)}
                            </div>
                          )}
                        </td>

                        <td className="tw-px-6 tw-py-4 tw-text-right">
                          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                            <div className="tw-flex tw-min-w-[120px] tw-flex-col tw-items-end tw-gap-1.5">
                              <span
                                className={`
                                                                        tw-inline-flex
                                                                        tw-items-center
                                                                        tw-gap-1.5
                                                                        tw-px-3
                                                                        tw-py-1.5
                                                                        tw-rounded-lg
                                                                        tw-text-[11px]
                                                                        tw-font-normal
                                                                        tw-uppercase
                                                                        tw-tracking-widest
                                                                        tw-shadow-sm
                                                                        ${
                                                                          isActiveStatus
                                                                            ? "tw-bg-blue-500 tw-text-white"
                                                                            : isWarningStatus
                                                                              ? "tw-bg-rose-500 tw-text-white"
                                                                              : "tw-bg-slate-200 tw-text-slate-900"
                                                                        }
                                                                    `}
                              >
                                {isActiveStatus && (
                                  <span className="tw-relative tw-flex tw-h-2 tw-w-2">
                                    <span className="tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-white tw-opacity-75" />

                                    <span className="tw-relative tw-inline-flex tw-rounded-full tw-h-2 tw-w-2 tw-bg-white" />
                                  </span>
                                )}

                                {statusText}
                              </span>
                            </div>

                            {canSettleAndOpenVehicle && (
                              <button
                                type="button"
                                onClick={() => settleAndOpenVehicle(log)}
                                disabled={
                                  !userReady ||
                                  Boolean(openingRecordId) ||
                                  !recordIdentity
                                }
                                title="Гарах хаалт нээх"
                                className="tw-inline-flex tw-h-9 tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-bg-emerald-600 tw-px-3 tw-text-xs tw-font-semibold tw-text-white tw-shadow-sm tw-transition-all hover:tw-bg-emerald-700 active:tw-scale-[0.98] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                              >
                                {openingRecordId === recordIdentity ? (
                                  <FiLoader className="tw-h-4 tw-w-4 tw-animate-spin" />
                                ) : null}
                                Хаалт нээх
                              </button>
                            )}

                            {canSettleAndOpenVehicle && (
                              <button
                                type="button"
                                onClick={() =>
                                  settleDoctorNoteAndOpenVehicle(log)
                                }
                                disabled={
                                  !userReady ||
                                  Boolean(openingRecordId) ||
                                  !recordIdentity
                                }
                                title={`Эмнэлгийн бичиг: эхний ${DOCTOR_NOTE_FREE_MINUTES} минут үнэгүй, үлдэгдэл ${doctorNoteAmount.amountDue.toLocaleString()}₮`}
                                className="tw-inline-flex tw-h-9 tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-bg-blue-600 tw-px-3 tw-text-xs tw-font-semibold tw-text-white tw-shadow-sm tw-transition-all hover:tw-bg-blue-700 active:tw-scale-[0.98] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                              >
                                {openingRecordId === recordIdentity ? (
                                  <FiLoader className="tw-h-4 tw-w-4 tw-animate-spin" />
                                ) : null}
                                Эмнэлгийн бичиг
                              </button>
                            )}

                            {hasGivenEbarimt ? (
                              <span
                                title={
                                  log.ebarimtGivenAt || log.ebarimtPrintedAt
                                    ? `Баримт өгсөн: ${formatDateTime(
                                        log.ebarimtGivenAt ||
                                          log.ebarimtPrintedAt,
                                      )}`
                                    : "Баримт хэвлэхийг баталгаажуулсан"
                                }
                                className="tw-inline-flex tw-h-9 tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-px-3 tw-text-xs tw-font-semibold tw-text-emerald-700"
                              >
                                Баримт өгсөн
                              </span>
                            ) : (
                              canPrintEbarimt && (
                                <button
                                  type="button"
                                  onClick={() => handleRequestEbarimtPrint(log)}
                                  title={`${ebarimtPrintableAmount.toLocaleString()}₮-ийн EBarimt үүсгээд Xprinter дээр хэвлэх`}
                                  className="tw-inline-flex tw-h-9 tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shadow-sm tw-transition-all hover:tw-border-indigo-200 hover:tw-bg-indigo-50 hover:tw-text-indigo-700 active:tw-scale-[0.98]"
                                >
                                  Баримт өгөх
                                </button>
                              )
                            )}

                            <button
                              type="button"
                              onClick={() =>
                                addBlock(log.car_plate || log.plate, log.price)
                              }
                              disabled={
                                Boolean(log.registered) ||
                                !["admin", "master"].includes(userRole)
                              }
                              title={
                                log.registered
                                  ? "Бүртгэлтэй хэрэглэгч"
                                  : !["admin", "master"].includes(userRole)
                                    ? "Блоклох эрх байхгүй"
                                    : "Блоклох"
                              }
                              className={`
                                                                    tw-flex
                                                                    tw-items-center
                                                                    tw-justify-center
                                                                    tw-w-8
                                                                    tw-h-8
                                                                    tw-rounded-xl
                                                                    tw-transition-all
                                                                    tw-duration-200
                                                                    tw-shadow-sm
                                                                    active:tw-scale-95
                                                                    disabled:tw-cursor-not-allowed
                                                                    ${
                                                                      log.registered
                                                                        ? "tw-bg-emerald-500 tw-text-white"
                                                                        : [
                                                                              "admin",
                                                                              "master",
                                                                            ].includes(
                                                                              userRole,
                                                                            )
                                                                          ? "tw-bg-white tw-text-slate-900 tw-border tw-border-slate-300 hover:tw-bg-rose-500 hover:tw-text-white hover:tw-border-rose-500"
                                                                          : "tw-bg-slate-100 tw-text-slate-400 tw-border tw-border-slate-200"
                                                                    }
                                                                `}
                            >
                              {log.registered ? (
                                <FiHome size={15} />
                              ) : (
                                <FiLock size={15} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="tw-px-6 tw-py-4 tw-border-t tw-border-slate-100 tw-bg-white tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
            <div className="tw-text-sm tw-font-normal tw-text-slate-500">
              Хуудас{" "}
              <span className="tw-text-slate-900 tw-font-medium">
                {currentPage + 1}
              </span>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2 tw-w-full sm:tw-w-auto">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentPage === 0 || isLoadingData}
                className="tw-flex-1 sm:tw-flex-none tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-normal tw-transition-colors tw-border tw-border-slate-200 tw-bg-white tw-text-slate-900 hover:tw-bg-slate-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Өмнөх
              </button>

              <button
                type="button"
                onClick={handleNext}
                disabled={!hasNextPage || isLoadingData}
                className="tw-flex-1 sm:tw-flex-none tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-normal tw-transition-colors tw-bg-slate-900 tw-text-white hover:tw-bg-slate-800 disabled:tw-bg-slate-200 disabled:tw-text-slate-500 disabled:tw-cursor-not-allowed"
              >
                Дараах
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
